import React, { useState, useEffect, useContext, useRef, useCallback } from 'react';
import axios from 'axios';
import { AuthContext } from '../../components/AuthContext';
import Select from 'react-select';
import Swal from 'sweetalert2';
import { 
  FaCheck, 
  FaSearch, 
  FaArrowUp, 
  FaArrowDown, 
  FaTrash, 
  FaFilter, 
  FaSort, 
  FaSortUp, 
  FaSortDown,
  FaPrint,
  FaEdit,
  FaEye,
  FaSave,
  FaDownload,
  FaCalendarAlt,
  FaChartBar,
  FaTags,
  FaUsers,
  FaTruck,
  FaCopy,
  FaPaste,
  FaWhatsapp
} from "react-icons/fa";
import DatePicker from 'react-datepicker';
import "react-datepicker/dist/react-datepicker.css";

import MasterSelection from '../../components/MasterSelection';
import useDraftManager from '../../hooks/useDraftManager';

// Add this helper function at the top <le></le>vel
const formatNumberWithCommas = (number) => {
  // Handle null, undefined, or string values
  const value = typeof number === 'string' ? parseFloat(number) : number;
  
  // Return 0.00 if the value is not a valid number
  if (!value || isNaN(value)) {
    return '0.00';
  }

  return new Intl.NumberFormat('en-IN', {
    maximumFractionDigits: 2,
    minimumFractionDigits: 2
  }).format(value);
};

// Helper function to format date as DD/MM/YYYY
const formatDate = (dateString) => {
  if (!dateString) return 'N/A';
  const date = new Date(dateString);
  const day = String(date.getDate()).padStart(2, '0');
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const year = date.getFullYear();
  return `${day}/${month}/${year}`;
};

// Initialize form state function - moved outside component
const initializeFormState = (userId = '') => ({
  type: 'SANITARY', // Default type
  recipientType: 'Customer',
  recipient: null,
  isreference: false,
  isnarration: false,
  items: [
    {
      itemId: '',
      Name: '',
      quantity: 0,
      rate: 0,
      description: '',
      printType: 'item', // Print type moved to item level
      units: 'pcs', // Default unit
      discount: 0,
      amountBeforeDiscount: 0,
      amountAfterDiscount: 0,
      integratedTax: 18, // Default integrated tax
      tt: {
        type: 'percentage',
        value: 0,
        calculatedAmount: 0
      }
    }
  ],
  vehicleNumber: '',
  transporterName: '',
  siteAddress: '',
  modeOfPayment: [],
  modeOfSales: 'Paid Instantly',
  creditReference: '',
  creditPeriod: 0,
  claimPaymentFrom: {
    name: '',
    number: ''
  },
  salesman: [],
  reference: '', // Changed from interior to reference
  godown: null, // Add godown field
  roundoff: 0,
  Narration: '',
  packingAndForwarding: 0,
  packingAndForwardingGST: 0,
    Manual_dO_Number: '',
    poNumber: '',
    itemDescription: '',
    referenceName: '',
    cartage: 0,
  cartageGST: 0,
  labour: 0,
  labourGST: 0,
  totalAmountWithoutGST: 0,
  totalAmountWithGST: 0,
  totalTTAmount: 0,
  createdBy: userId
});

const DeliveryOrderNew = () => {
  const { user } = useContext(AuthContext);
  
  // Initialize refs
  const quantityRefs = useRef([]);
  const formRef = useRef(null);
  const calculationInProgress = useRef(false);
  const unitsSelectRefs = useRef([]);

  // Add refs for focus management
  const firstItemButtonRef = useRef(null);
  const quantityInputRefs = useRef([]);
  const addItemButtonRef = useRef(null);

  // Function to calculate charges tax rate based on items in the order
  const getChargesTaxRate = useCallback((items = []) => {
    const validItems = items.filter(item => 
      item.itemId && item.quantity > 0 && item.rate > 0
    );
    
    if (validItems.length === 0) return 18; // Default fallback
    
    // Calculate weighted average tax rate based on item amounts
    let totalAmount = 0;
    let weightedTaxSum = 0;
    
    validItems.forEach(item => {
      const itemAmount = item.quantity * item.rate;
      const itemTaxRate = parseFloat(item.integratedTax) || 18;
      totalAmount += itemAmount;
      weightedTaxSum += itemAmount * itemTaxRate;
    });
    
    return totalAmount > 0 ? (weightedTaxSum / totalAmount) : 18;
  }, []);

  // Helper function to get item tax rate consistently
  const getItemTaxRate = useCallback((item) => {
    // First try integratedTax field
    let itemTaxRate = parseFloat(item.integratedTax) || 0;
    
    // If no integratedTax, try to extract from item name (e.g., "MISC 12%")
    if (!itemTaxRate && item.Name) {
      const taxMatch = item.Name.match(/(\d+)%/);
      if (taxMatch) {
        itemTaxRate = parseFloat(taxMatch[1]);
      }
    }
    
    // Default fallback
    if (!itemTaxRate) itemTaxRate = 18;
    
    return itemTaxRate;
  }, []);

  // Data source states
  const [customers, setCustomers] = useState([]);
  const [dealers, setDealers] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [transporters, setTransporters] = useState([]);
  const [references, setReferences] = useState([]); // Changed from interiors
  const [godowns, setGodowns] = useState([]);

  // Pagination and filtering states
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(5); // Changed default to 5
  const [itemsCurrentPage, setItemsCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(5);
  const [totalPages, setTotalPages] = useState(0);
  const [count, setCount] = useState(0);
  const [total, setTotal] = useState(0);
  const [orders, setOrders] = useState([]);

  // Constants
  const paymentModeOptions = [
    { value: 'cash', label: 'Cash' },
    { value: 'cod', label: 'Cash on Delivery' },
    { value: 'credit', label: 'Credit' },
    { value: 'cheque', label: 'Cheque' },
    { value: 'neft', label: 'NEFT' },
    { value: 'rtgs', label: 'RTGS' },
    { value: 'upi', label: 'UPI' },
    { value: 'net banking', label: 'Net Banking' },
    { value: 'card', label: 'Card Payment' },
    { value: 'Part Payment at Site', label: 'Part Payment at Site' },
    { value: 'Full Payment at Site', label: 'Full Payment at Site' },
  ];
    // all units are in lower case pcs, box, bag, pkt, pair, roll, set, bkt, mtr, sh, can, kg, ltr, bundles, bottle, drums, sq.ft, sq.mtr, fts, nos, rft
  const unitsOptions = [
    { value: 'pcs', label: 'Pieces' },
    { value: 'box', label: 'Box' },
    { value: 'bag', label: 'Bag' },
    { value: 'pkt', label: 'Packet' },
    { value: 'pair', label: 'Pair' },
    { value: 'roll', label: 'Roll' },
    { value: 'set', label: 'Set' },
    { value: 'bkt', label: 'Bucket' },
    { value: 'mtr', label: 'Meter' },
    { value: 'sh', label: 'Sheet' },
    { value: 'can', label: 'Can' },
    { value: 'kg', label: 'Kilogram' },
    { value: 'ltr', label: 'Liter' },
    { value: 'bundles', label: 'Bundles' },
    { value: 'bottle', label: 'Bottle' },
    { value: 'drums', label: 'Drums' },
    { value: 'sq.ft', label: 'Square Feet' },
    { value: 'sq.mtr', label: 'Square Meter' },
    { value: 'fts', label: 'Feet' },
    { value: 'nos', label: 'Nos' },
    { value: 'rft', label: 'Rft' }
    
  ];

  // Basic states
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [orderId, setOrderId] = useState(null);
  const [lastAddedIndex, setLastAddedIndex] = useState(null);
  const [showItemsModal, setShowItemsModal] = useState(false);
  const [showViewModal, setShowViewModal] = useState(false);
  const [selectedOrder, setSelectedOrder] = useState(null);
  const [showItemDetailsModal, setShowItemDetailsModal] = useState(false);
  const [selectedItemForDetails, setSelectedItemForDetails] = useState(null);
  const [loadingItemDetails, setLoadingItemDetails] = useState(new Set()); // Track loading per item
  const [showRecipientModal, setShowRecipientModal] = useState(false);
  const [itemsModalLoading, setItemsModalLoading] = useState(false);
  
  // Proforma Invoice Import states
  const [showProformaImportModal, setShowProformaImportModal] = useState(false);
  const [proformaInvoices, setProformaInvoices] = useState([]);
  const [proformaLoading, setProformaLoading] = useState(false);
  const [proformaSearch, setProformaSearch] = useState('');
  const [proformaPage, setProformaPage] = useState(1);
  const [proformaTotalPages, setProformaTotalPages] = useState(0);
  const [selectedProformaItems, setSelectedProformaItems] = useState([]);
  const [forceRefresh, setForceRefresh] = useState(0);
  
  // Print menu states
  const [showPrintMenu, setShowPrintMenu] = useState(false);
  const [printMenuPosition, setPrintMenuPosition] = useState({ top: 0, left: 0 });
  const [selectedPrintOrderId, setSelectedPrintOrderId] = useState(null);
  const printMenuRef = useRef(null);

  // Auto-save notification state
  const [showSaveNotification, setShowSaveNotification] = useState(false);
  const saveNotificationTimerRef = useRef(null);

  // Form and input states
  const [form, setForm] = useState(() => initializeFormState(user?.user?._id));
  const [currentItemIndex, setCurrentItemIndex] = useState(form.items.length);
  const [paymentModesInput, setPaymentModesInput] = useState('');
  const [itemsList, setItemsList] = useState([]);
  const [totalItems, setTotalItems] = useState(0);

  // Sorting and filtering states
  const [sortBy, setSortBy] = useState('createdAt');
  const [sortOrder, setSortOrder] = useState('desc');
  const [showFilters, setShowFilters] = useState(false);
  const [filters, setFilters] = useState({
    search: '',
    searchType: 'all', // all, doNumber, manual, poNumber, reference, recipient
    status: '',
    recipientType: '',
    modeOfSales: '',
    paymentModes: [], // Multiple selection
    salesmen: [], // Multiple selection
    transporters: [], // Multiple selection
    references: [], // Multiple selection
    startDate: null,
    endDate: null,
    dateRange: '', // predefined ranges
    minAmount: '',
    maxAmount: '',
    type: '', // SANITARY, TILES, etc.
    hasItems: '', // yes, no
    itemCount: '', // exact, min, max
    minItemCount: '',
    maxItemCount: '',
    createdBy: [], // Multiple selection of employees
    creditPeriod: '', // 0, 30, 60, 90 days
    minCreditPeriod: '',
    maxCreditPeriod: ''
  });
  const [stats, setStats] = useState(null);
  
  // Enhanced filter states
  const [showAdvancedFilters, setShowAdvancedFilters] = useState(false);
  const [filterPresets, setFilterPresets] = useState([]);
  const [savedFilters, setSavedFilters] = useState({});
  const [filterHistory, setFilterHistory] = useState([]);
  const [quickSearch, setQuickSearch] = useState('');
  const [searchSuggestionsList, setSearchSuggestionsList] = useState([]);
  const [showMainSearchSuggestions, setShowMainSearchSuggestions] = useState(false);

  // Add state for items modal search - simplified like QuotationNew
  const [itemsSearch, setItemsSearch] = useState('');
  const [searchHistory, setSearchHistory] = useState([]);
  const [showSearchSuggestions, setShowSearchSuggestions] = useState(false);
  const [selectedSuggestionIndex, setSelectedSuggestionIndex] = useState(-1);

  // Dark mode state
  const [isDarkMode, setIsDarkMode] = useState(() => {
    const savedMode = localStorage.getItem('darkMode');
    return savedMode === 'true';
  });

  // Sync dark mode with localStorage
  useEffect(() => {
    const interval = setInterval(() => {
      const savedMode = localStorage.getItem('darkMode');
      setIsDarkMode(savedMode === 'true');
    }, 500);

    return () => clearInterval(interval);
  }, []);

  // Helper function for dark mode select styles
  const getSelectStyles = (isDark) => ({
    control: (base) => ({
      ...base,
      backgroundColor: isDark ? '#374151' : '#ffffff',
      borderColor: isDark ? '#4B5563' : '#D1D5DB',
      color: isDark ? '#F3F4F6' : '#111827',
      minHeight: '44px',
      '@media (min-width: 640px)': {
        minHeight: '38px'
      },
      '&:hover': {
        borderColor: isDark ? '#6B7280' : '#9CA3AF'
      }
    }),
    singleValue: (base) => ({
      ...base,
      color: isDark ? '#F3F4F6' : '#111827'
    }),
    input: (base) => ({
      ...base,
      color: isDark ? '#F3F4F6' : '#111827'
    }),
    menu: (base) => ({
      ...base,
      backgroundColor: isDark ? '#374151' : '#ffffff',
      border: isDark ? '1px solid #4B5563' : '1px solid #D1D5DB',
      zIndex: 9999
    }),
    option: (base, state) => ({
      ...base,
      backgroundColor: state.isFocused 
        ? (isDark ? '#4B5563' : '#E5E7EB')
        : (isDark ? '#374151' : '#ffffff'),
      color: isDark ? '#F3F4F6' : '#111827',
      '&:hover': {
        backgroundColor: isDark ? '#4B5563' : '#E5E7EB'
      }
    }),
    multiValue: (base) => ({
      ...base,
      backgroundColor: isDark ? '#4B5563' : '#E5E7EB'
    }),
    multiValueLabel: (base) => ({
      ...base,
      color: isDark ? '#F3F4F6' : '#111827'
    }),
    multiValueRemove: (base) => ({
      ...base,
      color: isDark ? '#9CA3AF' : '#6B7280',
      '&:hover': {
        backgroundColor: isDark ? '#EF4444' : '#FCA5A5',
        color: isDark ? '#ffffff' : '#7F1D1D'
      }
    }),
    placeholder: (base) => ({
      ...base,
      color: isDark ? '#9CA3AF' : '#6B7280'
    })
  });

  // Helper function to get dark mode classes
  const getDarkModeClasses = () => ({
    bg: isDarkMode ? 'bg-gray-800' : 'bg-white',
    text: isDarkMode ? 'text-gray-100' : 'text-gray-900',
    border: isDarkMode ? 'border-gray-600' : 'border-gray-300',
    input: isDarkMode ? 'bg-gray-700 border-gray-600 text-gray-100' : 'bg-white border-gray-300 text-gray-900'
  });

  // Item-specific filter states
  const [itemFilters, setItemFilters] = useState({
    stockGroup: '',
    stockStatus: '', // in-stock, low-stock, out-of-stock
    sortBy: 'name' // name, stock, price
  });
  const [stockGroups, setStockGroups] = useState([]);

  // Add a new state for the last 7 days stats
  const [lastSevenDaysStats, setLastSevenDaysStats] = useState(null);

  // Debug: Log user object for draft initialization
  console.log('🔍 DeliveryOrder - User object for draft:', {
    hasUser: !!user,
    hasUserUser: !!user?.user,
    userId: user?.user?._id,
    companyId: user?.user?.company?._id || user?.user?.company,
    fullUser: user
  });

  // Draft Management
  // Draft creation is triggered automatically when user makes first meaningful change (adds item or selects recipient)
  // Auto-save (5 seconds) starts only AFTER draft has been created
  // Current session draft is hidden from drafts list until user navigates away from the page
  // Draft is automatically deleted when delivery order is successfully created
  const draftManager = useDraftManager({
    featureType: 'DeliveryOrder',
    formData: form,
    setFormData: setForm,
    userId: user?.user?._id,
    companyId: user?.user?.company?._id || user?.user?.company,
    autoDraft: true, // Enable auto-save
    autoDraftInterval: 5000, // 5 seconds
    onDraftLoaded: (draft) => {
      Swal.fire({
        icon: 'success',
        title: 'Draft Loaded',
        text: `Draft "${draft.draftName}" has been loaded`,
        timer: 2000,
        showConfirmButton: false
      });
      // Recalculate totals after loading
      calculateTotals(draft.draftData);
    }
  });

  // Auto-hide save notification after 3 seconds
  useEffect(() => {
    if (draftManager.lastSaved && !draftManager.autoSaving) {
      setShowSaveNotification(true);
      
      // Clear any existing timer
      if (saveNotificationTimerRef.current) {
        clearTimeout(saveNotificationTimerRef.current);
      }
      
      // Auto-hide after 3 seconds
      saveNotificationTimerRef.current = setTimeout(() => {
        setShowSaveNotification(false);
      }, 3000);
    }
    
    // Cleanup on unmount
    return () => {
      if (saveNotificationTimerRef.current) {
        clearTimeout(saveNotificationTimerRef.current);
      }
    };
  }, [draftManager.lastSaved, draftManager.autoSaving]);

  const fetchStats = async () => {
    try {
      setLoading(true);
      
      const params = {
        userId: user.user._id
      };

      // Add date range if present in filters
      if (filters.startDate) {
        const startDate = new Date(filters.startDate);
        startDate.setHours(0, 0, 0, 0);
        params.startDate = startDate.toISOString();
      }
      if (filters.endDate) {
        const endDate = new Date(filters.endDate);
        endDate.setHours(23, 59, 59, 999);
        params.endDate = endDate.toISOString();
      }

      const response = await axios.get(
        `${process.env.REACT_APP_API_BASE_URL}/delivery-orders/stats`,
        { params }
      );

      if (response.data.success) {
        const stats = response.data.stats;
        
        setStats({
          // Basic stats with number formatting
          totalOrders: parseInt(stats.totalOrders || 0),
          totalAmount: formatNumberWithCommas(stats.totalAmount),
          avgAmount: formatNumberWithCommas(stats.avgAmount),
          minAmount: formatNumberWithCommas(stats.minAmount),
          maxAmount: formatNumberWithCommas(stats.maxAmount),
          
          // Status counts (keep as numbers)
          pendingOrders: parseInt(stats.pendingOrders || 0),
          completedOrders: parseInt(stats.completedOrders || 0),
          cancelledOrders: parseInt(stats.cancelledOrders || 0),
          
          // Status amounts with formatting
          pendingAmount: formatNumberWithCommas(stats.pendingAmount),
          completedAmount: formatNumberWithCommas(stats.completedAmount),
          cancelledAmount: formatNumberWithCommas(stats.cancelledAmount),
          
          // Monthly trends with formatted amounts
          monthlyTrends: (stats.monthlyTrends || []).map(trend => ({
            year: trend.year,
            month: trend.month,
            count: parseInt(trend.count || 0),
            amount: formatNumberWithCommas(trend.amount)
          }))
        });
      } else {
        throw new Error(response.data.message || 'Failed to fetch statistics');
      }
    } catch (error) {
      console.error('Error fetching statistics:', error);
      Swal.fire({
        icon: 'error',
        title: 'Error',
        text: error.response?.data?.message || error.message || 'Failed to fetch statistics',
        confirmButtonColor: '#3085d6'
      });
    } finally {
      setLoading(false);
    }
  };

  // Add this function after fetchStats to get the last 7 days data
  const fetchLastSevenDaysStats = async () => {
    try {
      setLoading(true);
      
      // Calculate date range for the last 7 days
      const endDate = new Date();
      const startDate = new Date();
      startDate.setDate(startDate.getDate() - 7);
      
      const params = {
        userId: user.user._id,
        startDate: startDate.toISOString(),
        endDate: endDate.toISOString()
      };

      const response = await axios.get(
        `${process.env.REACT_APP_API_BASE_URL}/delivery-orders/stats`,
        { params }
      );

      if (response.data.success) {
        const stats = response.data.stats;
        
        return {
          totalOrders: parseInt(stats.totalOrders || 0),
          totalAmount: formatNumberWithCommas(stats.totalAmount),
          pendingOrders: parseInt(stats.pendingOrders || 0),
          completedOrders: parseInt(stats.completedOrders || 0),
          cancelledOrders: parseInt(stats.cancelledOrders || 0),
          pendingAmount: formatNumberWithCommas(stats.pendingAmount),
          completedAmount: formatNumberWithCommas(stats.completedAmount),
          cancelledAmount: formatNumberWithCommas(stats.cancelledAmount),
        };
      } 
      
      return null;
    } catch (error) {
      console.error('Error fetching last 7 days statistics:', error);
      return null;
    }
  };

  // Function to fetch orders
  const fetchOrders = async () => {
    try {
      setLoading(true);
      
      // Build enhanced parameters
      const params = { 
        page, 
        limit,
        userId: user.user._id,
        search: filters.search,
        searchType: filters.searchType,
        status: filters.status,
        recipientType: filters.recipientType,
        modeOfSales: filters.modeOfSales,
        type: filters.type,
        startDate: filters.startDate?.toISOString(),
        endDate: filters.endDate?.toISOString(),
        minAmount: filters.minAmount,
        maxAmount: filters.maxAmount,
        creditPeriod: filters.creditPeriod,
        minCreditPeriod: filters.minCreditPeriod,
        maxCreditPeriod: filters.maxCreditPeriod,
        minItemCount: filters.minItemCount,
        maxItemCount: filters.maxItemCount,
        sortBy,
        sortOrder
      };

      // Add multi-select filters
      if (filters.paymentModes.length > 0) {
        params.paymentModes = filters.paymentModes.join(',');
      }
      if (filters.salesmen.length > 0) {
        params.salesmen = filters.salesmen.join(',');
      }
      if (filters.transporters.length > 0) {
        params.transporters = filters.transporters.join(',');
      }
      if (filters.references.length > 0) {
        params.references = filters.references.join(',');
      }
      if (filters.createdBy.length > 0) {
        params.createdBy = filters.createdBy.join(',');
      }

      // Remove empty parameters
      Object.keys(params).forEach(key => {
        if (params[key] === '' || params[key] === null || params[key] === undefined) {
          delete params[key];
        }
      });
      
      const response = await axios.get(
        `${process.env.REACT_APP_API_BASE_URL}/delivery-orders`,
        { params }
      );
      
      if (response.data.success) {
        setOrders(response.data.orders || []);
        setTotal(response.data.pagination?.total || 0);
        setStats(response.data.stats || null);
      } else {
        console.error('API response was not successful:', response.data);
      }
    } catch (error) {
      console.error('Error fetching orders:', error);
      Swal.fire({
        icon: 'error',
        title: 'Error Fetching Orders',
        text: error.response?.data?.message || error.message || 'Failed to fetch orders'
      });
    } finally {
      setLoading(false);
    }
  };

  // Enhanced filter handling functions
  const handleFilterChange = (field, value) => {
    setFilters(prev => ({
      ...prev,
      [field]: value
    }));
  };

  // Add search state tracking
  const [isSearchActive, setIsSearchActive] = useState(false);

  // Simplified search handlers like QuotationNew
  const handleItemSearchChange = useCallback((searchTerm) => {
    setItemsSearch(searchTerm);
    setItemsCurrentPage(1);
    setIsSearchActive(true);
    // The debounced search will be triggered by useEffect
  }, []);

  const handleItemSearchClear = useCallback(() => {
    setItemsSearch('');
    setItemsCurrentPage(1);
    setIsSearchActive(false);
  }, []);

  // Fetch items function similar to QuotationNew pattern with focus-friendly loading
  const fetchItemsForModal = useCallback(async (searchTerm = '', currentPage = 1, currentLimit = 5) => {
    try {
      // Small delay before showing loading to prevent rapid state changes
      const loadingTimer = setTimeout(() => {
        setItemsModalLoading(true);
      }, 100);
      
      // Build filter parameters
      const filterParams = {
        search: searchTerm,
        page: currentPage,
        limit: currentLimit
      };

      // Add item-specific filters
      if (itemFilters.stockGroup) {
        filterParams.stockGroup = itemFilters.stockGroup;
      }
      if (itemFilters.sortBy) {
        filterParams.sortBy = itemFilters.sortBy;
      }

      const response = await axios.get(
        `${process.env.REACT_APP_API_BASE_URL}/delivery-orders/tally-items/${user.user.company._id || user.user.company}`,
        {
          params: filterParams
        }
      );

      // Clear the loading timer since we got a response
      clearTimeout(loadingTimer);

      if (response.data.success) {
        const newItems = response.data.data.items;
        const newCount = response.data.data.total;
        const newTotalPages = response.data.data.totalPages;
        
        // Batch update all states together to minimize re-renders
        setItemsList(newItems);
        setCount(newCount);
        setTotalPages(newTotalPages);
        setItemsModalLoading(false);
        
        // Reset search active state after successful fetch
        setTimeout(() => setIsSearchActive(false), 50);
      } else {
        setItemsList([]);
        setCount(0);
        setTotalPages(0);
        setItemsModalLoading(false);
        setTimeout(() => setIsSearchActive(false), 50);
      }
    } catch (error) {
      console.error('Error fetching items:', error);
      
      // Don't show error dialog during search to avoid focus loss
      if (searchTerm === '') {
        Swal.fire({
          icon: 'error',
          title: 'Error',
          text: 'Failed to fetch items: ' + (error.response?.data?.message || error.message)
        });
      }
      
      setItemsList([]);
      setCount(0);
      setTotalPages(0);
      setItemsModalLoading(false);
      setTimeout(() => setIsSearchActive(false), 50);
    }
  }, [user.user.company, itemFilters]);

  // Instant search function for immediate search without debouncing
  const performInstantItemSearch = useCallback((searchTerm) => {
    setItemsCurrentPage(1);
    fetchItemsForModal(searchTerm, 1, itemsPerPage);
  }, [fetchItemsForModal, itemsPerPage]);

  const clearSearch = useCallback((isItemsSearch = false) => {
    if (isItemsSearch) {
      setItemsSearch('');
      setItemsCurrentPage(1);
    } else {
      setQuickSearch('');
      handleFilterChange('search', '');
      setShowMainSearchSuggestions(false);
      setSelectedSuggestionIndex(-1);
    }
  }, [handleFilterChange]);

  const handleMainSearchChange = (value) => {
    setQuickSearch(value);
    handleFilterChange('search', value);
    
    // Generate suggestions based on existing orders
    if (value.trim()) {
      const suggestions = orders
        .filter(order => 
          order.doNumber?.toLowerCase().includes(value.toLowerCase()) ||
          order.recipient?.name?.toLowerCase().includes(value.toLowerCase()) ||
          order.recipient?.businessName?.toLowerCase().includes(value.toLowerCase()) ||
          order.Manual_dO_Number?.toLowerCase().includes(value.toLowerCase()) ||
          order.poNumber?.toLowerCase().includes(value.toLowerCase())
        )
        .map(order => ({
          text: order.doNumber,
          type: 'DO Number',
          fullText: `${order.doNumber} - ${order.recipient?.name || order.recipient?.businessName}`
        }))
        .slice(0, 5);
        
      setSearchSuggestionsList(suggestions);
      setShowMainSearchSuggestions(value.length > 1 && suggestions.length > 0);
    } else {
      setShowMainSearchSuggestions(false);
    }
    setSelectedSuggestionIndex(-1); // Reset selection
  };

  const handleSearchKeyDown = (e, isItemsSearch = false) => {
    const suggestions = isItemsSearch ? searchHistory : searchSuggestionsList;
    const isDropdownVisible = isItemsSearch ? showSearchSuggestions : showMainSearchSuggestions;
    
    // Only handle navigation if dropdown is visible and has suggestions
    if (!isDropdownVisible || suggestions.length === 0) {
      // Allow normal cursor movement in input when no dropdown
      if (e.key === 'ArrowUp' || e.key === 'ArrowDown') {
        return; // Don't prevent default - allow normal cursor movement
      }
    }
    
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      const newIndex = selectedSuggestionIndex < suggestions.length - 1 ? selectedSuggestionIndex + 1 : 0;
      setSelectedSuggestionIndex(newIndex);
      
      // Scroll selected item into view
      setTimeout(() => {
        const dropdownId = isItemsSearch ? 'items-search-dropdown' : 'main-search-dropdown';
        const dropdown = document.getElementById(dropdownId);
        const selectedItem = dropdown?.querySelector(`[data-suggestion-index="${newIndex}"]`);
        if (selectedItem) {
          selectedItem.scrollIntoView({ block: 'nearest', behavior: 'smooth' });
        }
      }, 0);
      
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      const newIndex = selectedSuggestionIndex > 0 ? selectedSuggestionIndex - 1 : suggestions.length - 1;
      setSelectedSuggestionIndex(newIndex);
      
      // Scroll selected item into view
      setTimeout(() => {
        const dropdownId = isItemsSearch ? 'items-search-dropdown' : 'main-search-dropdown';
        const dropdown = document.getElementById(dropdownId);
        const selectedItem = dropdown?.querySelector(`[data-suggestion-index="${newIndex}"]`);
        if (selectedItem) {
          selectedItem.scrollIntoView({ block: 'nearest', behavior: 'smooth' });
        }
      }, 0);
      
    } else if (e.key === 'Enter') {
      e.preventDefault();
      if (isItemsSearch) {
        if (selectedSuggestionIndex >= 0 && selectedSuggestionIndex < suggestions.length) {
          // Use selected suggestion
          const selectedTerm = suggestions[selectedSuggestionIndex];
          setItemsSearch(selectedTerm);
          setShowSearchSuggestions(false);
          setItemsCurrentPage(1);
          // Trigger instant search with selected term
          performInstantItemSearch(selectedTerm);
        } else {
          // Trigger instant search with current search term
          performInstantItemSearch(itemsSearch);
        }
      } else {
        if (selectedSuggestionIndex >= 0 && selectedSuggestionIndex < suggestions.length) {
          const suggestion = suggestions[selectedSuggestionIndex];
          setQuickSearch(suggestion.text);
          handleFilterChange('search', suggestion.text);
          setShowMainSearchSuggestions(false);
          setTimeout(() => applyFilters(), 100);
        }
      }
      setSelectedSuggestionIndex(-1);
      
    } else if (e.key === 'Escape') {
      e.preventDefault();
      if (isItemsSearch) {
        setShowSearchSuggestions(false);
      } else {
        setShowMainSearchSuggestions(false);
      }
      setSelectedSuggestionIndex(-1);
      // Return focus to input
      e.target.focus();
      
    } else if (e.key === 'Tab') {
      // Close dropdown on tab
      if (isItemsSearch) {
        setShowSearchSuggestions(false);
      } else {
        setShowMainSearchSuggestions(false);
      }
      setSelectedSuggestionIndex(-1);
    }
  };

  const handleMultiSelectFilter = (field, selectedOptions) => {
    const values = selectedOptions ? selectedOptions.map(option => option.value) : [];
    setFilters(prev => ({
      ...prev,
      [field]: values
    }));
  };

  const handleDateRangeChange = (range) => {
    const today = new Date();
    let startDate = null;
    let endDate = null;

    switch (range) {
      case 'today':
        startDate = new Date(today);
        endDate = new Date(today);
        break;
      case 'yesterday':
        const yesterday = new Date(today);
        yesterday.setDate(yesterday.getDate() - 1);
        startDate = new Date(yesterday);
        endDate = new Date(yesterday);
        break;
      case 'thisWeek':
        const weekStart = new Date(today);
        weekStart.setDate(today.getDate() - today.getDay());
        startDate = new Date(weekStart);
        endDate = new Date(today);
        break;
      case 'lastWeek':
        const lastWeekEnd = new Date(today);
        lastWeekEnd.setDate(today.getDate() - today.getDay() - 1);
        const lastWeekStart = new Date(lastWeekEnd);
        lastWeekStart.setDate(lastWeekEnd.getDate() - 6);
        startDate = new Date(lastWeekStart);
        endDate = new Date(lastWeekEnd);
        break;
      case 'thisMonth':
        startDate = new Date(today.getFullYear(), today.getMonth(), 1);
        endDate = new Date(today);
        break;
      case 'lastMonth':
        const lastMonth = new Date(today.getFullYear(), today.getMonth() - 1, 1);
        const lastMonthEnd = new Date(today.getFullYear(), today.getMonth(), 0);
        startDate = new Date(lastMonth);
        endDate = new Date(lastMonthEnd);
        break;
      case 'last7Days':
        const sevenDaysAgo = new Date(today);
        sevenDaysAgo.setDate(today.getDate() - 7);
        startDate = new Date(sevenDaysAgo);
        endDate = new Date(today);
        break;
      case 'last30Days':
        const thirtyDaysAgo = new Date(today);
        thirtyDaysAgo.setDate(today.getDate() - 30);
        startDate = new Date(thirtyDaysAgo);
        endDate = new Date(today);
        break;
      case 'thisYear':
        startDate = new Date(today.getFullYear(), 0, 1);
        endDate = new Date(today);
        break;
      default:
        break;
    }

    setFilters(prev => ({
      ...prev,
      dateRange: range,
      startDate,
      endDate
    }));
  };

  const resetFilters = () => {
    setFilters({
      search: '',
      searchType: 'all',
      status: '',
      recipientType: '',
      modeOfSales: '',
      paymentModes: [],
      salesmen: [],
      transporters: [],
      references: [],
      startDate: null,
      endDate: null,
      dateRange: '',
      minAmount: '',
      maxAmount: '',
      type: '',
      hasItems: '',
      itemCount: '',
      minItemCount: '',
      maxItemCount: '',
      createdBy: [],
      creditPeriod: '',
      minCreditPeriod: '',
      maxCreditPeriod: ''
    });
    fetchOrders();
    fetchStats();
  };

  const applyFilters = () => {
    setPage(1);
    
    // Track filter history
    const filterSnapshot = {
      ...filters,
      timestamp: new Date(),
      id: Date.now()
    };
    
    setFilterHistory(prev => {
      const newHistory = [filterSnapshot, ...prev.slice(0, 9)]; // Keep last 10
      localStorage.setItem('deliveryOrderFilterHistory', JSON.stringify(newHistory));
      return newHistory;
    });
    
    fetchOrders();
    fetchStats();
  };

  const saveFilterPreset = async () => {
    const { value: presetName } = await Swal.fire({
      title: 'Save Filter Preset',
      input: 'text',
      inputLabel: 'Preset Name',
      inputPlaceholder: 'Enter a name for this filter preset',
      showCancelButton: true,
      inputValidator: (value) => {
        if (!value) {
          return 'Please enter a preset name!';
        }
      }
    });

    if (presetName) {
      const newPreset = {
        name: presetName,
        filters: { ...filters },
        createdAt: new Date()
      };
      
      const updatedPresets = [...filterPresets, newPreset];
      setFilterPresets(updatedPresets);
      localStorage.setItem('deliveryOrderFilterPresets', JSON.stringify(updatedPresets));
      
      Swal.fire('Saved!', 'Filter preset has been saved.', 'success');
    }
  };

  const loadFilterPreset = (preset) => {
    setFilters({ ...preset.filters });
    applyFilters();
  };

  const deleteFilterPreset = async (index) => {
    const result = await Swal.fire({
      title: 'Delete Filter Preset?',
      text: 'This action cannot be undone.',
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#d33',
      cancelButtonColor: '#3085d6',
      confirmButtonText: 'Yes, delete it!'
    });

    if (result.isConfirmed) {
      const updatedPresets = filterPresets.filter((_, i) => i !== index);
      setFilterPresets(updatedPresets);
      localStorage.setItem('deliveryOrderFilterPresets', JSON.stringify(updatedPresets));
      Swal.fire('Deleted!', 'Filter preset has been deleted.', 'success');
    }
  };

  // Load saved filter presets and history on component mount
  useEffect(() => {
    const savedPresets = localStorage.getItem('deliveryOrderFilterPresets');
    if (savedPresets) {
      setFilterPresets(JSON.parse(savedPresets));
    }
    
    const savedHistory = localStorage.getItem('deliveryOrderFilterHistory');
    if (savedHistory) {
      setFilterHistory(JSON.parse(savedHistory));
    }

    const savedItemsSearchHistory = localStorage.getItem('itemsSearchHistory');
    if (savedItemsSearchHistory) {
      setSearchHistory(JSON.parse(savedItemsSearchHistory));
    }
  }, []);



  // Fetch initial data
  useEffect(() => {
    const fetchInitialData = async () => {
      try {
        setLoading(true);
        const userCompanyId = user.user.company._id || user.user.company;
        const [customersRes, dealersRes, employeesRes, transportersRes, godownsRes] = await Promise.all([
          axios.get(`${process.env.REACT_APP_API_BASE_URL}/customers/all?userCompanyId=${userCompanyId}`),
          axios.get(`${process.env.REACT_APP_API_BASE_URL}/dealers/all?userCompanyId=${userCompanyId}`),
          axios.get(`${process.env.REACT_APP_API_BASE_URL}/employees/company2/${userCompanyId}`),
          axios.get(`${process.env.REACT_APP_API_BASE_URL}/transporters/all?userCompanyId=${userCompanyId}`),
          axios.get(`${process.env.REACT_APP_API_BASE_URL}/delivery-orders/godowns/${userCompanyId}`)
        ]);

        setCustomers(customersRes.data || []);
        setDealers(dealersRes.data?.dealers || []);
        setEmployees(employeesRes.data.employees || []);
        setTransporters(transportersRes.data || []);
        setGodowns(godownsRes.data?.data || []);
      } catch (error) {
        console.error('Error fetching initial data:', error);
        Swal.fire('Error', 'Failed to fetch initial data', 'error');
      } finally {
        setLoading(false);
      }
    };

    // Fetch references (was interiors)
    const fetchReferences = async () => {
      try {
        const userCompanyId = user.user.company._id || user.user.company;
        const response = await axios.get(`${process.env.REACT_APP_API_BASE_URL}/references`, { 
          params: { 
            limit: 1000, 
            page: 1, 
            userCompanyId: userCompanyId 
          } 
        });
        setReferences(response.data.references || []);
      } catch (err) {
        console.error('Error fetching references:', err);
        setReferences([]);
      }
    };

    fetchInitialData();
    fetchReferences();
  }, [user.user.company]);

  // Update the fetchData function to include the last 7 days stats
  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        await Promise.all([
          fetchOrders(),
          fetchStats()
        ]);
        
        // Fetch last 7 days stats separately
        const sevenDaysData = await fetchLastSevenDaysStats();
        setLastSevenDaysStats(sevenDaysData);
      } catch (error) {
        console.error('Error fetching data:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [page, limit, sortBy, sortOrder, filters]);

  // Fetch items when modal opens or pagination changes 
  useEffect(() => {
    if (showItemsModal) {
      fetchItemsForModal(itemsSearch, itemsCurrentPage, itemsPerPage);
    }
  }, [showItemsModal, itemsCurrentPage, itemsPerPage, fetchItemsForModal]);

  // Handle search with debouncing
  useEffect(() => {
    if (!showItemsModal) return;
    
    const timeoutId = setTimeout(() => {
      fetchItemsForModal(itemsSearch, 1, itemsPerPage);
      if (itemsCurrentPage !== 1) {
        setItemsCurrentPage(1);
      }
    }, 300);

    return () => clearTimeout(timeoutId);
  }, [itemsSearch, fetchItemsForModal, itemsPerPage, itemsCurrentPage]);

  // Refetch items when filters change
  useEffect(() => {
    if (showItemsModal) {
      setItemsCurrentPage(1);
      fetchItemsForModal(itemsSearch, 1, itemsPerPage);
    }
  }, [itemFilters, showItemsModal]);

  // Restore focus after items are loaded/updated
  useEffect(() => {
    if (showItemsModal && !itemsModalLoading) {
      // Small delay to ensure DOM is updated
      setTimeout(() => {
        const searchInput = document.querySelector('[data-focus-preserve="true"]');
        if (searchInput && searchInput.dataset.focusPreserve === 'true') {
          try {
            searchInput.focus();
            // Restore cursor to end of input if there's text
            if (searchInput.value.length > 0) {
              searchInput.setSelectionRange(searchInput.value.length, searchInput.value.length);
            }
          } catch (e) {
            console.debug('Post-load focus restoration failed:', e);
          }
        }
      }, 50);
    }
  }, [itemsList, itemsModalLoading, showItemsModal]);

  // Global focus interceptor during active search
  useEffect(() => {
    if (!showItemsModal || (!itemsModalLoading && !isSearchActive)) return;

    const handleGlobalFocusChange = (e) => {
      const searchInput = document.querySelector('[data-focus-preserve="true"]');
      
      // If focus is moving away from search input during loading/searching
      if (searchInput && e.target !== searchInput && (itemsModalLoading || isSearchActive)) {
        e.preventDefault();
        e.stopImmediatePropagation();
        
        // Force focus back to search input
        setTimeout(() => {
          if (searchInput) {
            try {
              searchInput.focus();
              // Maintain cursor position
              const cursorPos = searchInput.value.length;
              searchInput.setSelectionRange(cursorPos, cursorPos);
            } catch (err) {
              console.debug('Global focus intercept failed:', err);
            }
          }
        }, 0);
      }
    };

    // Use capture phase to intercept focus changes before they happen
    document.addEventListener('focusin', handleGlobalFocusChange, true);
    document.addEventListener('focusout', handleGlobalFocusChange, true);

    return () => {
      document.removeEventListener('focusin', handleGlobalFocusChange, true);
      document.removeEventListener('focusout', handleGlobalFocusChange, true);
    };
  }, [showItemsModal, itemsModalLoading, isSearchActive]);

  // Fetch stock groups for filtering
  const fetchStockGroups = useCallback(async () => {
    try {
      const response = await axios.get(
        `${process.env.REACT_APP_API_BASE_URL}/delivery-orders/stock-groups/${user.user.company._id || user.user.company}`
      );

      if (response.data.success) {
        setStockGroups(response.data.data);
      }
    } catch (error) {
      console.error('Error fetching stock groups:', error);
    }
  }, [user.user.company]);

  // Fetch stock groups when modal is opened
  useEffect(() => {
    if (showItemsModal && stockGroups.length === 0) {
      fetchStockGroups();
    }
  }, [showItemsModal, fetchStockGroups, stockGroups.length]);

  // Fetch unused proforma invoices for importing
  const fetchProformaInvoices = useCallback(async (searchTerm = '', currentPage = 1) => {
    if (!user?.user?.company) return;
    
    setProformaLoading(true);
    try {
      const userCompanyId = user.user.company._id || user.user.company;
      const response = await axios.get(
        `${process.env.REACT_APP_API_BASE_URL}/delivery-orders/unused-proforma-invoices/${userCompanyId}`,
        {
          params: {
            page: currentPage,
            limit: 10,
            search: searchTerm
          }
        }
      );

      if (response.data.success) {
        setProformaInvoices(response.data.data);
        setProformaTotalPages(response.data.pagination.totalPages);
      }
    } catch (error) {
      console.error('Error fetching proforma invoices:', error);
    } finally {
      setProformaLoading(false);
    }
  }, [user.user.company]);

  // Fetch proforma invoices when modal opens or pagination changes
  useEffect(() => {
    if (showProformaImportModal) {
      fetchProformaInvoices(proformaSearch, proformaPage);
    }
  }, [showProformaImportModal, proformaSearch, proformaPage, fetchProformaInvoices]);

  // Debounced calculation effect for TT values
  useEffect(() => {
    const timeoutId = setTimeout(async () => {
      if (form.items && form.items.some(item => item.tt?.value > 0) && !isUserTyping.current) {
        try {
          const calculatedForm = await calculateTotals(form);
          // Only update calculated amounts, preserve user inputs
          setForm(prevForm => {
            // Check if there are actual changes to prevent infinite loops
            const hasChanges = prevForm.totalTTAmount !== calculatedForm.totalTTAmount ||
              prevForm.totalAmountWithGST !== calculatedForm.totalAmountWithGST ||
              prevForm.totalAmountWithoutGST !== calculatedForm.totalAmountWithoutGST;
            
            if (!hasChanges) return prevForm;
            
            return {
              ...calculatedForm,
              items: calculatedForm.items.map((calcItem, index) => ({
                ...calcItem,
                // Preserve the original TT input values to avoid reverting
                tt: {
                  ...calcItem.tt,
                  type: prevForm.items[index]?.tt?.type || calcItem.tt?.type,
                  value: prevForm.items[index]?.tt?.value || calcItem.tt?.value
                }
              }))
            };
          });
        } catch (error) {
          console.warn('Failed to calculate TT totals:', error);
        }
      }
    }, 400); // 400ms delay to allow user to finish typing

    return () => clearTimeout(timeoutId);
  }, [form.items?.length, form.items?.map(item => `${item.tt?.value}-${item.quantity}-${item.rate}-${item.discount}`).join(',')]); // More specific dependencies to prevent unnecessary recalculations

  // Define calculateTotals at component scope
  const calculateTotals = useCallback(async (formData) => {
    console.log('calculateTotals called with:', {
      hasFormData: !!formData,
      hasItems: !!formData?.items,
      itemsCount: formData?.items?.length || 0,
      packingAndForwarding: formData?.packingAndForwarding || 0,
      labour: formData?.labour || 0,
      cartage: formData?.cartage || 0
    });
    
    if (!formData || !formData.items) return formData;

    // Calculate items total and TT amounts
    const items = formData.items || [];
    let itemsTotal = 0;
    let totalTTAmount = 0;

    // Update items with calculated amounts and TT
    const updatedItems = await Promise.all(items.map(async (item, index) => {
      if (!item) {
        console.warn(`Item at index ${index} is null or undefined`);
        return null;
      }
      
      const quantity = parseFloat(item.quantity) || 0;
      const rate = parseFloat(item.rate) || 0;
      const discount = parseFloat(item.discount) || 0;
      
      if (quantity === 0 && rate === 0) {
        console.warn(`Item "${item.Name}" has zero quantity and rate`);
        return item; // Return item as-is if no quantity/rate
      }
      
      const amountBeforeDiscount = quantity * rate;
      const discountAmount = amountBeforeDiscount * (discount / 100);
      const amountAfterDiscount = amountBeforeDiscount - discountAmount;
      
      console.log('calculateTotals Item Processing:', {
        itemName: item.Name,
        quantity,
        rate,
        discount,
        amountBeforeDiscount,
        discountAmount,
        amountAfterDiscount,
        hasTT: !!(item.tt && item.tt.value > 0),
        currentItemsTotal: itemsTotal
      });
      
      itemsTotal += amountAfterDiscount;
      
      console.log('After adding item to total:', {
        addedAmount: amountAfterDiscount,
        newItemsTotal: itemsTotal
      });

      // Calculate TT amount
      let ttCalculatedAmount = 0;
      if (item.tt && item.tt.value > 0) {
        if (item.itemId) {
          try {
            // Fetch TallyItem to get integrated tax percentage
            const response = await axios.get(`${process.env.REACT_APP_API_BASE_URL}/delivery-orders/tally-item-details/${item.itemId}`, {
              headers: {
                Authorization: `Bearer ${user.token}`,
              }
            });
            
            const tallyItem = response.data.data;
            const integratedTaxPercentage = parseFloat(tallyItem?.integratedTax) || 0; // Ensure conversion to float
            
            console.log('calculateTotals TT Debug:', {
              itemId: item.itemId,
              itemName: item.Name,
              tallyItemIntegratedTax: tallyItem?.integratedTax,
              parsedIntegratedTax: integratedTaxPercentage,
              quantity,
              rate,
              discount,
              amountBeforeDiscount,
              amountAfterDiscount,
              ttValue: item.tt.value,
              ttType: item.tt.type
            });
            
            // Calculate item value without GST using amount after discount and new formula
            // Formula: amountAfterDiscount * 100 / (100 + tax_rate)
            const itemValueWithoutGST = (amountAfterDiscount * 100) / (100 + integratedTaxPercentage);
            
            console.log('calculateTotals TT Calculation:', {
              amountAfterDiscount,
              itemValueWithoutGST,
              integratedTaxPercentage,
              ttCalculation: itemValueWithoutGST * (item.tt.value / 100),
              finalTTAmount: Math.min(itemValueWithoutGST * (item.tt.value / 100), itemValueWithoutGST)
            });
            
            if (item.tt.type === 'percentage') {
              // TTValueForPercentage = ItemValueWithOutGST * (TTPercentage/100)
              ttCalculatedAmount = itemValueWithoutGST * (item.tt.value / 100);
            } else if (item.tt.type === 'fixed') {
              // TTValueForAmount = TTFixedAmount (but not exceeding ItemValueWithOutGST)
              ttCalculatedAmount = Math.min(item.tt.value, itemValueWithoutGST);
            }
            
            // Ensure TT amount doesn't exceed item value without GST
            ttCalculatedAmount = Math.min(ttCalculatedAmount, itemValueWithoutGST);
            totalTTAmount += ttCalculatedAmount;
            
          } catch (error) {
            console.warn(`Failed to calculate TT for item ${item.itemId}:`, error);
            ttCalculatedAmount = 0;
          }
        } else if (quantity > 0 && rate > 0) {
          // Fallback calculation when itemId is not available - use item's integrated tax or default
          const defaultIntegratedTax = parseFloat(item.integratedTax) || 18; // Ensure conversion to float
          const amountAfterDiscount = (quantity * rate) * (1 - (discount / 100)); // Calculate amount after discount
          const itemValueWithoutGST = (amountAfterDiscount * 100) / (100 + defaultIntegratedTax);
          
          if (item.tt.type === 'percentage') {
            ttCalculatedAmount = itemValueWithoutGST * (item.tt.value / 100);
          } else if (item.tt.type === 'fixed') {
            ttCalculatedAmount = Math.min(item.tt.value, itemValueWithoutGST);
          }
          
          ttCalculatedAmount = Math.min(ttCalculatedAmount, itemValueWithoutGST);
          totalTTAmount += ttCalculatedAmount;
        }
      }
      
      return {
        ...item,
        amountBeforeDiscount: parseFloat(amountBeforeDiscount?.toFixed(2)),
        amountAfterDiscount: parseFloat(amountAfterDiscount?.toFixed(2)),
        tt: {
          ...(item.tt || { type: 'percentage', value: 0 }),
          calculatedAmount: parseFloat(ttCalculatedAmount?.toFixed(2))
        }
      };
    }));

    const validItems = updatedItems.filter(Boolean);
    
    console.log('Items processing result:', {
      originalItemsCount: items.length,
      processedItemsCount: updatedItems.length,
      validItemsCount: validItems.length,
      itemsTotal,
      nullItems: updatedItems.filter(item => item === null).length,
      validItemsWithAmounts: validItems.filter(item => (item.amountAfterDiscount || 0) > 0).length
    });

    // Calculate charges and their GST
    const packingAndForwarding = parseFloat(formData.packingAndForwarding) || 0;
    const labour = parseFloat(formData.labour) || 0;
    const cartage = parseFloat(formData.cartage) || 0;
    const roundoff = parseFloat(formData.roundoff) || 0;

    // Get dynamic tax rate for charges based on items
    const chargesTaxRate = getChargesTaxRate(validItems) / 100; // Convert to decimal

    // Use fixed 18% GST for additional charges (P&F, Labour, Cartage)
    const additionalChargesGSTRate = 0.18; // Fixed 18% for additional charges

    // Calculate GST for each charge using fixed 18% rate
    const packingAndForwardingGST = packingAndForwarding * additionalChargesGSTRate;
    const labourGST = labour * additionalChargesGSTRate;
    const cartageGST = cartage * additionalChargesGSTRate;

    // Calculate total amount without GST (base amount of items and charges)
    const totalAmountWithoutGST = itemsTotal + packingAndForwarding + labour + cartage;

    console.log('Total calculation breakdown:', {
      itemsTotal,
      packingAndForwarding,
      labour,
      cartage,
      itemsTotalType: typeof itemsTotal,
      itemsTotalIsNaN: isNaN(itemsTotal),
      calculation: `${itemsTotal} + ${packingAndForwarding} + ${labour} + ${cartage}`,
      result: totalAmountWithoutGST
    });

    // Calculate total GST amount
    const totalGST = packingAndForwardingGST + labourGST + cartageGST;

    // Calculate final amount including everything (TT amount should NOT affect final total)
    const totalAmountWithGST = totalAmountWithoutGST + totalGST + roundoff;

    console.log('calculateTotals Final Summary:', {
      itemsTotal,
      packingAndForwarding,
      labour,
      cartage,
      totalAmountWithoutGST,
      packingAndForwardingGST: packingAndForwarding * 0.18,
      labourGST: labour * 0.18,
      cartageGST: cartage * 0.18,
      totalGST,
      totalTTAmount,
      roundoff,
      totalAmountWithGST
    });

    return {
      ...formData,
      items: validItems,
      packingAndForwardingGST: parseFloat(packingAndForwardingGST?.toFixed(2)),
      labourGST: parseFloat(labourGST?.toFixed(2)),
      cartageGST: parseFloat(cartageGST?.toFixed(2)),
      totalAmountWithoutGST: parseFloat(totalAmountWithoutGST?.toFixed(2)),
      totalAmountWithGST: parseFloat(totalAmountWithGST?.toFixed(2)),
      totalTTAmount: parseFloat(totalTTAmount?.toFixed(2))
    };
  }, []); // Stable function - user.token is accessed from closure

  // Cleanup typing timeouts on unmount
  useEffect(() => {
    return () => {
      if (typingTimeout.current) {
        clearTimeout(typingTimeout.current);
      }
    };
  }, []);

  // Calculate totals when form changes
  useEffect(() => {
    console.log('Totals calculation useEffect triggered:', {
      hasForm: !!form,
      hasItems: !!form?.items,
      itemsCount: form?.items?.length || 0,
      calculationInProgress: calculationInProgress.current,
      isUserTyping: isUserTyping.current
    });
    
    if (!form || !form.items || calculationInProgress.current || isUserTyping.current) return;
    
    const debouncedCalculation = setTimeout(async () => {
      // Double check user isn't actively typing
      if (isUserTyping.current) return;
      
      console.log('Starting totals calculation...');
      calculationInProgress.current = true;
      try {
        const updatedForm = await calculateTotals(form);
        
        console.log('Calculation result:', {
          originalTotalWithoutGST: form.totalAmountWithoutGST,
          calculatedTotalWithoutGST: updatedForm.totalAmountWithoutGST,
          originalTotalWithGST: form.totalAmountWithGST,
          calculatedTotalWithGST: updatedForm.totalAmountWithGST
        });
        
        if (updatedForm && JSON.stringify(updatedForm) !== JSON.stringify(form)) {
          setForm(updatedForm);
        }
      } catch (error) {
        console.error('Error calculating totals:', error);
      } finally {
        calculationInProgress.current = false;
      }
    }, 300); // Reduced debounce time to make it more responsive

    return () => {
      clearTimeout(debouncedCalculation);
      calculationInProgress.current = false;
    };
  }, [
    form?.items, 
    form?.labour, 
    form?.cartage, 
    form?.packingAndForwarding, 
    form?.roundoff,
    form?.cartageGST, // Add GST fields to the dependency array
    form?.labourGST,
    form?.packingAndForwardingGST,
    // Force recalculation when totals are zero but items exist
    form?.totalAmountWithoutGST === 0 && form?.items?.length > 0
    // Remove calculateTotals from dependencies to prevent infinite loop
  ]);

  // Force recalculation if totals are incorrect - more aggressive approach
  useEffect(() => {
    // Check if we have items with value but totals are zero
    const hasValidItems = form?.items?.some(item => 
      (parseFloat(item.quantity) || 0) > 0 && (parseFloat(item.rate) || 0) > 0
    );
    const hasZeroTotals = (form?.totalAmountWithoutGST || 0) === 0;
    
    if (hasValidItems && hasZeroTotals && !calculationInProgress.current && !isUserTyping.current) {
      console.log('FORCE RECALCULATION: Items exist but totals are zero');
      
      // Immediate synchronous calculation and update
      let directItemsTotal = 0;
      if (form.items && form.items.length > 0) {
        directItemsTotal = form.items.reduce((sum, item) => {
          if (!item) return sum;
          const quantity = parseFloat(item.quantity) || 0;
          const rate = parseFloat(item.rate) || 0;
          const discount = parseFloat(item.discount) || 0;
          const amountAfterDiscount = (quantity * rate) * (1 - discount / 100);
          return sum + amountAfterDiscount;
        }, 0);
      }
      
      const packingAndForwarding = parseFloat(form.packingAndForwarding) || 0;
      const labour = parseFloat(form.labour) || 0;
      const cartage = parseFloat(form.cartage) || 0;
      const roundoff = parseFloat(form.roundoff) || 0;
      
      const totalWithoutGST = directItemsTotal + packingAndForwarding + labour + cartage;
      const gstOnCharges = (packingAndForwarding + labour + cartage) * 0.18;
      const totalTT = form.items?.reduce((sum, item) => sum + (parseFloat(item.tt?.calculatedAmount) || 0), 0) || 0;
      const totalWithGST = totalWithoutGST + gstOnCharges + roundoff; // TT amount excluded from final total
      
      console.log('FORCE UPDATE with calculated values:', {
        directItemsTotal,
        totalWithoutGST,
        totalWithGST
      });
      
      // Direct synchronous form update
      setForm(prev => ({
        ...prev,
        totalAmountWithoutGST: parseFloat(totalWithoutGST.toFixed(2)),
        totalAmountWithGST: parseFloat(totalWithGST.toFixed(2)),
        packingAndForwardingGST: parseFloat((packingAndForwarding * 0.18).toFixed(2)),
        labourGST: parseFloat((labour * 0.18).toFixed(2)),
        cartageGST: parseFloat((cartage * 0.18).toFixed(2))
      }));
    }
  }, [form?.items, form?.totalAmountWithoutGST, form?.packingAndForwarding, form?.labour, form?.cartage]);

  // Handle manual draft save
  // Handle form submission
  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!(await validateForm())) {
      return;
    }

    try {
      setSubmitting(true);
      
      // Ensure calculations are up-to-date before submission
      const finalForm = await calculateTotals(form);
      
      console.log('Final form with godown:', {
        godown: finalForm.godown,
        hasGodown: !!finalForm.godown
      });

      console.log('Final form with calculations:', {
        totalTTAmount: finalForm.totalTTAmount,
        items: finalForm.items.map(item => ({
          Name: item.Name,
          tt: item.tt,
          quantity: item.quantity,
          rate: item.rate
        }))
      });

      // Get current financial year (April to March format)
      const today = new Date();
      const currentMonth = today.getMonth(); // 0-11
      const currentYear = today.getFullYear();
      const financialYear = currentMonth < 3 ? 
        `${currentYear-1}-${currentYear}` : 
        `${currentYear}-${currentYear+1}`;

      // Calculate proforma invoices usage tracking
      const proformaUsageMap = new Map();
      finalForm.items.forEach(item => {
        if (item.proformaInvoice && item.proformaItemId) {
          if (!proformaUsageMap.has(item.proformaInvoice)) {
            proformaUsageMap.set(item.proformaInvoice, []);
          }
          proformaUsageMap.get(item.proformaInvoice).push({
            itemId: item.proformaItemId,
            quantityUsed: item.quantity
          });
        }
      });

      const proformaInvoicesUsed = Array.from(proformaUsageMap.entries()).map(([proformaId, items]) => ({
        proformaInvoice: proformaId,
        itemsUsed: items
      }));

      // Filter out items with empty or invalid itemIds before submission
      const validItems = finalForm.items.filter(item => {
        if (!item.itemId || item.itemId.trim() === '') {
          console.warn('Filtering out item with empty itemId:', item);
          return false;
        }
        if (!item.quantity || item.quantity <= 0) {
          console.warn('Filtering out item with invalid quantity:', item);
          return false;
        }
        if (item.rate === undefined || item.rate === null || item.rate < 0) {
          console.warn('Filtering out item with invalid rate:', item);
          return false;
        }
        return true;
      });

      // Check if we have any valid items
      if (validItems.length === 0) {
        Swal.fire({
          icon: 'warning',
          title: 'No Valid Items',
          text: 'Please add at least one item with valid details before submitting.'
        });
        return;
      }

      // Show warning if some items were filtered out
      if (validItems.length < finalForm.items.length) {
        const result = await Swal.fire({
          icon: 'warning',
          title: 'Invalid Items Detected',
          text: `${finalForm.items.length - validItems.length} item(s) with empty or invalid data will be excluded. Continue?`,
          showCancelButton: true,
          confirmButtonText: 'Yes, Continue',
          cancelButtonText: 'Cancel'
        });
        
        if (!result.isConfirmed) {
          return;
        }
      }

      const formData = {
        recipientType: finalForm.recipientType,
        recipient: finalForm.recipient.value,
        items: validItems.map(item => {
          console.log('Submitting item with TT:', {
            itemId: item.itemId,
            Name: item.Name,
            tt: item.tt,
            quantity: item.quantity,
            rate: item.rate
          });
          return {
            itemId: item.itemId,
            quantity: item.quantity,
            rate: item.rate,
            units: item.units,
            discount: item.discount,
            amountBeforeDiscount: item.amountBeforeDiscount,
            amountAfterDiscount: item.amountAfterDiscount,
            printType: item.printType || 'item', // Include printType
            description: item.description || '', // Include description
            tt: item.tt || { type: 'percentage', value: 0, calculatedAmount: 0 }, // Include TT details
            // Include proforma tracking fields if they exist
            ...(item.proformaInvoice && {
              proformaInvoice: item.proformaInvoice,
              proformaItemId: item.proformaItemId
            })
          };
        }),
        vehicleNumber: finalForm.vehicleNumber,

        transporterName: finalForm.transporterName,
        siteAddress: finalForm.siteAddress,
        modeOfPayment: finalForm.modeOfPayment,
        modeOfSales: finalForm.modeOfSales,
        creditReference: finalForm.creditReference,
        creditPeriod: finalForm.creditPeriod,
        salesman: finalForm.salesman,
        reference: finalForm.reference, // Change interior to reference
        godown: finalForm.godown, // Add godown field
        isreference: finalForm.isreference, // Add isreference field
        isnarration: finalForm.isnarration, // Add isnarration field
        packingAndForwarding: finalForm.packingAndForwarding,
        packingAndForwardingGST: finalForm.packingAndForwardingGST,
        labour: finalForm.labour,
        labourGST: finalForm.labourGST,
        cartage: finalForm.cartage,
        cartageGST: finalForm.cartageGST,
        roundoff: finalForm.roundoff,
        Manual_dO_Number: finalForm.Manual_dO_Number,
        poNumber: finalForm.poNumber,
        Narration: finalForm.Narration,
        totalAmountWithoutGST: finalForm.totalAmountWithoutGST,
        totalAmountWithGST: finalForm.totalAmountWithGST,
        totalTTAmount: finalForm.totalTTAmount, // Ensure TT amount is included
        createdBy: user?.user?._id, // Add createdBy
        companyId: (user?.user?.company._id || user?.user?.company), // Add companyId
        financialYear: financialYear, // Add financialYear
        type: finalForm.type, // Add type
        proformaInvoicesUsed: proformaInvoicesUsed // Add proforma usage tracking
      };

      let response;
      
      if (isEditing) {
        response = await axios.put(
          `${process.env.REACT_APP_API_BASE_URL}/delivery-orders/${orderId}`,
          formData
        );
      } else {
        response = await axios.post(
          `${process.env.REACT_APP_API_BASE_URL}/delivery-orders`,
          formData
        );
      }

      if (response.data.success) {
        // Delete the draft after successful order creation
        if (draftManager.currentDraftId) {
          try {
            await draftManager.deleteDraft(draftManager.currentDraftId);
            console.log('✅ Draft deleted after successful order creation');
          } catch (draftError) {
            console.error('⚠️ Failed to delete draft:', draftError);
            // Don't show error to user, draft deletion is not critical
          }
        }
        
        Swal.fire({
          icon: 'success',
          title: 'Success',
          text: `Delivery order ${isEditing ? 'updated' : 'created'} successfully`
        });
        resetForm();
        fetchOrders();
      }
    } catch (error) {
      console.error('Error submitting delivery order:', error);
      Swal.fire({
        icon: 'error',
        title: 'Error',
        text: error.response?.data?.message || error.message || 'Failed to process delivery order'
      });
    } finally {
      setSubmitting(false);
    }
  };

  // Add validateForm function
  const validateForm = async () => {
    // Check recipient
    if (!form.recipient || !form.recipient.value) {
      Swal.fire({
        icon: 'error',
        title: 'Recipient Required',
        text: 'Please select a valid recipient',
        confirmButtonColor: '#3085d6'
      });
      return false;
    }

    // Check items
    if (form.items.length === 0) {
      Swal.fire({
        icon: 'error',
        title: 'Items Required',
        text: 'Please add at least one item to the order',
        confirmButtonColor: '#3085d6'
      });
      return false;
    }
    
    // Validate each item
    const negativeQuantityItems = [];
    const negativeRateItems = [];
    const insufficientStockItems = [];

    for (let i = 0; i < form.items.length; i++) {
      const item = form.items[i];
      
      if (item.quantity < 0) {
        negativeQuantityItems.push(`Item #${i + 1}`);
      }
      
      if (parseFloat(item.quantity) > parseFloat(item.availableStock)) {
        insufficientStockItems.push(`Item #${i + 1}: Qty ${item.quantity} > Stock ${item.availableStock}`);
      }
      
      if (item.rate < 0) {
        negativeRateItems.push(`Item #${i + 1}`);
      }
    }

    if (negativeQuantityItems.length > 0) {
      Swal.fire({
        icon: 'error',
        title: 'Invalid Quantity',
        html: `Quantity cannot be negative for:<br/><b>${negativeQuantityItems.join(', ')}</b>`,
        confirmButtonColor: '#3085d6'
      });
      return false;
    }

    if (negativeRateItems.length > 0) {
      Swal.fire({
        icon: 'error',
        title: 'Invalid Rate',
        html: `Rate cannot be negative for:<br/><b>${negativeRateItems.join(', ')}</b>`,
        confirmButtonColor: '#3085d6'
      });
      return false;
    }

    if (insufficientStockItems.length > 0) {
      const result = await Swal.fire({
        icon: 'warning',
        title: 'Insufficient Stock',
        html: `The following items exceed available stock:<br/><br/>${insufficientStockItems.map(s => `• ${s}`).join('<br/>')}<br/><br/>Do you want to continue anyway?`,
        showCancelButton: true,
        confirmButtonColor: '#3085d6',
        cancelButtonColor: '#d33',
        confirmButtonText: 'Yes, Continue',
        cancelButtonText: 'No, I\'ll Change It'
      });
      
      if (!result.isConfirmed) {
        return false;
      }
    }
    
    // Check salesmen
    if (form.salesman.length === 0) {
      Swal.fire({
        icon: 'error',
        title: 'Salesman Required',
        text: 'Please select at least one salesman',
        confirmButtonColor: '#3085d6'
      });
      return false;
    }

    // Validate based on mode of sales
    if (form.modeOfSales === 'Credit') {
      if (!form.creditReference) {
        Swal.fire({
          icon: 'error',
          title: 'Credit Reference Required',
          text: 'Credit reference is required for credit sales',
          confirmButtonColor: '#3085d6'
        });
        return false;
      }

      if (form.creditPeriod < 0) {
        Swal.fire({
          icon: 'error',
          title: 'Invalid Credit Period',
          text: 'Credit period cannot be negative',
          confirmButtonColor: '#3085d6'
        });
        return false;
      }
    }
    
    // Validate payment modes
    if (form.modeOfPayment.length === 0) {
      Swal.fire({
        icon: 'error',
        title: 'Payment Mode Required',
        text: 'Please select at least one payment mode',
        confirmButtonColor: '#3085d6'
      });
      return false;
    }

    // If mode of sales is Credit, ensure payment modes include credit
    if (form.modeOfSales === 'Credit' && !form.modeOfPayment.some(mode => mode.toLowerCase() === 'credit')) {
      Swal.fire({
        icon: 'error',
        title: 'Payment Mode Mismatch',
        text: 'When Mode of Sales is Credit, at least one payment mode must be Credit',
        confirmButtonColor: '#3085d6'
      });
      return false;
    }

    // Validate claim payment from fields (optional but if provided, must be valid)
    if (form.claimPaymentFrom?.name && form.claimPaymentFrom.name.length > 100) {
      Swal.fire({
        icon: 'error',
        title: 'Invalid Contact Person Name',
        text: 'Contact person name cannot exceed 100 characters',
        confirmButtonColor: '#3085d6'
      });
      return false;
    }

    if (form.claimPaymentFrom?.number) {
      // Must be exactly 10 digits
      const isValidFormat = /^\d{10}$/.test(form.claimPaymentFrom.number);
      
      if (!isValidFormat) {
        Swal.fire({
          icon: 'error',
          title: 'Invalid Contact Number',
          text: 'Contact number must be exactly 10 numeric digits',
          confirmButtonColor: '#3085d6'
        });
        return false;
      }
    }

    // If only one field is provided, suggest completing the other (but don't block submission)
    if ((form.claimPaymentFrom?.name && !form.claimPaymentFrom?.number) || 
        (!form.claimPaymentFrom?.name && form.claimPaymentFrom?.number)) {
      const result = await Swal.fire({
        icon: 'info',
        title: 'Incomplete Claim Payment Information',
        text: 'You\'ve provided partial claim payment information. Would you like to complete both contact person name and contact number for better record keeping?',
        showCancelButton: true,
        confirmButtonColor: '#3085d6',
        cancelButtonColor: '#6c757d',
        confirmButtonText: 'I\'ll Complete It',
        cancelButtonText: 'Continue Anyway'
      });
      
      if (result.isConfirmed) {
        return false; // Stop submission to allow user to complete
      }
    }

    return true;
  };

  // Add updateTotals function
  const updateTotals = useCallback(async () => {
    try {
      const calculatedForm = await calculateTotals(form);
      setForm(calculatedForm);
    } catch (error) {
      console.warn('Failed to update totals:', error);
    }
  }, [calculateTotals]); // Remove form from dependencies to prevent infinite loop

  // Update handlePrint function with correct endpoint
  const handlePrint = (id, printType = 'basic') => {
    const baseUrl = window.location.origin;
    let url;
    if (printType === 'discount') {
      url = `${baseUrl}/delivery-orders-details/${id}`;
    } else {
      url = `${baseUrl}/delivery-orders/${id}`;
    }
    window.open(url, '_blank');
  };

  // Handle send delivery order via WhatsApp
  const handleWhatsAppSend = async (order) => {
    try {
      const authHeaders = { withCredentials: true, headers: { Authorization: `Bearer ${localStorage.getItem('token')}` } };
      const checkRes = await axios.get(
        `${process.env.REACT_APP_API_BASE_URL}/whatsapp-automations/check-automation/delivery-order-send`,
        authHeaders
      );
      if (!checkRes.data.data.isActive) {
        return Swal.fire('Not Enabled', 'Delivery order send via WhatsApp is not enabled. Please ask your Company Head to enable it in WhatsApp Automations.', 'warning');
      }

      const companyConnected = checkRes.data.data.isConnected;
      const personalConnected = checkRes.data.data.personalConnected;

      if (!companyConnected && !personalConnected) {
        return Swal.fire('WhatsApp Not Connected', 'Neither company nor your personal WhatsApp is connected. Connect your WhatsApp from your Profile page, or ask your Company Head to connect the company WhatsApp.', 'warning');
      }

      // Determine send source
      let sendFrom = 'company';
      if (companyConnected && personalConnected) {
        const { value: chosen } = await Swal.fire({
          title: 'Send from which WhatsApp?',
          input: 'radio',
          inputOptions: {
            personal: 'My WhatsApp',
            company: 'Company WhatsApp'
          },
          inputValue: 'personal',
          showCancelButton: true,
          confirmButtonText: 'Continue',
          confirmButtonColor: '#25D366',
          inputValidator: (value) => { if (!value) return 'Please select an option'; }
        });
        if (!chosen) return;
        sendFrom = chosen;
      } else if (personalConnected) {
        sendFrom = 'personal';
      } else {
        sendFrom = 'company';
      }

      const recipientName = order.recipient?.name || order.recipient?.businessName || 'Unknown';
      const recipientPhone = order.recipient?.phone || order.recipient?.phoneNumber || '';

      const { value: formValues } = await Swal.fire({
        title: 'Send Delivery Order via WhatsApp',
        html: `
          <div style="text-align: left; font-size: 14px;">
            <p style="margin-bottom: 12px;"><strong>Recipient:</strong> ${recipientName}</p>
            <p style="margin-bottom: 12px;"><strong>DO Number:</strong> ${order.doNumber || ''}</p>
            <p style="margin-bottom: 12px;"><strong>Amount:</strong> \u20B9${order.totalAmountWithGST?.toLocaleString('en-IN') || '0'}</p>
            <p style="margin-bottom: 12px;"><strong>Sending from:</strong> ${sendFrom === 'personal' ? 'My WhatsApp' : 'Company WhatsApp'}</p>
            <label style="display: block; margin-bottom: 4px; font-weight: 600;">Phone Number:</label>
            <input id="swal-phone" class="swal2-input" placeholder="Enter phone number" value="${recipientPhone}" style="margin: 0 0 8px 0; width: 100%; box-sizing: border-box;">
          </div>
        `,
        showCancelButton: true,
        confirmButtonText: 'Send via WhatsApp',
        confirmButtonColor: '#25D366',
        cancelButtonText: 'Cancel',
        preConfirm: () => {
          const phone = document.getElementById('swal-phone').value.trim();
          if (!phone) {
            Swal.showValidationMessage('Phone number is required');
            return false;
          }
          return { phone };
        }
      });

      if (!formValues) return;

      Swal.fire({
        title: 'Sending...',
        text: 'Generating PDF and queuing message...',
        allowOutsideClick: false,
        didOpen: () => Swal.showLoading()
      });

      const response = await axios.post(
        `${process.env.REACT_APP_API_BASE_URL}/whatsapp-automations/send-delivery-order`,
        {
          orderId: order._id,
          phone: formValues.phone,
          recipientName,
          recipientType: order.recipientType,
          recipientId: order.recipient?._id,
          sendFrom
        },
        authHeaders
      );

      if (response.data.success) {
        Swal.fire('Queued!', response.data.message, 'success');
      } else {
        throw new Error(response.data.message);
      }
    } catch (error) {
      console.error('WhatsApp send error:', error);
      Swal.fire('Error', error.response?.data?.message || error.message || 'Failed to send delivery order', 'error');
    }
  };

  // Print menu handlers
  const handlePrintMenuOpen = (event, orderId) => {
    const rect = event.currentTarget.getBoundingClientRect();
    setPrintMenuPosition({
      top: rect.bottom,
      left: rect.left
    });
    setShowPrintMenu(true);
    setSelectedPrintOrderId(orderId);
  };

  const handlePrintMenuClose = () => {
    setShowPrintMenu(false);
    setSelectedPrintOrderId(null);
  };

  const handlePrintOption = (printType) => {
    if (selectedPrintOrderId) {
      handlePrint(selectedPrintOrderId, printType);
    }
    handlePrintMenuClose();
  };

  // Add moveItem functions
  const moveItemUp = (index) => {
    if (index <= 0) return;
    
    setForm(prev => {
      const updatedItems = [...prev.items];
      const temp = updatedItems[index];
      updatedItems[index] = updatedItems[index - 1];
      updatedItems[index - 1] = temp;
      return {
        ...prev,
        items: updatedItems
      };
    });
  };

  const moveItemDown = (index) => {
    if (index >= form.items.length - 1) return;
    
    setForm(prev => {
      const updatedItems = [...prev.items];
      const temp = updatedItems[index];
      updatedItems[index] = updatedItems[index + 1];
      updatedItems[index + 1] = temp;
      return {
        ...prev,
        items: updatedItems
      };
    });
  };

  // Helper function to close items modal and reset all related states
  const closeItemsModal = useCallback(() => {
    setShowItemsModal(false);
    setCurrentItemIndex(null);
    setItemsSearch('');
  }, []);

  // Helper function to close proforma import modal and reset states
  const closeProformaImportModal = useCallback(() => {
    setShowProformaImportModal(false);
    setSelectedProformaItems([]);
    setProformaSearch('');
    setProformaPage(1);
  }, []);

  // Handle proforma item selection
  const handleProformaItemSelection = (item, isSelected) => {
    if (isSelected) {
      // Allow multiple selections of the same item by creating unique entries
      // Add a unique selection ID to distinguish multiple selections of the same item
      const itemWithSelectionId = {
        ...item,
        _selectionId: `${item._id}_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`
      };
      
      // Add the item to selection (no duplicate check, allowing multiple selections)
      setSelectedProformaItems(prev => [...prev, itemWithSelectionId]);
    } else {
      // Remove this specific selection from the list
      // Find the exact item in the list by matching both _id and custom properties
      setSelectedProformaItems(prev => {
        const itemIndex = prev.findIndex(selectedItem => 
          selectedItem._id === item._id && 
          selectedItem.customName === item.customName
        );
        
        if (itemIndex !== -1) {
          // Remove only the first matching item
          return prev.filter((_, index) => index !== itemIndex);
        }
        
        return prev;
      });
    }
    
    // Force refresh to ensure UI updates
    setForceRefresh(prev => prev + 1);
  };
  


  // Import selected proforma items
  const importProformaItems = useCallback(async () => {
    if (selectedProformaItems.length === 0) {
      Swal.fire({
        icon: 'warning',
        title: 'No Items Selected',
        text: 'Please select at least one item to import',
        confirmButtonColor: '#3085d6'
      });
      return;
    }

    try {
      const newItems = selectedProformaItems.map(item => ({
        itemId: item.itemId,
        Name: item.itemName,
        quantity: item.quantity,
        rate: item.offerPrice || item.salesRate || 0,
        description: item.customName || '', // Use custom name as description
        printType: 'both', // Set print type to 'both' for imported items
        units: item.unit || item.baseUnits || 'pcs',
        discount: 0,
        amountBeforeDiscount: 0,
        amountAfterDiscount: 0,
        integratedTax: item.integratedTax || 18,
        availableStock: 0,
        _HSNCode: 'N/A',
        tallyItem: null,
        tt: { type: 'percentage', value: 0, calculatedAmount: 0 },
        // Add proforma tracking metadata
        proformaInvoice: item.proformaId,
        proformaItemId: item._id
      }));

      setForm(prev => ({
        ...prev,
        items: [...prev.items, ...newItems]
      }));

      // Recalculate totals
      const updatedForm = { ...form, items: [...form.items, ...newItems] };
      const calculatedForm = await calculateTotals(updatedForm);
      setForm(calculatedForm);

      // Clear selected items and close modal
      setSelectedProformaItems([]);
      closeProformaImportModal();

      Swal.fire({
        icon: 'success',
        title: 'Items Imported',
        text: `Successfully imported ${selectedProformaItems.length} items from proforma invoice`,
        confirmButtonColor: '#3085d6'
      });
    } catch (error) {
      console.error('Error importing proforma items:', error);
      Swal.fire({
        icon: 'error',
        title: 'Import Failed',
        text: 'Failed to import items from proforma invoice',
        confirmButtonColor: '#3085d6'
      });
    }
  }, [selectedProformaItems, form, calculateTotals, closeProformaImportModal]);

  // Add handleItemSelection function with proper units handling
  const handleItemSelection = useCallback((item) => {
    // Default to 'pcs' for all items
    let defaultUnit = 'pcs';
    
    // ask for confirmation if the item is already selected where based on confirmation it will add the item again
    const isItemSelected = form.items.some(i => i.itemId === item._id);
    if (isItemSelected) {
      Swal.fire({
        icon: 'warning',
        title: 'Item Already Selected',
        text: 'This item is already in your order. Do you want to add it again?',
        confirmButtonColor: '#3085d6',
        confirmButtonText: 'Yes, Add Again',
        cancelButtonColor: '#d33',
        cancelButtonText: 'No, Cancel',
        showCancelButton: true,
      }).then((result) => {
        if (result.isConfirmed) {
          setForm(prev => ({
            ...prev,
            items: [...prev.items, {
              itemId: item._id,
              Name: item.Name,
              quantity: 0,
              rate: 0,
              description: '',
              printType: 'item', // Default print type
              units: defaultUnit, // Use automatically selected unit
              discount: 0,
              amountBeforeDiscount: 0,
              amountAfterDiscount: 0,
              availableStock: item.closingBalance || 0,
              _HSNCode: item._HSNCode || 'N/A',
              integratedTax: item.integratedTax || 18, // Include integrated tax
              tallyItem: item, // Store the full tally item data for unit options
              tt: {
                type: 'percentage',
                value: 0,
                calculatedAmount: 0
              }
            }]
          }));
          
          // Close modal and reset states
          setShowItemsModal(false);
          setCurrentItemIndex(null);
        }
      });
      return;
    }
    
    // If item is not already selected
    if (!item || !item._id) {
      console.error('Invalid item selected');
      return;
    }

    setForm(prev => {
      const updatedItems = [...prev.items];
      if (currentItemIndex === null) {
        // Add as a new item with automatically selected unit
        updatedItems.push({
          itemId: item._id,
          Name: item.Name,
          quantity: 0,
          rate: 0,
          description: '',
          printType: 'item', // Default print type
          units: defaultUnit, // Use automatically selected unit
          discount: 0,
          amountBeforeDiscount: 0,
          amountAfterDiscount: 0,
          availableStock: item.closingBalance || 0,
          _HSNCode: item._HSNCode || 'N/A',
          integratedTax: item.integratedTax || 18, // Include integrated tax
          tallyItem: item, // Store the full tally item data for unit options
          tt: {
            type: 'percentage',
            value: 0,
            calculatedAmount: 0
          }
        });
      } else {
        // Update existing item with automatically selected unit
        updatedItems[currentItemIndex] = {
          ...updatedItems[currentItemIndex],
          itemId: item._id,
          Name: item.Name,
          quantity: 0,
          rate: 0,
          description: '',
          printType: 'item', // Default print type
          units: defaultUnit, // Use automatically selected unit
          discount: 0,
          _HSNCode: item._HSNCode || 'N/A',
          availableStock: item.closingBalance || 0,
          integratedTax: item.integratedTax || 18, // Include integrated tax
          tallyItem: item, // Store the full tally item data for unit options
          tt: {
            type: 'percentage',
            value: 0,
            calculatedAmount: 0
          }
        };
      }
      return {
        ...prev,
        items: updatedItems
      };
    });
    
    // Close modal and reset states
    closeItemsModal();
    
    // Focus on quantity input field after item selection instead of units
    setTimeout(() => {
      const index = currentItemIndex !== null ? currentItemIndex : form.items.length;
      if (quantityInputRefs.current[index]) {
        quantityInputRefs.current[index].focus();
      }
    }, 100);
  }, [form.items.length, currentItemIndex, closeItemsModal, calculateTotals]);

  // Function to get unit options for a specific item
  const getUnitOptionsForItem = (item) => {
    // Always return all available unit options regardless of item
    return unitsOptions;
  };

  // Add refs to track user interaction
  const isUserTyping = useRef(false);
  const typingTimeout = useRef(null);

  // Helper function for form changes with typing protection
  const handleFormFieldChange = (field, value) => {
    // Set typing flag to prevent calculation interference
    isUserTyping.current = true;
    
    // Clear existing typing timeout
    if (typingTimeout.current) {
      clearTimeout(typingTimeout.current);
    }
    
    // Set timeout to clear typing flag after user stops typing
    typingTimeout.current = setTimeout(() => {
      isUserTyping.current = false;
    }, 1000); // 1 second after last keystroke

    setForm(prev => ({ ...prev, [field]: value }));
  };

  // Updated handleItemChange function to properly handle units
  const handleItemChange = (index, field, value) => {
    // Set typing flag to prevent calculation interference
    isUserTyping.current = true;
    
    // Clear existing typing timeout
    if (typingTimeout.current) {
      clearTimeout(typingTimeout.current);
    }
    
    // Set timeout to clear typing flag after user stops typing
    // Use longer timeout for TT fields to prevent input reversion
    const timeoutDuration = (field === 'tt') ? 2000 : 1000;
    typingTimeout.current = setTimeout(() => {
      isUserTyping.current = false;
    }, timeoutDuration);

    // Calculate TT for quantity, rate, discount, or TT field changes
    if (field === 'tt' || field === 'quantity' || field === 'rate' || field === 'discount'){
      const item = form.items[index];
      const rawIntegratedTax = item?.integratedTax || item?.tallyItem?.integratedTax;
      const integratedTax = parseFloat(rawIntegratedTax) || 18; // Try multiple sources for integrated tax and ensure float conversion
      const quantity = parseFloat(field === 'quantity' ? value : item?.quantity) || 0;
      const rate = parseFloat(field === 'rate' ? value : item?.rate) || 0;
      const discount = parseFloat(field === 'discount' ? value : item?.discount) || 0;
      const amountAfterDiscount = (quantity * rate) * (1 - (discount / 100)); // Calculate amount after discount
      const ttValue = field === 'tt' ? value : item?.tt;
      let ttCalculatedAmount = 0;
      
      console.log('handleItemChange TT Debug:', {
        index,
        field,
        rawIntegratedTax,
        parsedIntegratedTax: integratedTax,
        quantity,
        rate,
        discount,
        amountAfterDiscount,
        ttType: ttValue?.type,
        ttValue: ttValue?.value
      });
      
      if (ttValue && ttValue.value > 0 && amountAfterDiscount > 0) {
        // Calculate item value without GST using amount after discount and new formula
        // Formula: amountAfterDiscount * 100 / (100 + tax_rate)
        const itemValueWithoutGST = (amountAfterDiscount * 100) / (100 + integratedTax);
        
        console.log('handleItemChange TT Calculation:', {
          amountAfterDiscount,
          integratedTax,
          itemValueWithoutGST
        });
        
        if (ttValue.type === 'percentage') {
          // TTValueForPercentage = ItemValueWithOutGST * (TTPercentage/100)
          ttCalculatedAmount = itemValueWithoutGST * (ttValue.value / 100);
        }
        else if (ttValue.type === 'fixed') {
          // TTValueForAmount = TTFixedAmount (but not exceeding ItemValueWithOutGST)
          ttCalculatedAmount = Math.min(ttValue.value, itemValueWithoutGST);
        }
        
        // Ensure TT amount doesn't exceed item value without GST
        ttCalculatedAmount = Math.min(ttCalculatedAmount, itemValueWithoutGST);
        
        console.log('handleItemChange Final TT:', ttCalculatedAmount);
      }

      // Update TT calculatedAmount for the current item
      if (field === 'tt') {
        value.calculatedAmount = parseFloat(ttCalculatedAmount?.toFixed(2));
      }
    }

    setForm(prev => {
      const updatedItems = [...prev.items];
      
      if (field === 'units') {
        // For units field, normalize to lowercase and trim
        updatedItems[index] = {
          ...updatedItems[index],
          units: typeof value === 'string' ? value.toLowerCase().trim() : value
        };
      } else {
        updatedItems[index] = {
          ...updatedItems[index],
          [field]: value
        };
      }

      // Immediately recalculate amounts for fields that affect calculations
      if (field === 'quantity' || field === 'rate' || field === 'discount') {
        const item = updatedItems[index];
        const quantity = parseFloat(field === 'quantity' ? value : item.quantity) || 0;
        const rate = parseFloat(field === 'rate' ? value : item.rate) || 0;
        const discount = parseFloat(field === 'discount' ? value : item.discount) || 0;
        
        // Calculate amounts immediately
        const amountBeforeDiscount = quantity * rate;
        const discountAmount = amountBeforeDiscount * (discount / 100);
        const amountAfterDiscount = amountBeforeDiscount - discountAmount;
        
        // Also update TT calculatedAmount if there's TT applied
        let updatedTT = item.tt;
        if (item.tt && item.tt.value > 0) {
          const rawIntegratedTax = item?.integratedTax || item?.tallyItem?.integratedTax;
          const integratedTax = parseFloat(rawIntegratedTax) || 18;
          const amountAfterDiscount = (quantity * rate) * (1 - (discount / 100)); // Calculate amount after discount
          let ttCalculatedAmount = 0;
          
          if (amountAfterDiscount > 0) {
            const itemValueWithoutGST = (amountAfterDiscount * 100) / (100 + integratedTax);
            
            if (item.tt.type === 'percentage') {
              ttCalculatedAmount = itemValueWithoutGST * (item.tt.value / 100);
            } else if (item.tt.type === 'fixed') {
              ttCalculatedAmount = Math.min(item.tt.value, itemValueWithoutGST);
            }
            
            ttCalculatedAmount = Math.min(ttCalculatedAmount, itemValueWithoutGST);
          }
          
          updatedTT = {
            ...item.tt,
            calculatedAmount: parseFloat(ttCalculatedAmount?.toFixed(2))
          };
        }
        
        // Update the item with calculated amounts and TT
        updatedItems[index] = {
          ...updatedItems[index],
          amountBeforeDiscount: parseFloat(amountBeforeDiscount.toFixed(2)),
          amountAfterDiscount: parseFloat(amountAfterDiscount.toFixed(2)),
          tt: updatedTT
        };
      }
      
      const updatedForm = {
        ...prev,
        items: updatedItems
      };
      
      // Immediately calculate totals synchronously to ensure display is correct
      setTimeout(() => {
        const itemsTotal = updatedItems.reduce((sum, item) => {
          if (!item) return sum;
          const quantity = parseFloat(item.quantity) || 0;
          const rate = parseFloat(item.rate) || 0;
          const discount = parseFloat(item.discount) || 0;
          const amountBeforeDiscount = quantity * rate;
          const discountAmount = amountBeforeDiscount * (discount / 100);
          const amountAfterDiscount = amountBeforeDiscount - discountAmount;
          return sum + amountAfterDiscount;
        }, 0);
        
        const packingAndForwarding = parseFloat(updatedForm.packingAndForwarding) || 0;
        const labour = parseFloat(updatedForm.labour) || 0;
        const cartage = parseFloat(updatedForm.cartage) || 0;
        const roundoff = parseFloat(updatedForm.roundoff) || 0;
        
        const totalWithoutGST = itemsTotal + packingAndForwarding + labour + cartage;
        const gstOnCharges = (packingAndForwarding + labour + cartage) * 0.18;
        
        // Calculate TT total (for tracking only, not added to final amount)
        const totalTT = updatedItems.reduce((sum, item) => 
          sum + (parseFloat(item.tt?.calculatedAmount) || 0), 0);
        
        const finalAmount = totalWithoutGST + gstOnCharges + roundoff; // TT amount excluded from final total
        
        // Update form with calculated totals
        setForm(current => ({
          ...current,
          totalAmountWithoutGST: parseFloat(totalWithoutGST.toFixed(2)),
          totalAmountWithGST: parseFloat(finalAmount.toFixed(2))
        }));
        
        console.log('Synchronous Total Update:', {
          itemsTotal: itemsTotal.toFixed(2),
          totalWithoutGST: totalWithoutGST.toFixed(2),
          totalTT: totalTT.toFixed(2),
          finalAmount: finalAmount.toFixed(2)
        });
      }, 0);
      
      return updatedForm;
    });
  };

  // Open item modal function similar to QuotationNew
  const openItemModal = () => {
    setCurrentItemIndex(form.items.length);
    setShowItemsModal(true);
    setItemsCurrentPage(1);
    setItemsSearch('');
    // Initial fetch will be handled by useEffect
  };

  // Add item management functions
  const addItem = () => {
    openItemModal();
  };

  const removeItem = (index) => {
    setForm(prev => ({
      ...prev,
      items: prev.items.filter((_, i) => i !== index)
    }));
  }

  // Clipboard state for copy/paste items
  const [copiedItem, setCopiedItem] = useState(() => {
    try {
      const saved = localStorage.getItem('delivery_order_clipboard_item');
      return saved ? JSON.parse(saved) : null;
    } catch { return null; }
  });

  const copyItem = (index) => {
    const itemCopy = JSON.parse(JSON.stringify(form.items[index]));
    setCopiedItem(itemCopy);
    localStorage.setItem('delivery_order_clipboard_item', JSON.stringify(itemCopy));
    Swal.fire({ toast: true, position: 'top-end', icon: 'success', title: 'Item copied', showConfirmButton: false, timer: 1500 });
  };

  const pasteItem = () => {
    if (!copiedItem) {
      Swal.fire({ toast: true, position: 'top-end', icon: 'info', title: 'No item copied', showConfirmButton: false, timer: 1500 });
      return;
    }
    setForm(prev => ({
      ...prev,
      items: [...prev.items, JSON.parse(JSON.stringify(copiedItem))]
    }));
    Swal.fire({ toast: true, position: 'top-end', icon: 'success', title: 'Item pasted', showConfirmButton: false, timer: 1500 });
  };



  // Add handleEdit function
  const handleEdit = async (order) => {
    try {
      setLoading(true);
      setIsEditing(true);
      
      // Delete any existing draft when editing an order
      if (draftManager.currentDraftId) {
        try {
          await draftManager.deleteDraft(draftManager.currentDraftId);
          console.log('✅ Draft deleted when editing order');
        } catch (draftError) {
          console.error('⚠️ Failed to delete draft on edit:', draftError);
          // Continue anyway, draft deletion is not critical
        }
      }
      
      const response = await axios.get(
        `${process.env.REACT_APP_API_BASE_URL}/delivery-orders/details/${order._id}`
      );

      if (!response.data || !response.data.success || !response.data.deliveryOrder) {
        throw new Error('Invalid response data');
      }

      const orderDetails = response.data.deliveryOrder;

      // Create form data
      const formData = {
        ...initializeFormState(user?.user?._id), // Start with clean state
        ...orderDetails,
        type: orderDetails.typeOfDeliveryOrder,
        recipientType: orderDetails.recipientType, // Add this line
        recipient: orderDetails.recipient ? {
          value: orderDetails.recipient._id,
          label: orderDetails.recipientType === 'Customer' 
            ? orderDetails.recipient.name
            : orderDetails.recipient.businessName
        } : null,
        transporterName: orderDetails.transporterName ? orderDetails.transporterName._id : '',
        reference: orderDetails.reference ? orderDetails.reference._id : '',
        godown: orderDetails.godown ? orderDetails.godown._id : null, // Add godown mapping
        isreference: orderDetails.isreference || false, // Add explicit isreference handling
        isnarration: orderDetails.isnarration || false, // Add explicit isnarration handling
        claimPaymentFrom: {
          name: orderDetails.claimPaymentFrom?.name || '',
          number: orderDetails.claimPaymentFrom?.number || ''
        },
        salesman: orderDetails.salesman ? orderDetails.salesman.map(s => s._id) : [], // Map salesman IDs
        items: (orderDetails.items || []).map(item => ({
          itemId: item.itemId?._id || '',
          Name: item.itemId?.Name || '',
          quantity: parseFloat(item.quantity) || 0,
          rate: parseFloat(item.rate) || 0,
          description: item.description || '', // Include description field
          printType: item.printType || 'item', // Include printType field with default
          units: item.units || 'pcs', 
          discount: parseFloat(item.discount) || 0,
          amountBeforeDiscount: parseFloat(item.amountBeforeDiscount) || 0,
          amountAfterDiscount: parseFloat(item.amountAfterDiscount) || 0,
          availableStock: parseFloat(item.availableStock) || 0,
          _HSNCode: item.itemId?._HSNCode || 'N/A',
          tallyItem: item.itemId || null, // Store the full tally item data for unit options
          tt: item.tt || { type: 'percentage', value: 0, calculatedAmount: 0 } // Include TT details
        }))
      };

      setForm(formData);
      setOrderId(order._id);
      
      // Update payment modes input
      setPaymentModesInput(formData.modeOfPayment?.join(', ') || '');

      // Scroll to top
      window.scrollTo(0, 0);
    } catch (error) {
      console.error('Error fetching order details:', error);
      Swal.fire({
        icon: 'error',
        title: 'Error',
        text: error.response?.data?.message || error.message || 'Failed to fetch order details'
      });
    } finally {
      setLoading(false);
    }
  };

  // Add handleUpdateStatus function
  const handleUpdateStatus = async (orderId) => {
    // Show options as buttons instead of dropdown
    const { value: selectedStatus } = await Swal.fire({
      title: '<span style="font-size: 1.5rem; font-weight: 600;">Update Delivery Order Status</span>',
      html: `
        <p style="color: #6B7280; font-size: 0.875rem; margin-top: 0.5rem; margin-bottom: 1.5rem;">
          Select the new status for this delivery order
        </p>
        <div style="display: flex; flex-direction: column; gap: 12px;">
          <button 
            id="status-completed" 
            class="swal2-confirm swal2-styled" 
            style="background-color: #10B981; padding: 14px 24px; font-size: 1rem; border-radius: 8px; width: 100%;"
          >
            ✅ Mark as Completed
          </button>
          <button 
            id="status-cancelled" 
            class="swal2-deny swal2-styled" 
            style="background-color: #EF4444; padding: 14px 24px; font-size: 1rem; border-radius: 8px; width: 100%;"
          >
            ❌ Mark as Cancelled
          </button>
        </div>
      `,
      showConfirmButton: false,
      showCancelButton: true,
      cancelButtonText: 'Cancel',
      cancelButtonColor: isDarkMode ? '#6B7280' : '#9CA3AF',
      background: isDarkMode ? '#1F2937' : '#FFFFFF',
      color: isDarkMode ? '#F3F4F6' : '#111827',
      customClass: {
        popup: isDarkMode ? 'dark-mode-swal' : '',
        cancelButton: 'px-6 py-2.5 rounded-lg font-medium shadow-md hover:shadow-lg transition-all'
      },
      didOpen: () => {
        const popup = Swal.getPopup();
        if (isDarkMode && popup) {
          popup.style.borderRadius = '12px';
          popup.style.border = '1px solid #374151';
        }
        
        // Add click handlers to status buttons
        document.getElementById('status-completed').addEventListener('click', () => {
          Swal.clickConfirm();
          Swal.close();
          handleStatusSelection('Completed', orderId);
        });
        
        document.getElementById('status-cancelled').addEventListener('click', () => {
          Swal.clickDeny();
          Swal.close();
          handleStatusSelection('Cancelled', orderId);
        });
      }
    });
  };

  // Handle status selection
  const handleStatusSelection = async (status, orderId) => {
    try {
      let updateData = { status };
      
      // If status is Completed, ask for invoice number
      if (status === 'Completed') {
        const { value: invoiceNumber } = await Swal.fire({
          title: '<span style="font-size: 1.5rem; font-weight: 600;">Enter Invoice Number</span>',
          html: '<p style="color: #6B7280; font-size: 0.875rem; margin-top: 0.5rem;">Provide the invoice number for this completed order</p>',
          input: 'text',
          inputPlaceholder: 'e.g., INV-2025-001',
          showCancelButton: true,
          confirmButtonText: '<i class="fas fa-check"></i> Confirm',
          cancelButtonText: '<i class="fas fa-arrow-left"></i> Back',
          confirmButtonColor: isDarkMode ? '#10B981' : '#059669',
          cancelButtonColor: isDarkMode ? '#6B7280' : '#9CA3AF',
          background: isDarkMode ? '#1F2937' : '#FFFFFF',
          color: isDarkMode ? '#F3F4F6' : '#111827',
          inputValidator: (value) => {
            if (!value || !value.trim()) {
              return 'Invoice number is required';
            }
          },
          customClass: {
            popup: isDarkMode ? 'dark-mode-swal' : '',
            confirmButton: 'px-6 py-2.5 rounded-lg font-medium shadow-lg hover:shadow-xl transition-all',
            cancelButton: 'px-6 py-2.5 rounded-lg font-medium shadow-md hover:shadow-lg transition-all',
            input: `mt-4 px-4 py-3 rounded-lg font-medium text-base ${
              isDarkMode 
                ? 'bg-gray-700 border-gray-600 text-gray-100' 
                : 'bg-gray-50 border-gray-300 text-gray-900'
            }`
          },
          didOpen: () => {
            const popup = Swal.getPopup();
            if (isDarkMode && popup) {
              popup.style.borderRadius = '12px';
              popup.style.border = '1px solid #374151';
            }
          }
        });
        
        if (invoiceNumber) {
          updateData.invoiceNumber = invoiceNumber.trim();
        } else {
          return;
        }
      }
      
      // If status is Cancelled, ask for reason
      if (status === 'Cancelled') {
        const { value: reason } = await Swal.fire({
          title: '<span style="font-size: 1.5rem; font-weight: 600;">Reason for Cancellation</span>',
          html: '<p style="color: #6B7280; font-size: 0.875rem; margin-top: 0.5rem;">Please provide a reason for cancelling this order</p>',
          input: 'textarea',
          inputPlaceholder: 'e.g., Customer request, Stock unavailable, etc.',
          showCancelButton: true,
          confirmButtonText: '<i class="fas fa-check"></i> Confirm Cancellation',
          cancelButtonText: '<i class="fas fa-arrow-left"></i> Back',
          confirmButtonColor: isDarkMode ? '#DC2626' : '#EF4444',
          cancelButtonColor: isDarkMode ? '#6B7280' : '#9CA3AF',
          background: isDarkMode ? '#1F2937' : '#FFFFFF',
          color: isDarkMode ? '#F3F4F6' : '#111827',
          inputValidator: (value) => {
            if (!value || !value.trim()) {
              return 'Cancellation reason is required';
            }
          },
          customClass: {
            popup: isDarkMode ? 'dark-mode-swal' : '',
            confirmButton: 'px-6 py-2.5 rounded-lg font-medium shadow-lg hover:shadow-xl transition-all',
            cancelButton: 'px-6 py-2.5 rounded-lg font-medium shadow-md hover:shadow-lg transition-all',
            input: `mt-4 px-4 py-3 rounded-lg font-medium text-base min-h-[100px] ${
              isDarkMode 
                ? 'bg-gray-700 border-gray-600 text-gray-100' 
                : 'bg-gray-50 border-gray-300 text-gray-900'
            }`
          },
          didOpen: () => {
            const popup = Swal.getPopup();
            if (isDarkMode && popup) {
              popup.style.borderRadius = '12px';
              popup.style.border = '1px solid #374151';
            }
          }
        });
        
        if (reason) {
          updateData.reason = reason.trim();
        } else {
          return;
        }
      }

      const response = await axios.put(
        `${process.env.REACT_APP_API_BASE_URL}/delivery-orders/update-status/${orderId}`,
        updateData
      );

      if (response.data.success) {
        await Swal.fire({
          icon: 'success',
          title: '<span style="font-size: 1.5rem; font-weight: 600;">Status Updated!</span>',
          html: `<p style="color: #6B7280; font-size: 0.875rem; margin-top: 0.5rem;">The delivery order status has been successfully updated to <strong>${status}</strong></p>`,
          confirmButtonText: 'Great!',
          confirmButtonColor: isDarkMode ? '#10B981' : '#059669',
          background: isDarkMode ? '#1F2937' : '#FFFFFF',
          color: isDarkMode ? '#F3F4F6' : '#111827',
          customClass: {
            popup: isDarkMode ? 'dark-mode-swal' : '',
            confirmButton: 'px-6 py-2.5 rounded-lg font-medium shadow-lg hover:shadow-xl transition-all'
          },
          didOpen: () => {
            const popup = Swal.getPopup();
            if (isDarkMode && popup) {
              popup.style.borderRadius = '12px';
              popup.style.border = '1px solid #374151';
            }
          }
        });
        fetchOrders();
        fetchStats();
      } else {
        throw new Error(response.data.message || 'Failed to update status');
      }
    } catch (error) {
      console.error('Error updating status:', error);
      await Swal.fire({
        icon: 'error',
        title: '<span style="font-size: 1.5rem; font-weight: 600;">Update Failed</span>',
        html: `<p style="color: #6B7280; font-size: 0.875rem; margin-top: 0.5rem;">${error.response?.data?.message || error.message || 'Failed to update status'}</p>`,
        confirmButtonText: 'OK',
        confirmButtonColor: isDarkMode ? '#DC2626' : '#EF4444',
        background: isDarkMode ? '#1F2937' : '#FFFFFF',
        color: isDarkMode ? '#F3F4F6' : '#111827',
        customClass: {
          popup: isDarkMode ? 'dark-mode-swal' : '',
          confirmButton: 'px-6 py-2.5 rounded-lg font-medium shadow-lg hover:shadow-xl transition-all'
        },
        didOpen: () => {
          const popup = Swal.getPopup();
          if (isDarkMode && popup) {
            popup.style.borderRadius = '12px';
            popup.style.border = '1px solid #374151';
          }
        }
      });
    }
  };

  // Add payment modes handlers
  const handlePaymentModesChange = (selected) => {
    const paymentModes = selected ? selected.map(option => {
      if (typeof option === 'string') return option;
      if (option.value) return option.value;
      if (option.__isNew__ && option.label) return option.label;
      return option.toString();
    }) : [];
    
    setForm(prev => ({
      ...prev,
      modeOfPayment: paymentModes
    }));
    setPaymentModesInput(paymentModes.join(', '));
  };
  
  const handlePaymentModeInput = (e) => {
    const input = e.target.value;
    setPaymentModesInput(input);
    
    if (input.trim()) {
      const paymentModes = input.split(',').map(mode => mode.trim());
      setForm(prev => ({
        ...prev,
        modeOfPayment: paymentModes
      }));
    } else {
      setForm(prev => ({
        ...prev,
        modeOfPayment: []
      }));
    }
  };

  // Add recipient selection handler
  const handleRecipientSelect = (recipient, recipientType) => {
    // Get the appropriate name based on recipient type
    const recipientName = recipientType === 'Customer' ? recipient.name : recipient.businessName;
    
    // Determine the site address based on recipient type and available addresses
    let siteAddress = '';
    if (recipientType === 'Customer') {
      // For customers, prefer delivery address, fallback to site address
      siteAddress = recipient.deliveryAddress || recipient.siteAddress || '';
    } else {
      // For dealers, use the address field
      siteAddress = recipient.address || '';
    }
    
    setForm(prev => ({
      ...prev,
      recipientType: recipientType,
      recipient: {
        value: recipient._id,
        label: recipientType === 'Customer' 
          ? `${recipient.name} (Ph: ${recipient.phone}, ID: ${recipient.customerCode}, Station: ${recipient.station?.name})`
          : recipient.businessName
      },
      siteAddress: siteAddress
    }));
    
    setShowRecipientModal(false);
  };

  // Add handleKeyDown function after other handler functions
  const handleKeyDown = (event) => {
    // Allow only numbers, backspace, tab, decimal point, minus sign, and arrow keys
    if (
      !/[0-9]/.test(event.key) &&
      event.key !== 'Backspace' &&
      event.key !== 'Tab' &&
      event.key !== '.' &&
      event.key !== '-' &&
      event.key !== 'ArrowLeft' &&
      event.key !== 'ArrowRight'
    ) {
      event.preventDefault();
    }
  };

  // Global keyboard shortcuts handler
  const handleGlobalKeyDown = (event) => {
    // Only handle if not in an input field or if it's a global shortcut
    const isInputActive = event.target.tagName === 'INPUT' || event.target.tagName === 'TEXTAREA' || event.target.tagName === 'SELECT';
    
    // Global shortcuts (work even in input fields when Ctrl is pressed)
    if (event.ctrlKey) {
      switch (event.key) {
        case 'k':
          event.preventDefault();
          // Focus on quick search
          const quickSearchInput = document.querySelector('input[placeholder*="Quick search"]');
          if (quickSearchInput) {
            quickSearchInput.focus();
            quickSearchInput.select();
          }
          break;
        case 'f':
          event.preventDefault();
          setShowFilters(prev => !prev);
          break;
        case 'Enter':
          if (!isInputActive) {
            event.preventDefault();
            applyFilters();
          }
          break;
        default:
          break;
      }
      return;
    }

    // Only handle these shortcuts when not in input fields
    if (!isInputActive) {
      switch (event.key) {
        case '/':
          event.preventDefault();
          // Focus on quick search
          const quickSearchInput = document.querySelector('input[placeholder*="Quick search"]');
          if (quickSearchInput) {
            quickSearchInput.focus();
            quickSearchInput.select();
          }
          break;
        case 'Escape':
          event.preventDefault();
          if (showFilters) {
            setShowFilters(false);
          } else if (quickSearch) {
            clearSearch(false);
          } else {
            resetFilters();
          }
          break;
        default:
          break;
      }
    }
  };

  // Update useEffect for focusing quantity input (but not when modal is open)
  useEffect(() => {
    if (lastAddedIndex !== null && quantityRefs.current[lastAddedIndex] && !showItemsModal) {
      quantityRefs.current[lastAddedIndex].focus();
      quantityRefs.current[lastAddedIndex].select();
      setLastAddedIndex(null);
    }
  }, [lastAddedIndex, form.items, showItemsModal]);

  // Update handleChargeChange function
  const handleChargeChange = (field, value) => {
    // Set typing flag to prevent calculation interference
    isUserTyping.current = true;
    
    // Clear existing typing timeout
    if (typingTimeout.current) {
      clearTimeout(typingTimeout.current);
    }
    
    // Set timeout to clear typing flag after user stops typing
    typingTimeout.current = setTimeout(() => {
      isUserTyping.current = false;
    }, 1000); // 1 second after last keystroke

    const numValue = parseFloat(value) || 0;
    setForm(prev => {
      const updatedForm = {
        ...prev,
        [field]: numValue
      };
      
      // Get dynamic tax rate for charges
      const chargesTaxRate = getChargesTaxRate(prev.items) / 100; // Convert to decimal
      
      // Use fixed 18% GST rate for additional charges
      const additionalChargesGSTRate = 0.18;
      
      // Immediately calculate GST for the updated charge using fixed 18% rate
      if (field === 'packingAndForwarding') {
        updatedForm.packingAndForwardingGST = numValue * additionalChargesGSTRate;
      } else if (field === 'labour') {
        updatedForm.labourGST = numValue * additionalChargesGSTRate;
      } else if (field === 'cartage') {
        updatedForm.cartageGST = numValue * additionalChargesGSTRate;
      }
      
      return updatedForm;
    });
  };

  // Add function to fetch detailed item information
  const fetchItemDetails = useCallback(async (itemId) => {
    try {
      // Add this item to the loading set
      setLoadingItemDetails(prev => new Set([...prev, itemId]));
      
      const response = await axios.get(
        `${process.env.REACT_APP_API_BASE_URL}/delivery-orders/tally-item-details/${itemId}`
      );
      
      if (response.data.success) {
        setSelectedItemForDetails(response.data.data);
        setShowItemDetailsModal(true);
      } else {
        Swal.fire({
          icon: 'error',
          title: 'Error',
          text: 'Failed to fetch item details',
          confirmButtonColor: '#3085d6'
        });
      }
    } catch (error) {
      console.error('Error fetching item details:', error);
      Swal.fire({
        icon: 'error',
        title: 'Error',
        text: error.response?.data?.message || 'Failed to fetch item details',
        confirmButtonColor: '#3085d6'
      });
    } finally {
      // Remove this item from the loading set
      setLoadingItemDetails(prev => {
        const newSet = new Set(prev);
        newSet.delete(itemId);
        return newSet;
      });
    }
  }, []);

  // Add handleSort function before return statement
  const handleSort = (field) => {
    setSortBy(field);
    setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc');
    setPage(1); // Reset to first page when sorting changes
  };
  // Reset form function
  const resetForm = () => {
    setForm(initializeFormState(user?.user?._id));
    setIsEditing(false);
    setPaymentModesInput('');
  };

  // Type of Delivery Order field
  const handleTypeChange = async (newType) => {
    if (isEditing) {
      return; // Prevent type change when editing
    }
      setForm(prev => ({
        ...prev,
        type: newType
      }));
  };

  // Focus is now handled automatically by ItemSearchBar component with autoFocus

  // Global keyboard shortcuts effect
  useEffect(() => {
    document.addEventListener('keydown', handleGlobalKeyDown);
    return () => {
      document.removeEventListener('keydown', handleGlobalKeyDown);
    };
  }, [showFilters, quickSearch]);

  // Add effect to focus first item button when recipient tab is clicked
  useEffect(() => {
    if (form.recipient && firstItemButtonRef.current && !showItemsModal) {
      firstItemButtonRef.current.focus();
    }
  }, [form.recipient, showItemsModal]);

  // Add effect to focus quantity input after item selection
  useEffect(() => {
    if (currentItemIndex !== null && quantityInputRefs.current[currentItemIndex] && !showItemsModal) {
      quantityInputRefs.current[currentItemIndex].focus();
      
      // for adding item, set values of quantity and rate to 0 if not already set
      if (form.items[currentItemIndex] && form.items[currentItemIndex].quantity === '') {
        setForm(prev => {
          const updatedItems = [...prev.items];
          updatedItems[currentItemIndex].quantity = 0;
          return {
            ...prev,
            items: updatedItems
          };
        });
      }
    }
  }, [currentItemIndex, showItemsModal]);

  // Close print menu when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (printMenuRef.current && !printMenuRef.current.contains(event.target)) {
        setShowPrintMenu(false);
      }
    };

    if (showPrintMenu) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => document.removeEventListener('mousedown', handleClickOutside);
    }
  }, [showPrintMenu]);

  // Ensure credit payment mode when mode of sales is Credit
  useEffect(() => {
    if (form.modeOfSales === 'Credit' && form.modeOfPayment.length > 0) {
      const hasCredit = form.modeOfPayment.some(mode => mode.toLowerCase() === 'credit');
      if (!hasCredit) {
        const updatedPaymentModes = [...form.modeOfPayment, 'credit'];
        setForm(prev => ({
          ...prev,
          modeOfPayment: updatedPaymentModes
        }));
        setPaymentModesInput(updatedPaymentModes.join(', '));
      }
    }
  }, [form.modeOfSales, form.modeOfPayment]);

  // Create stable callback functions to prevent unnecessary re-renders in ItemsModal
  const stableCloseItemsModal = useCallback(closeItemsModal, []);
  const stableHandleItemSelection = useCallback(handleItemSelection, [form.items.length, currentItemIndex, calculateTotals]);
  const stableFetchItemDetails = useCallback(fetchItemDetails, []);

  const ProformaImportModal = () => (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-60 backdrop-blur-sm p-2 sm:p-4">
      <div className={`rounded-2xl w-full max-w-6xl max-h-[95vh] sm:max-h-[90vh] flex flex-col shadow-2xl border ${
        isDarkMode ? 'bg-gray-800 border-gray-600' : 'bg-white border-gray-200'
      }`}>
        {/* Responsive Header */}
        <div className="flex-shrink-0 bg-gradient-to-r from-purple-600 to-indigo-600 text-white rounded-t-2xl p-3 sm:p-4 md:p-6">
          <div className="flex justify-between items-start sm:items-center mb-3 sm:mb-4">
            <div className="flex items-start sm:items-center space-x-2 sm:space-x-3 flex-1 min-w-0">
              <div className="bg-white bg-opacity-20 rounded-full p-1.5 sm:p-2 flex-shrink-0">
                <svg className="w-4 h-4 sm:w-5 md:w-6 sm:h-5 md:h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M9 19l3 3m0 0l3-3m-3 3V10" />
                </svg>
              </div>
              <div className="min-w-0 flex-1">
                <h2 className="text-base sm:text-xl md:text-2xl font-bold truncate">Import from Proforma Invoice</h2>
                <p className="text-blue-100 text-xs sm:text-sm hidden sm:block">Select items from unused proforma invoices</p>
              </div>
            </div>
            <button
              onClick={closeProformaImportModal}
              className="text-white hover:text-red-200 transition-colors duration-200 p-1.5 sm:p-2 hover:bg-white hover:bg-opacity-10 rounded-full flex-shrink-0 ml-2"
            >
              <svg className="w-5 h-5 sm:w-6 sm:h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>

          {/* Search Bar */}
          <div className="relative">
            <input
              type="text"
              placeholder="Search proforma invoices..."
              value={proformaSearch}
              onChange={(e) => {
                setProformaSearch(e.target.value);
                setProformaPage(1);
              }}
              className="w-full pl-8 sm:pl-10 pr-3 sm:pr-4 py-2 sm:py-3 bg-white bg-opacity-20 border border-white border-opacity-30 rounded-lg text-white placeholder-blue-100 focus:outline-none focus:ring-2 focus:ring-white focus:ring-opacity-50 text-sm sm:text-base"
            />
            <div className="absolute inset-y-0 left-0 pl-2 sm:pl-3 flex items-center pointer-events-none">
              <svg className="h-4 w-4 sm:h-5 sm:w-5 text-blue-100" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
              </svg>
            </div>
          </div>
        </div>

        {/* Content */}
        <div className="flex-grow flex flex-col min-h-0 p-3 sm:p-4 md:p-6">
          {proformaLoading ? (
            <div className="flex-grow flex items-center justify-center">
              <div className="text-center">
                <div className="inline-block animate-spin rounded-full h-10 w-10 sm:h-12 sm:w-12 border-b-2 border-purple-600"></div>
                <p className={`mt-4 text-sm sm:text-base ${isDarkMode ? 'text-gray-400' : 'text-gray-600'}`}>
                  Loading proforma invoices...
                </p>
              </div>
            </div>
          ) : proformaInvoices.length === 0 ? (
            <div className="flex-grow flex items-center justify-center">
              <div className={`text-center px-4 ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                <svg className={`w-12 h-12 sm:w-16 sm:h-16 mx-auto mb-4 ${isDarkMode ? 'text-gray-600' : 'text-gray-300'}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                </svg>
                <p className="text-base sm:text-lg font-medium">No unused proforma invoices found</p>
                <p className="text-xs sm:text-sm">Try adjusting your search terms</p>
              </div>
            </div>
          ) : (
            <div className="flex-grow space-y-3 sm:space-y-4 overflow-y-auto">
              {proformaInvoices.map((invoice) => (
                <div key={invoice._id} className={`border rounded-lg p-3 sm:p-4 hover:shadow-md transition-shadow ${
                  isDarkMode ? 'border-gray-700 bg-gray-750' : 'border-gray-200 bg-white'
                }`}>
                  {/* Responsive Invoice Header - Mobile */}
                  <div className="md:hidden mb-3">
                    <h3 className={`font-semibold text-base ${isDarkMode ? 'text-gray-100' : 'text-gray-900'}`}>
                      {invoice.invoiceNumber}
                    </h3>
                    <div className={`mt-1 space-y-1 text-xs ${isDarkMode ? 'text-gray-400' : 'text-gray-600'}`}>
                      <p>📅 {formatDate(invoice.invoiceDate)}</p>
                      <p>💰 ₹{formatNumberWithCommas(invoice.totalAmount)}</p>
                      <p className="flex items-center">
                        <span className="mr-2">Status:</span>
                        <span className={`inline-block px-2 py-0.5 rounded text-xs font-medium ${
                          invoice.status === 'PENDING' ? 'bg-yellow-100 text-yellow-800' :
                          invoice.status === 'PARTIAL' ? 'bg-blue-100 text-blue-800' :
                          'bg-green-100 text-green-800'
                        }`}>
                          {invoice.status}
                        </span>
                      </p>
                      {invoice.createdBy && (
                        <p className={`text-xs ${isDarkMode ? 'text-gray-500' : 'text-gray-500'}`}>
                          👤 {invoice.createdBy.name}
                        </p>
                      )}
                    </div>
                  </div>

                  {/* Desktop Invoice Header */}
                  <div className="hidden md:flex justify-between items-start mb-3">
                    <div>
                      <h3 className={`font-semibold text-lg ${isDarkMode ? 'text-gray-100' : 'text-gray-900'}`}>
                        {invoice.invoiceNumber}
                      </h3>
                      <p className={`text-sm ${isDarkMode ? 'text-gray-400' : 'text-gray-600'}`}>
                        Date: {formatDate(invoice.invoiceDate)} | 
                        Amount: ₹{formatNumberWithCommas(invoice.totalAmount)} | 
                        Status: <span className={`inline-block px-2 py-1 rounded text-xs font-medium ${
                          invoice.status === 'PENDING' ? 'bg-yellow-100 text-yellow-800' :
                          invoice.status === 'PARTIAL' ? 'bg-blue-100 text-blue-800' :
                          'bg-green-100 text-green-800'
                        }`}>
                          {invoice.status}
                        </span>
                      </p>
                      {invoice.createdBy && (
                        <p className={`text-xs ${isDarkMode ? 'text-gray-500' : 'text-gray-500'}`}>
                          Created by: {invoice.createdBy.name}
                        </p>
                      )}
                    </div>
                  </div>

                  {/* Items List */}
                  <div className="space-y-2">
                    <h4 className={`font-medium text-xs sm:text-sm ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                      Available Items ({(invoice.items || []).length}):
                    </h4>
                    {(invoice.items || []).map((item) => {
                      // Count how many times this item has been selected
                      const selectionCount = selectedProformaItems.filter(selected => 
                        selected._id === item._id && selected.customName === item.customName
                      ).length;
                      const isSelected = selectionCount > 0;
                      const checkboxChecked = isSelected;
                      

                      
                      // Create a unique key that changes when selection state changes
                      const componentKey = `${item._id}-${isSelected}-${selectedProformaItems.length}-${forceRefresh}`;
                      

                      
                      return (
                        <div key={componentKey} className={`rounded border ${
                          isDarkMode ? 'bg-gray-700 border-gray-600' : 'bg-gray-50 border-gray-200'
                        }`}>
                          {/* Mobile Item Layout */}
                          <div className="md:hidden p-2 space-y-2">
                            <div className="flex items-start space-x-2">
                              <input
                                type="checkbox"
                                checked={checkboxChecked}
                                onChange={(e) => handleProformaItemSelection(item, e.target.checked)}
                                className="w-4 h-4 mt-0.5 text-purple-600 rounded focus:ring-purple-500 flex-shrink-0"
                              />
                              <div className="flex-1 min-w-0">
                                <p className={`font-medium text-sm ${isDarkMode ? 'text-gray-200' : 'text-gray-800'} truncate`}>
                                  {item.itemName}
                                </p>
                                {selectionCount > 1 && (
                                  <span className="inline-block mt-1 text-xs bg-blue-100 text-blue-800 px-2 py-0.5 rounded">
                                    Selected {selectionCount}x
                                  </span>
                                )}
                              </div>
                            </div>
                            
                            <div className="pl-6 space-y-1">
                              {item.printName && item.printName !== item.itemName && (
                                <p className={`text-xs font-medium ${isDarkMode ? 'text-blue-400' : 'text-blue-700'}`}>
                                  📝 {item.printName}
                                </p>
                              )}
                              {item.customName && (
                                <p className={`text-xs ${isDarkMode ? 'text-green-400' : 'text-green-700'}`}>
                                  ✏️ {item.customName}
                                </p>
                              )}
                              {item.brandName && (
                                <p className={`text-xs ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                                  🏷️ {item.brandName}
                                </p>
                              )}
                              
                              <div className="grid grid-cols-2 gap-1 text-xs">
                                <p className={isDarkMode ? 'text-gray-400' : 'text-gray-600'}>
                                  Qty: {item.quantity} {item.unit}
                                </p>
                                <p className={isDarkMode ? 'text-gray-400' : 'text-gray-600'}>
                                  Rate: ₹{formatNumberWithCommas(item.offerPrice)}
                                </p>
                                <p className={isDarkMode ? 'text-gray-400' : 'text-gray-600'}>
                                  Amt: ₹{formatNumberWithCommas(item.amount)}
                                </p>
                                <div className="flex justify-end">
                                  <span className={`inline-block px-1.5 py-0.5 rounded text-xs font-medium ${
                                    item.stockStatus === 'to-be-reserved' ? 'bg-orange-100 text-orange-800' :
                                    item.stockStatus === 'to-be-ordered' ? 'bg-red-100 text-red-800' :
                                    item.stockStatus === 'to-be-billed' ? 'bg-blue-100 text-blue-800' :
                                    'bg-green-100 text-green-800'
                                  }`}>
                                    {item.stockStatus.replace(/-/g, ' ').replace('to be', '').trim().toUpperCase()}
                                  </span>
                                </div>
                              </div>
                            </div>
                          </div>

                          {/* Desktop Item Layout */}
                          <div className="hidden md:flex items-center justify-between p-2">
                            <div className="flex items-center space-x-3">
                              <input
                                type="checkbox"
                                checked={checkboxChecked}
                                onChange={(e) => handleProformaItemSelection(item, e.target.checked)}
                                className="w-4 h-4 text-purple-600 rounded focus:ring-purple-500"
                              />
                              <div>
                                <div className="mb-1">
                                  <p className={`font-medium text-sm ${isDarkMode ? 'text-gray-200' : 'text-gray-800'}`}>
                                    {item.itemName}
                                    {selectionCount > 1 && (
                                      <span className="ml-2 text-xs bg-blue-100 text-blue-800 px-2 py-1 rounded">
                                        Selected {selectionCount}x
                                      </span>
                                    )}
                                  </p>
                                  {item.printName && item.printName !== item.itemName && (
                                    <p className={`text-sm font-medium ${isDarkMode ? 'text-blue-400' : 'text-blue-700'}`}>
                                      📝 {item.printName}
                                    </p>
                                  )}
                                  {item.customName && (
                                    <p className={`text-sm ${isDarkMode ? 'text-green-400' : 'text-green-700'}`}>
                                      ✏️ Custom: {item.customName}
                                    </p>
                                  )}
                                </div>
                                {item.brandName && (
                                  <p className={`text-xs mb-1 ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                                    🏷️ Brand: {item.brandName}
                                  </p>
                                )}
                                <p className={`text-xs ${isDarkMode ? 'text-gray-400' : 'text-gray-600'}`}>
                                  Qty: {item.quantity} {item.unit} | 
                                  Rate: ₹{formatNumberWithCommas(item.offerPrice)} | 
                                  Amount: ₹{formatNumberWithCommas(item.amount)}
                                </p>
                              </div>
                            </div>
                            <div className="text-right">
                              <span className={`inline-block px-2 py-1 rounded text-xs font-medium ${
                                item.stockStatus === 'to-be-reserved' ? 'bg-orange-100 text-orange-800' :
                                item.stockStatus === 'to-be-ordered' ? 'bg-red-100 text-red-800' :
                                item.stockStatus === 'to-be-billed' ? 'bg-blue-100 text-blue-800' :
                                'bg-green-100 text-green-800'
                              }`}>
                                {item.stockStatus.replace(/-/g, ' ').toUpperCase()}
                              </span>
                            </div>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              ))}
            </div>
          )}

          {/* Responsive Pagination */}
          {proformaTotalPages > 1 && (
            <div className={`flex flex-col sm:flex-row justify-between items-center gap-2 sm:gap-0 mt-4 pt-4 border-t ${
              isDarkMode ? 'border-gray-700' : 'border-gray-200'
            }`}>
              <button
                onClick={() => setProformaPage(prev => Math.max(1, prev - 1))}
                disabled={proformaPage === 1}
                className={`w-full sm:w-auto px-4 py-2 text-sm rounded transition-colors disabled:opacity-50 disabled:cursor-not-allowed ${
                  isDarkMode 
                    ? 'bg-gray-700 text-gray-200 hover:bg-gray-600' 
                    : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                }`}
              >
                ← Previous
              </button>
              <span className={`text-xs sm:text-sm ${isDarkMode ? 'text-gray-400' : 'text-gray-600'}`}>
                Page {proformaPage} of {proformaTotalPages}
              </span>
              <button
                onClick={() => setProformaPage(prev => Math.min(proformaTotalPages, prev + 1))}
                disabled={proformaPage === proformaTotalPages}
                className={`w-full sm:w-auto px-4 py-2 text-sm rounded transition-colors disabled:opacity-50 disabled:cursor-not-allowed ${
                  isDarkMode 
                    ? 'bg-gray-700 text-gray-200 hover:bg-gray-600' 
                    : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                }`}
              >
                Next →
              </button>
            </div>
          )}
          
          {/* Selected Items Summary */}
          {selectedProformaItems.length > 0 && (
            <div className={`mt-4 sm:mt-6 p-3 sm:p-4 rounded-lg border ${
              isDarkMode 
                ? 'bg-blue-900/30 border-blue-700' 
                : 'bg-blue-50 border-blue-200'
            }`}>
              <h4 className={`font-medium text-sm sm:text-base mb-2 ${isDarkMode ? 'text-blue-300' : 'text-blue-800'}`}>
                Selected Items ({selectedProformaItems.length})
              </h4>
              <div className="space-y-1 max-h-32 overflow-y-auto">
                {selectedProformaItems.map((selectedItem, index) => (
                  <div key={selectedItem._selectionId || `${selectedItem._id}_${index}`} className="flex justify-between items-center text-xs sm:text-sm gap-2">
                    <span className={`truncate flex-1 ${isDarkMode ? 'text-blue-400' : 'text-blue-700'}`}>
                      {selectedItem.customName || selectedItem.printName || selectedItem.itemName}
                    </span>
                    <button
                      onClick={() => handleProformaItemSelection(selectedItem, false)}
                      className={`text-xs whitespace-nowrap flex-shrink-0 ${isDarkMode ? 'text-red-400 hover:text-red-300' : 'text-red-500 hover:text-red-700'}`}
                    >
                      Remove
                    </button>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>

        {/* Responsive Footer */}
        <div className={`flex-shrink-0 p-3 sm:p-4 md:p-6 rounded-b-2xl border-t ${
          isDarkMode 
            ? 'bg-gray-750 border-gray-700' 
            : 'bg-gray-50 border-gray-200'
        }`}>
          {/* Mobile Footer Layout */}
          <div className="md:hidden space-y-3">
            <div className={`text-xs sm:text-sm ${isDarkMode ? 'text-gray-400' : 'text-gray-600'}`}>
              <div className="font-medium">{selectedProformaItems.length} item(s) selected</div>
              {selectedProformaItems.length > 0 && (
                <div className={`mt-1 text-xs line-clamp-2 ${isDarkMode ? 'text-blue-400' : 'text-blue-600'}`}>
                  {selectedProformaItems.map(item => item.customName || item.itemName).join(', ')}
                </div>
              )}
            </div>
            {selectedProformaItems.length > 0 && (
              <div className="flex flex-wrap gap-2 text-xs">
                <button
                  onClick={() => setSelectedProformaItems([])}
                  className={`hover:underline transition-colors duration-200 ${
                    isDarkMode ? 'text-red-400 hover:text-red-300' : 'text-red-600 hover:text-red-800'
                  }`}
                >
                  Clear All
                </button>
                <span className={isDarkMode ? 'text-gray-600' : 'text-gray-400'}>•</span>
                <button
                  onClick={() => {
                    setSelectedProformaItems([]);
                    setForceRefresh(prev => prev + 1);
                    fetchProformaInvoices(proformaSearch, proformaPage);
                  }}
                  className={`hover:underline transition-colors duration-200 ${
                    isDarkMode ? 'text-blue-400 hover:text-blue-300' : 'text-blue-600 hover:text-blue-800'
                  }`}
                >
                  Reset & Refresh
                </button>
              </div>
            )}
            <div className="flex flex-col gap-2">
              <button
                onClick={importProformaItems}
                disabled={selectedProformaItems.length === 0}
                className="w-full px-4 py-2.5 bg-purple-600 text-white rounded-lg hover:bg-purple-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors duration-200 flex items-center justify-center space-x-2 text-sm font-medium"
              >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M9 19l3 3m0 0l3-3m-3 3V10" />
                </svg>
                <span>Import Selected Items</span>
              </button>
              <button
                onClick={closeProformaImportModal}
                className={`w-full px-4 py-2 rounded-lg transition-colors duration-200 text-sm ${
                  isDarkMode 
                    ? 'bg-gray-700 text-gray-200 hover:bg-gray-600' 
                    : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
                }`}
              >
                Cancel
              </button>
            </div>
          </div>

          {/* Desktop Footer Layout */}
          <div className="hidden md:flex justify-between items-center">
            <div className="flex items-center space-x-4">
              <div className={`text-sm ${isDarkMode ? 'text-gray-400' : 'text-gray-600'}`}>
                {selectedProformaItems.length} item(s) selected
                {selectedProformaItems.length > 0 && (
                  <span className={`ml-2 text-xs ${isDarkMode ? 'text-blue-400' : 'text-blue-600'}`}>
                    ({selectedProformaItems.map(item => item.customName || item.itemName).join(', ')})
                  </span>
                )}
              </div>
              {selectedProformaItems.length > 0 && (
                <div className="flex space-x-2">
                  <button
                    onClick={() => setSelectedProformaItems([])}
                    className={`text-xs hover:underline transition-colors duration-200 ${
                      isDarkMode ? 'text-red-400 hover:text-red-300' : 'text-red-600 hover:text-red-800'
                    }`}
                  >
                    Clear All
                  </button>
                  <button
                    onClick={() => {
                      setSelectedProformaItems([]);
                      setForceRefresh(prev => prev + 1);
                      fetchProformaInvoices(proformaSearch, proformaPage);
                    }}
                    className={`text-xs hover:underline transition-colors duration-200 ${
                      isDarkMode ? 'text-blue-400 hover:text-blue-300' : 'text-blue-600 hover:text-blue-800'
                    }`}
                  >
                    Reset & Refresh
                  </button>
                </div>
              )}
            </div>
            <div className="flex space-x-3">
              <button
                onClick={closeProformaImportModal}
                className={`px-6 py-2 rounded-lg transition-colors duration-200 ${
                  isDarkMode 
                    ? 'bg-gray-700 text-gray-200 hover:bg-gray-600' 
                    : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
                }`}
              >
                Cancel
              </button>
              <button
                onClick={importProformaItems}
                disabled={selectedProformaItems.length === 0}
                className="px-6 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors duration-200 flex items-center space-x-2"
              >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M9 19l3 3m0 0l3-3m-3 3V10" />
                </svg>
                <span>Import Selected Items</span>
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );

  const ItemsModal = () => (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-60 backdrop-blur-sm">
      <div className={`rounded-2xl w-full max-w-6xl max-h-[90vh] m-4 flex flex-col shadow-2xl border ${
        isDarkMode ? 'bg-gray-800 border-gray-600' : 'bg-white border-gray-200'
      }`}>
        {/* Enhanced Header with Gradient */}
        <div className="flex-shrink-0 bg-gradient-to-r from-blue-600 to-indigo-600 text-white rounded-t-2xl p-6">
          <div className="flex justify-between items-center mb-4">
            <div className="flex items-center space-x-3">
              <div className="bg-white bg-opacity-20 rounded-full p-2">
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M20 13V6a2 2 0 00-2-2H6a2 2 0 00-2 2v7m16 0v5a2 2 0 01-2 2H6a2 2 0 01-2-2v-5m16 0h-2.586a1 1 0 00-.707.293l-2.414 2.414a1 1 0 01-.707.293h-3.172a1 1 0 01-.707-.293l-2.414-2.414A1 1 0 006.586 13H4" />
                </svg>
              </div>
              <div>
                <h3 className="text-xl font-bold">Select Items</h3>
                <p className="text-blue-100 text-sm">
                  {itemsSearch ? (
                    <>
                      Searching "{itemsSearch}" • {count || 0} results found
                    </>
                  ) : (
                    <>Choose from {count || 0} available items</>
                  )}
                </p>
              </div>
            </div>
            <button
              onClick={stableCloseItemsModal}
              className="bg-white bg-opacity-20 hover:bg-opacity-30 rounded-full p-2 transition-all duration-200"
            >
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>

          {/* Search Bar with Enhanced Styling */}
          <div className="relative bg-white bg-opacity-20 rounded-lg p-1">
            <div className="relative">
              <input
                type="text"
                value={itemsSearch}
                onChange={(e) => handleItemSearchChange(e.target.value)}
                onKeyDown={(e) => handleSearchKeyDown(e, true)}
                placeholder="🔍 Search items by name, HSN code, or description..."
                className="w-full pl-12 pr-12 py-3 bg-white bg-opacity-90 border-0 rounded-lg focus:ring-2 focus:ring-white focus:bg-white transition-all duration-200 text-gray-900 placeholder-gray-600"
                autoComplete="off"
                autoFocus
              />
              <svg className="absolute left-4 top-4 w-5 h-5 text-gray-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
              </svg>
              {itemsSearch && !itemsModalLoading && (
                <button
                  onClick={() => handleItemSearchClear()}
                  className="absolute right-4 top-3 p-1 text-gray-500 hover:text-gray-700 rounded-full hover:bg-white hover:bg-opacity-50 transition-all duration-200"
                >
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              )}
              {itemsModalLoading && (
                <div className="absolute right-4 top-4">
                  <div className="animate-spin rounded-full h-5 w-5 border-2 border-blue-500 border-t-transparent"></div>
                </div>
              )}
            </div>
            <div className="mt-2 text-xs text-white text-opacity-80 px-1">
              💡 Type to search automatically (300ms delay) or press Enter for instant search
            </div>
          </div>
        </div>

        {/* Filters and Controls - Compact Design */}
        <div className={`flex-shrink-0 border-b p-4 ${
          isDarkMode ? 'bg-gray-750 border-gray-700' : 'bg-gray-50 border-gray-200'
        }`}>
          <div className="flex flex-col lg:flex-row gap-4 justify-between items-start lg:items-center">
            {/* Left Side - Filters */}
            <div className="flex flex-wrap items-center gap-3">
              <div className="flex items-center space-x-2">
                <label className={`text-sm font-medium ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                  Show:
                </label>
                <Select
                  value={{ value: itemsPerPage, label: `${itemsPerPage}` }}
                  onChange={(selectedOption) => setItemsPerPage(selectedOption.value)}
                  options={[
                    { value: 5, label: '5' },
                    { value: 10, label: '10' },
                    { value: 20, label: '20' },
                    { value: 50, label: '50' }
                  ]}
                  className="text-sm min-w-[80px]"
                  isSearchable={false}
                  styles={getSelectStyles(isDarkMode)}
                />
              </div>

              <div className={`h-6 w-px ${isDarkMode ? 'bg-gray-600' : 'bg-gray-300'}`}></div>

              <div className="flex items-center space-x-2">
                <label className={`text-sm font-medium ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                  Group:
                </label>
                <Select
                  value={stockGroups.find(group => group.value === itemFilters.stockGroup) || { value: '', label: 'All' }}
                  onChange={(selectedOption) => setItemFilters(prev => ({ 
                    ...prev, 
                    stockGroup: selectedOption ? selectedOption.value : '' 
                  }))}
                  options={[
                    { value: '', label: 'All Groups' },
                    ...stockGroups
                  ]}
                  className="text-sm min-w-[150px]"
                  isSearchable={true}
                  isClearable={true}
                  placeholder="Select group..."
                  menuPortalTarget={document.body}
                  styles={getSelectStyles(isDarkMode)}
                />
              </div>

              <div className="flex items-center space-x-2">
                <label className={`text-sm font-medium ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                  Sort:
                </label>
                <Select
                  value={{ value: itemFilters.sortBy, label: 
                    itemFilters.sortBy === 'name' ? 'Name' : 
                    itemFilters.sortBy === 'stock' ? 'Stock' : 'Recent' 
                  }}
                  onChange={(selectedOption) => setItemFilters(prev => ({ 
                    ...prev, 
                    sortBy: selectedOption.value 
                  }))}
                  options={[
                    { value: 'name', label: 'Name' },
                    { value: 'stock', label: 'Stock' },
                    { value: 'recent', label: 'Recent' }
                  ]}
                  className="text-sm min-w-[100px]"
                  isSearchable={false}
                  menuPortalTarget={document.body}
                  styles={getSelectStyles(isDarkMode)}
                />
              </div>
            </div>

            {/* Right Side - Quick Actions */}
            <div className="flex items-center gap-2">
              <button
                onClick={() => setItemFilters(prev => ({ ...prev, stockGroup: 'unassigned' }))}
                className={`px-3 py-1.5 text-xs rounded-full transition-colors flex items-center space-x-1 ${
                  isDarkMode 
                    ? 'bg-amber-900/30 text-amber-300 hover:bg-amber-900/50' 
                    : 'bg-amber-100 text-amber-700 hover:bg-amber-200'
                }`}
                title="Show items with no stock group assigned"
              >
                <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.996-.833-2.464 0L3.34 16.5c-.77.833.192 2.5 1.732 2.5z" />
                </svg>
                <span>Unassigned</span>
              </button>
              <button
                onClick={() => {
                  setItemFilters({
                    stockGroup: '',
                    stockStatus: '',
                    sortBy: 'name'
                  });
                  clearSearch(true);
                }}
                className={`px-3 py-1.5 text-xs rounded-full transition-colors flex items-center space-x-1 ${
                  isDarkMode 
                    ? 'bg-gray-700 text-gray-300 hover:bg-gray-600' 
                    : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                }`}
              >
                <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                </svg>
                <span>Reset</span>
              </button>
            </div>
          </div>
        </div>

        {/* Enhanced Content Section */}
        <div className={`flex-1 overflow-y-auto ${isDarkMode ? 'bg-gray-900' : 'bg-gray-50'}`}>
          {/* Search Status and Tips */}
          {!itemsModalLoading && (
            <div className="mx-6 mt-4 mb-4">
              {itemsSearch ? (
                /* Active Search Status */
                <div className={`p-4 rounded-xl shadow-sm border ${
                  isDarkMode 
                    ? 'bg-green-900/30 border-green-700' 
                    : 'bg-gradient-to-r from-green-50 to-emerald-50 border-green-200'
                }`}>
                  <div className="flex items-start space-x-3">
                    <div className={`rounded-full p-2 flex-shrink-0 ${
                      isDarkMode ? 'bg-green-900/50' : 'bg-green-100'
                    }`}>
                      <svg className={`w-4 h-4 ${isDarkMode ? 'text-green-400' : 'text-green-600'}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                      </svg>
                    </div>
                    <div className="flex-1">
                      <h4 className={`text-sm font-semibold mb-2 ${isDarkMode ? 'text-green-300' : 'text-green-800'}`}>
                        🔍 Searching for: <span className={`font-mono px-2 py-1 rounded ${
                          isDarkMode ? 'bg-gray-800 text-green-300' : 'bg-white text-green-900'
                        }`}>"{itemsSearch}"</span>
                      </h4>
                      <div className={`text-xs flex flex-wrap gap-4 ${isDarkMode ? 'text-green-300' : 'text-green-700'}`}>
                        <span>• {count || 0} results found</span>
                        <span>• {itemFilters.stockGroup ? `Filtered by: ${itemFilters.stockGroup}` : 'All categories'}</span>
                        <span>• Sorted by: {itemFilters.sortBy === 'name' ? 'Name' : itemFilters.sortBy === 'stock' ? 'Stock Level' : 'Recently Added'}</span>
                      </div>
                    </div>
                  </div>
                </div>
              ) : (
                /* Search Tips */
                <div className={`p-4 rounded-xl shadow-sm border ${
                  isDarkMode 
                    ? 'bg-blue-900/30 border-blue-700' 
                    : 'bg-gradient-to-r from-blue-50 to-indigo-50 border-blue-200'
                }`}>
                  <div className="flex items-start space-x-3">
                    <div className={`rounded-full p-2 flex-shrink-0 ${
                      isDarkMode ? 'bg-blue-900/50' : 'bg-blue-100'
                    }`}>
                      <svg className={`w-4 h-4 ${isDarkMode ? 'text-blue-400' : 'text-blue-600'}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                      </svg>
                    </div>
                    <div className="flex-1">
                      <h4 className={`text-sm font-semibold mb-2 ${isDarkMode ? 'text-blue-300' : 'text-blue-800'}`}>
                        💡 Quick Search Tips
                      </h4>
                      <div className={`text-xs grid grid-cols-1 md:grid-cols-2 gap-1 ${isDarkMode ? 'text-blue-300' : 'text-blue-700'}`}>
                        <p>• Search by name, HSN, or description</p>
                        <p>• Auto-search with 300ms delay</p>
                        <p>• Use stock group filters</p>
                        <p>• Focus preserved during loading</p>
                      </div>
                    </div>
                  </div>
                </div>
              )}
            </div>
          )}

          <div className="relative min-h-[300px] px-6">
            {itemsModalLoading ? (
              <div className={`absolute inset-0 flex flex-col items-center justify-center rounded-xl ${
                isDarkMode ? 'bg-gray-900 bg-opacity-90' : 'bg-white bg-opacity-90'
              }`}>
                <div className={`animate-spin rounded-full h-12 w-12 border-4 border-t-transparent mb-4 ${
                  isDarkMode ? 'border-blue-400' : 'border-blue-500'
                }`}></div>
                <p className={`font-medium ${isDarkMode ? 'text-gray-300' : 'text-gray-600'}`}>
                  {itemsSearch ? `Searching for "${itemsSearch}"...` : 'Loading items...'}
                </p>
                <p className={`text-sm ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                  {itemsSearch ? 'Filtering inventory based on your search term' : 'Please wait while we fetch the inventory'}
                </p>
              </div>
            ) : (
              <div className="pb-4">
                {itemsList.length > 0 ? (
                  <div className="grid gap-4">
                    {itemsList.map((item, index) => (
                      <div key={item._id} className={`border rounded-xl p-3 sm:p-5 transition-all duration-300 group ${
                        isDarkMode 
                          ? 'bg-gray-750 border-gray-700 hover:border-blue-500 hover:shadow-lg hover:shadow-blue-500/20' 
                          : 'bg-white border-gray-200 hover:border-blue-300 hover:shadow-lg'
                      }`}>
                        {/* Mobile Layout */}
                        <div className="md:hidden space-y-3">
                          {/* Item Name and Status */}
                          <div className="flex items-start justify-between gap-2">
                            <h3 className={`font-semibold text-base flex-1 transition-colors ${
                              isDarkMode 
                                ? 'text-gray-100 group-hover:text-blue-400' 
                                : 'text-gray-900 group-hover:text-blue-600'
                            }`}>
                              {item.Name}
                            </h3>
                            <div className="flex flex-wrap gap-1 justify-end">
                              {item.closingBalance > 0 ? (
                                <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${
                                  isDarkMode 
                                    ? 'bg-green-900/40 text-green-300' 
                                    : 'bg-green-100 text-green-800'
                                }`}>
                                  In Stock
                                </span>
                              ) : (
                                <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${
                                  isDarkMode 
                                    ? 'bg-red-900/40 text-red-300' 
                                    : 'bg-red-100 text-red-800'
                                }`}>
                                  Out
                                </span>
                              )}
                              {form.items.some(i => i.itemId === item._id) && (
                                <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${
                                  isDarkMode 
                                    ? 'bg-blue-900/40 text-blue-300' 
                                    : 'bg-blue-100 text-blue-800'
                                }`}>
                                  Added
                                </span>
                              )}
                            </div>
                          </div>

                          {/* Item Details Grid - Mobile */}
                          <div className="grid grid-cols-2 gap-3">
                            <div className="flex items-center space-x-2">
                              <div className={`p-1.5 rounded-lg flex-shrink-0 ${
                                isDarkMode ? 'bg-green-900/40' : 'bg-green-100'
                              }`}>
                                <svg className={`w-3 h-3 ${isDarkMode ? 'text-green-400' : 'text-green-600'}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M20 7l-8-4-8 4m16 0l-8 4m8-4v10l-8 4m0-10L4 7m8 4v10M4 7v10l8 4" />
                                </svg>
                              </div>
                              <div className="min-w-0 flex-1">
                                <p className={`text-xs ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>Stock</p>
                                <p className={`font-semibold text-sm ${isDarkMode ? 'text-gray-100' : 'text-gray-900'}`}>{item.closingBalance || 0}</p>
                              </div>
                            </div>

                            <div className="flex items-center space-x-2">
                              <div className={`p-1.5 rounded-lg flex-shrink-0 ${
                                isDarkMode ? 'bg-orange-900/40' : 'bg-orange-100'
                              }`}>
                                <svg className={`w-3 h-3 ${isDarkMode ? 'text-orange-400' : 'text-orange-600'}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 6l3 1m0 0l-3 9a5.002 5.002 0 006.001 0M6 7l3 9M6 7l6-2m6 2l3-1m-3 1l-3 9a5.002 5.002 0 006.001 0M18 7l3 9m-3-9l-6-2m0-2v2m0 16V5m0 16H9m3 0h3" />
                                </svg>
                              </div>
                              <div className="min-w-0 flex-1">
                                <p className={`text-xs ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>Units</p>
                                <p className={`font-semibold text-xs truncate ${isDarkMode ? 'text-gray-100' : 'text-gray-900'}`}>
                                  {item.baseUnits || 'N/A'}
                                  {item.additionalUnits && !item.additionalUnits.toLowerCase().includes('not applicable') && (
                                    <span className={`text-xs ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                                      /{item.additionalUnits}
                                    </span>
                                  )}
                                </p>
                              </div>
                            </div>

                            <div className="flex items-center space-x-2">
                              <div className={`p-1.5 rounded-lg flex-shrink-0 ${
                                isDarkMode ? 'bg-blue-900/40' : 'bg-blue-100'
                              }`}>
                                <svg className={`w-3 h-3 ${isDarkMode ? 'text-blue-400' : 'text-blue-600'}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 7h.01M7 3h5c.512 0 1.024.195 1.414.586l7 7a2 2 0 010 2.828l-7 7a2 2 0 01-2.828 0l-7-7A1.994 1.994 0 013 12V7a4 4 0 014-4z" />
                                </svg>
                              </div>
                              <div className="min-w-0 flex-1">
                                <p className={`text-xs ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>HSN</p>
                                <p className={`font-semibold text-sm truncate ${isDarkMode ? 'text-gray-100' : 'text-gray-900'}`}>{item._HSNCode || 'N/A'}</p>
                              </div>
                            </div>

                            <div className="flex items-center space-x-2">
                              <div className={`p-1.5 rounded-lg flex-shrink-0 ${
                                isDarkMode ? 'bg-purple-900/40' : 'bg-purple-100'
                              }`}>
                                <svg className={`w-3 h-3 ${isDarkMode ? 'text-purple-400' : 'text-purple-600'}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 11H5m14 0a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2v-6a2 2 0 012-2m14 0V9a2 2 0 00-2-2M5 11V9a2 2 0 012-2m0 0V5a2 2 0 012-2h6a2 2 0 012 2v2M7 7h10" />
                                </svg>
                              </div>
                              <div className="min-w-0 flex-1">
                                <p className={`text-xs ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>Category</p>
                                <p className={`font-semibold text-xs truncate ${isDarkMode ? 'text-gray-100' : 'text-gray-900'}`}>{item.stockGroup || 'Unassigned'}</p>
                              </div>
                            </div>
                          </div>

                          {/* Action Buttons - Mobile */}
                          <div className="flex gap-2">
                            <button
                              onClick={() => stableFetchItemDetails(item._id)}
                              disabled={loadingItemDetails.has(item._id)}
                              className="flex-1 px-3 py-2 bg-emerald-500 disabled:bg-gray-400 text-white rounded-lg hover:bg-emerald-600 text-xs sm:text-sm transition-all duration-200 flex items-center justify-center space-x-1.5 shadow-sm"
                            >
                              {loadingItemDetails.has(item._id) ? (
                                <div className="animate-spin rounded-full h-3 w-3 border-2 border-white border-t-transparent"></div>
                              ) : (
                                <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                                </svg>
                              )}
                              <span>{loadingItemDetails.has(item._id) ? 'Loading' : 'Details'}</span>
                            </button>
                            <button
                              disabled={form.items.some(i => i.itemId === item._id) && currentItemIndex !== form.items.length}
                              onClick={() => {
                                setCurrentItemIndex(currentItemIndex);
                                stableHandleItemSelection(item);
                              }}
                              className={`flex-1 px-3 py-2 rounded-lg text-xs sm:text-sm transition-all duration-200 flex items-center justify-center space-x-1.5 shadow-sm ${
                                isDarkMode
                                  ? 'bg-blue-600 hover:bg-blue-500 text-white disabled:bg-gray-700 disabled:text-gray-500'
                                  : 'bg-blue-500 hover:bg-blue-600 text-white disabled:bg-gray-400'
                              }`}
                            >
                              <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
                              </svg>
                              <span>
                                {form.items.some(i => i.itemId === item._id) ? 'Add Again' : 'Select'}
                              </span>
                            </button>
                          </div>
                        </div>

                        {/* Desktop Layout */}
                        <div className="hidden md:block">
                        <div className="flex justify-between items-start">
                          <div className="flex-1 space-y-3">
                            {/* Item Name and Status */}
                            <div className="flex items-center justify-between">
                              <h3 className={`font-semibold text-lg transition-colors ${
                                isDarkMode 
                                  ? 'text-gray-100 group-hover:text-blue-400' 
                                  : 'text-gray-900 group-hover:text-blue-600'
                              }`}>
                                {item.Name}
                              </h3>
                              <div className="flex items-center space-x-2">
                                {item.closingBalance > 0 ? (
                                  <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                                    isDarkMode 
                                      ? 'bg-green-900/40 text-green-300' 
                                      : 'bg-green-100 text-green-800'
                                  }`}>
                                    In Stock
                                  </span>
                                ) : (
                                  <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                                    isDarkMode 
                                      ? 'bg-red-900/40 text-red-300' 
                                      : 'bg-red-100 text-red-800'
                                  }`}>
                                    Out of Stock
                                  </span>
                                )}
                                {form.items.some(i => i.itemId === item._id) && (
                                  <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                                    isDarkMode 
                                      ? 'bg-blue-900/40 text-blue-300' 
                                      : 'bg-blue-100 text-blue-800'
                                  }`}>
                                    Added
                                  </span>
                                )}
                              </div>
                            </div>

                            {/* Item Details Grid */}
                            <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
                              <div className="flex items-center space-x-2">
                                <div className={`p-1.5 rounded-lg ${
                                  isDarkMode ? 'bg-green-900/40' : 'bg-green-100'
                                }`}>
                                  <svg className={`w-4 h-4 ${isDarkMode ? 'text-green-400' : 'text-green-600'}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M20 7l-8-4-8 4m16 0l-8 4m8-4v10l-8 4m0-10L4 7m8 4v10M4 7v10l8 4" />
                                  </svg>
                                </div>
                                <div>
                                  <p className={`text-xs ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>Stock</p>
                                  <p className={`font-semibold ${isDarkMode ? 'text-gray-100' : 'text-gray-900'}`}>{item.closingBalance || 0}</p>
                                </div>
                              </div>

                              <div className="flex items-center space-x-2">
                                <div className={`p-1.5 rounded-lg ${
                                  isDarkMode ? 'bg-orange-900/40' : 'bg-orange-100'
                                }`}>
                                  <svg className={`w-4 h-4 ${isDarkMode ? 'text-orange-400' : 'text-orange-600'}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 6l3 1m0 0l-3 9a5.002 5.002 0 006.001 0M6 7l3 9M6 7l6-2m6 2l3-1m-3 1l-3 9a5.002 5.002 0 006.001 0M18 7l3 9m-3-9l-6-2m0-2v2m0 16V5m0 16H9m3 0h3" />
                                  </svg>
                                </div>
                                <div>
                                  <p className={`text-xs ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>Units</p>
                                  <p className={`font-semibold text-xs ${isDarkMode ? 'text-gray-100' : 'text-gray-900'}`}>
                                    {item.baseUnits ? (
                                      <span className="truncate block">{item.baseUnits}</span>
                                    ) : (
                                      <span className={isDarkMode ? 'text-gray-500' : 'text-gray-400'}>N/A</span>
                                    )}
                                    {item.additionalUnits && !item.additionalUnits.toLowerCase().includes('not applicable') && (
                                      <span className={`text-xs ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                                        / {item.additionalUnits}
                                      </span>
                                    )}
                                  </p>
                                </div>
                              </div>

                              <div className="flex items-center space-x-2">
                                <div className={`p-1.5 rounded-lg ${
                                  isDarkMode ? 'bg-blue-900/40' : 'bg-blue-100'
                                }`}>
                                  <svg className={`w-4 h-4 ${isDarkMode ? 'text-blue-400' : 'text-blue-600'}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 7h.01M7 3h5c.512 0 1.024.195 1.414.586l7 7a2 2 0 010 2.828l-7 7a2 2 0 01-2.828 0l-7-7A1.994 1.994 0 013 12V7a4 4 0 014-4z" />
                                  </svg>
                                </div>
                                <div>
                                  <p className={`text-xs ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>HSN Code</p>
                                  <p className={`font-semibold ${isDarkMode ? 'text-gray-100' : 'text-gray-900'}`}>{item._HSNCode || 'N/A'}</p>
                                </div>
                              </div>

                              <div className="flex items-center space-x-2">
                                <div className={`p-1.5 rounded-lg ${
                                  isDarkMode ? 'bg-purple-900/40' : 'bg-purple-100'
                                }`}>
                                  <svg className={`w-4 h-4 ${isDarkMode ? 'text-purple-400' : 'text-purple-600'}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 11H5m14 0a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2v-6a2 2 0 012-2m14 0V9a2 2 0 00-2-2M5 11V9a2 2 0 012-2m0 0V5a2 2 0 012-2h6a2 2 0 012 2v2M7 7h10" />
                                  </svg>
                                </div>
                                <div>
                                  <p className={`text-xs ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>Category</p>
                                  <p className={`font-semibold ${isDarkMode ? 'text-gray-100' : 'text-gray-900'}`}>{item.stockGroup || 'Unassigned'}</p>
                                </div>
                              </div>

                              <div className="flex items-center space-x-2">
                                <div className={`p-1.5 rounded-lg ${
                                  isDarkMode ? 'bg-gray-700' : 'bg-gray-100'
                                }`}>
                                  <svg className={`w-4 h-4 ${isDarkMode ? 'text-gray-400' : 'text-gray-600'}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                                  </svg>
                                </div>
                                <div>
                                  <p className={`text-xs ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>Updated</p>
                                  <p className={`font-semibold ${isDarkMode ? 'text-gray-100' : 'text-gray-900'}`}>{formatDate(item.updatedAt)}</p>
                                </div>
                              </div>
                            </div>
                          </div>

                          {/* Action Buttons - Desktop */}
                          <div className="flex flex-col space-y-2 ml-6">
                            <button
                              onClick={() => stableFetchItemDetails(item._id)}
                              disabled={loadingItemDetails.has(item._id)}
                              className="px-4 py-2 bg-emerald-500 disabled:bg-gray-400 text-white rounded-lg hover:bg-emerald-600 text-sm transition-all duration-200 flex items-center space-x-2 shadow-sm hover:shadow-md"
                            >
                              {loadingItemDetails.has(item._id) ? (
                                <div className="animate-spin rounded-full h-4 w-4 border-2 border-white border-t-transparent"></div>
                              ) : (
                                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                                </svg>
                              )}
                              <span>{loadingItemDetails.has(item._id) ? 'Loading...' : 'Details'}</span>
                            </button>
                            <button
                              disabled={form.items.some(i => i.itemId === item._id) && currentItemIndex !== form.items.length}
                              onClick={() => {
                                setCurrentItemIndex(currentItemIndex);
                                stableHandleItemSelection(item);
                              }}
                              className={`px-4 py-2 rounded-lg transition-all duration-200 flex items-center space-x-2 shadow-sm hover:shadow-md ${
                                isDarkMode
                                  ? 'bg-blue-600 hover:bg-blue-500 text-white disabled:bg-gray-700 disabled:text-gray-500'
                                  : 'bg-blue-500 hover:bg-blue-600 text-white disabled:bg-gray-400'
                              }`}
                            >
                              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
                              </svg>
                              <span>
                                {form.items.some(i => i.itemId === item._id) ? 'Add Again' : 'Select Item'}
                              </span>
                            </button>
                          </div>
                        </div>
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="text-center py-16">
                    <div className={`mx-auto w-32 h-32 rounded-full flex items-center justify-center mb-6 shadow-inner ${
                      isDarkMode 
                        ? 'bg-gradient-to-br from-gray-800 to-gray-700' 
                        : 'bg-gradient-to-br from-gray-100 to-gray-200'
                    }`}>
                      {itemsSearch ? (
                        <svg className={`w-16 h-16 ${isDarkMode ? 'text-gray-500' : 'text-gray-400'}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                        </svg>
                      ) : (
                        <svg className={`w-16 h-16 ${isDarkMode ? 'text-gray-500' : 'text-gray-400'}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M20 13V6a2 2 0 00-2-2H6a2 2 0 00-2 2v7m16 0v5a2 2 0 01-2 2H6a2 2 0 01-2-2v-5m16 0h-2.586a1 1 0 00-.707.293l-2.414 2.414a1 1 0 01-.707.293h-3.172a1 1 0 01-.707-.293l-2.414-2.414A1 1 0 006.586 13H4" />
                        </svg>
                      )}
                    </div>
                    <h3 className={`text-xl font-semibold mb-3 ${isDarkMode ? 'text-gray-100' : 'text-gray-900'}`}>
                      {itemsSearch ? 'No search results' : 'No items found'}
                    </h3>
                    <div className="mb-6 max-w-lg mx-auto">
                      {itemsSearch ? (
                        <div className="space-y-3">
                          <div className={`border rounded-lg p-4 ${
                            isDarkMode 
                              ? 'bg-yellow-900/30 border-yellow-700' 
                              : 'bg-yellow-50 border-yellow-200'
                          }`}>
                            <div className="flex items-center space-x-2 mb-2">
                              <svg className={`w-5 h-5 ${isDarkMode ? 'text-yellow-400' : 'text-yellow-600'}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                              </svg>
                              <span className={`font-medium ${isDarkMode ? 'text-yellow-300' : 'text-yellow-800'}`}>
                                Search Term:
                              </span>
                              <span className={`font-mono px-2 py-1 rounded ${
                                isDarkMode ? 'bg-yellow-900/50 text-yellow-200' : 'bg-yellow-100 text-yellow-900'
                              }`}>
                                "{itemsSearch}"
                              </span>
                            </div>
                            <div className={`text-sm space-y-1 ${isDarkMode ? 'text-yellow-300' : 'text-yellow-700'}`}>
                              <p>• No items match this search in the current inventory</p>
                              {itemFilters.stockGroup && (
                                <p>• Filtered by stock group: <span className="font-medium">{itemFilters.stockGroup}</span></p>
                              )}
                              <p>• Sorted by: <span className="font-medium">
                                {itemFilters.sortBy === 'name' ? 'Name' : 
                                 itemFilters.sortBy === 'stock' ? 'Stock Level' : 'Recently Added'}
                              </span></p>
                            </div>
                          </div>
                          <p className={isDarkMode ? 'text-gray-400' : 'text-gray-500'}>
                            Try different search terms, remove filters, or check spelling.
                          </p>
                        </div>
                      ) : (
                        <p className={isDarkMode ? 'text-gray-400' : 'text-gray-500'}>
                          No items are available in the inventory. Please add items to continue.
                        </p>
                      )}
                    </div>
                    {itemsSearch ? (
                      <div className="flex flex-col sm:flex-row gap-3 justify-center">
                        <button
                          onClick={() => {
                            setItemsSearch('');
                            setItemsCurrentPage(1);
                          }}
                          className={`px-6 py-3 rounded-lg transition-all duration-200 flex items-center justify-center space-x-2 shadow-sm hover:shadow-md ${
                            isDarkMode 
                              ? 'bg-blue-600 hover:bg-blue-500 text-white' 
                              : 'bg-blue-500 hover:bg-blue-600 text-white'
                          }`}
                        >
                          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                          </svg>
                          <span>Clear Search & Show All</span>
                        </button>
                        <button
                          onClick={stableCloseItemsModal}
                          className={`px-6 py-3 rounded-lg transition-all duration-200 flex items-center justify-center space-x-2 shadow-sm hover:shadow-md ${
                            isDarkMode 
                              ? 'bg-gray-700 hover:bg-gray-600 text-white' 
                              : 'bg-gray-500 hover:bg-gray-600 text-white'
                          }`}
                        >
                          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                          </svg>
                          <span>Close Modal</span>
                        </button>
                      </div>
                    ) : (
                      <button
                        onClick={stableCloseItemsModal}
                        className={`px-6 py-3 rounded-lg transition-all duration-200 flex items-center justify-center space-x-2 mx-auto shadow-sm hover:shadow-md ${
                          isDarkMode 
                            ? 'bg-gray-700 hover:bg-gray-600 text-white' 
                            : 'bg-gray-500 hover:bg-gray-600 text-white'
                        }`}
                      >
                        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                        </svg>
                        <span>Close Modal</span>
                      </button>
                    )}
                  </div>
                )}
              </div>
            )}          
          </div>

          {/* Enhanced Pagination Footer */}
          {!itemsModalLoading && itemsList.length > 0 && (
            <div className={`border-t p-6 rounded-b-2xl ${
              isDarkMode ? 'bg-gray-750 border-gray-700' : 'bg-white border-gray-200'
            }`}>
              <div className="flex flex-col lg:flex-row justify-between items-center gap-4">
                {/* Results Summary */}
                <div className="flex items-center space-x-3">
                  <div className={`p-2 rounded-lg ${
                    isDarkMode ? 'bg-blue-900/40' : 'bg-blue-100'
                  }`}>
                    <svg className={`w-5 h-5 ${isDarkMode ? 'text-blue-400' : 'text-blue-600'}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" />
                    </svg>
                  </div>
                  <div>
                    <p className={`text-sm font-medium ${isDarkMode ? 'text-gray-100' : 'text-gray-900'}`}>
                      Showing {((itemsCurrentPage - 1) * itemsPerPage) + 1} to {Math.min(itemsCurrentPage * itemsPerPage, count)} of {count} items
                    </p>
                    <p className={`text-xs ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                      {itemsSearch ? (
                        <span className="flex items-center space-x-1">
                          <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                          </svg>
                          <span>Search: "{itemsSearch}"</span>
                          {itemFilters.stockGroup && (
                            <>
                              <span>•</span>
                              <span>Group: {itemFilters.stockGroup}</span>
                            </>
                          )}
                        </span>
                      ) : (
                        'All inventory items'
                      )}
                    </p>
                  </div>
                </div>

                {/* Pagination Controls */}
                <div className="flex items-center space-x-2">
                  <button
                    onClick={() => setItemsCurrentPage(1)}
                    disabled={itemsCurrentPage === 1}
                    className={`px-3 py-2 text-sm border rounded-lg disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-200 flex items-center space-x-1 ${
                      isDarkMode 
                        ? 'border-gray-600 hover:bg-gray-700 text-gray-300' 
                        : 'border-gray-300 hover:bg-gray-50 text-gray-700'
                    }`}
                  >
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 19l-7-7 7-7m8 14l-7-7 7-7" />
                    </svg>
                    <span className="hidden sm:inline">First</span>
                  </button>
                  
                  <button
                    onClick={() => setItemsCurrentPage(p => Math.max(1, p - 1))}
                    disabled={itemsCurrentPage === 1}
                    className={`px-4 py-2 text-sm border rounded-lg disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-200 flex items-center space-x-2 ${
                      isDarkMode 
                        ? 'border-gray-600 hover:bg-gray-700 text-gray-300' 
                        : 'border-gray-300 hover:bg-gray-50 text-gray-700'
                    }`}
                  >
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                    </svg>
                    <span>Previous</span>
                  </button>

                  <div className="flex items-center space-x-1">
                    {/* Page Numbers */}
                    {(() => {
                      const pages = [];
                      const showPages = 5;
                      let start = Math.max(1, itemsCurrentPage - Math.floor(showPages / 2));
                      let end = Math.min(totalPages, start + showPages - 1);
                      
                      if (end - start < showPages - 1) {
                        start = Math.max(1, end - showPages + 1);
                      }

                      for (let i = start; i <= end; i++) {
                        pages.push(
                          <button
                            key={i}
                            onClick={() => setItemsCurrentPage(i)}
                            className={`px-3 py-2 text-sm rounded-lg transition-all duration-200 ${
                              i === itemsCurrentPage
                                ? isDarkMode 
                                  ? 'bg-blue-600 text-white shadow-sm' 
                                  : 'bg-blue-500 text-white shadow-sm'
                                : isDarkMode 
                                  ? 'border border-gray-600 hover:bg-gray-700 text-gray-300' 
                                  : 'border border-gray-300 hover:bg-gray-50 text-gray-700'
                            }`}
                          >
                            {i}
                          </button>
                        );
                      }
                      return pages;
                    })()}
                  </div>

                  <button
                    onClick={() => setItemsCurrentPage(p => p + 1)}
                    disabled={itemsCurrentPage >= totalPages}
                    className={`px-4 py-2 text-sm border rounded-lg disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-200 flex items-center space-x-2 ${
                      isDarkMode 
                        ? 'border-gray-600 hover:bg-gray-700 text-gray-300' 
                        : 'border-gray-300 hover:bg-gray-50 text-gray-700'
                    }`}
                  >
                    <span>Next</span>
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                    </svg>
                  </button>

                  <button
                    onClick={() => setItemsCurrentPage(totalPages)}
                    disabled={itemsCurrentPage === totalPages}
                    className={`px-3 py-2 text-sm border rounded-lg disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-200 flex items-center space-x-1 ${
                      isDarkMode 
                        ? 'border-gray-600 hover:bg-gray-700 text-gray-300' 
                        : 'border-gray-300 hover:bg-gray-50 text-gray-700'
                    }`}
                  >
                    <span className="hidden sm:inline">Last</span>
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 5l7 7-7 7M5 5l7 7-7 7" />
                    </svg>
                  </button>
                </div>
              </div>

              {/* Quick Jump to Page */}
              <div className={`mt-4 pt-4 border-t ${isDarkMode ? 'border-gray-700' : 'border-gray-100'}`}>
                <div className="flex flex-col sm:flex-row items-center justify-between gap-4">
                  <div className={`flex items-center space-x-2 text-sm ${isDarkMode ? 'text-gray-300' : 'text-gray-600'}`}>
                    <span>Quick jump to page:</span>
                    <input
                      type="number"
                      min="1"
                      max={totalPages}
                      value={itemsCurrentPage}
                      onChange={(e) => {
                        const page = parseInt(e.target.value);
                        if (page >= 1 && page <= totalPages) {
                          setItemsCurrentPage(page);
                        }
                      }}
                      className={`w-16 px-2 py-1 border rounded text-center focus:ring-2 focus:ring-blue-500 ${
                        isDarkMode 
                          ? 'bg-gray-700 border-gray-600 text-gray-100 focus:border-blue-500' 
                          : 'bg-white border-gray-300 text-gray-900 focus:border-blue-500'
                      }`}
                    />
                    <span>of {totalPages}</span>
                  </div>
                  
                  <div className={`text-xs ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                    Press <kbd className={`px-2 py-1 rounded text-xs ${
                      isDarkMode ? 'bg-gray-700' : 'bg-gray-100'
                    }`}>Enter</kbd> to select an item quickly
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );

  // Add handleSalesmanChange function
  const handleSalesmanChange = (selected) => {
    setForm(prev => ({
      ...prev,
      salesman: selected ? selected.map(s => s.value) : []
    }));
  };

  // Add this function to handle viewing order details
  const handleViewOrder = async (orderId) => {
    try {
      setLoading(true);
      const response = await axios.get(
        `${process.env.REACT_APP_API_BASE_URL}/delivery-orders/details/${orderId}`
      );
      
      if (response.data && response.data.success) {
        setSelectedOrder(response.data.deliveryOrder);
        setShowViewModal(true);
      } else {
        throw new Error('Failed to fetch order details');
      }
    } catch (error) {
      console.error('Error fetching order details:', error);
      Swal.fire({
        icon: 'error',
        title: 'Error',
        text: error.response?.data?.message || error.message || 'Failed to fetch order details'
      });
    } finally {
      setLoading(false);
    }
  };

  // Create the order details modal component
  const OrderDetailsModal = () => {
    // State to track which version is being viewed
    const [viewingPastData, setViewingPastData] = useState(null);
    
    if (!selectedOrder) return null;
    
    return (
      <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-60 overflow-y-auto">
        <div className={`rounded-lg p-4 sm:p-6 w-full max-w-6xl max-h-[75vh] sm:max-h-[80vh] overflow-y-auto shadow-2xl border m-4 ${
          isDarkMode ? 'bg-gray-800 border-gray-600' : 'bg-white border-gray-200'
        }`}>
          <div className={`flex justify-between items-center mb-4 sm:mb-6 border-b pb-3 sm:pb-4 ${
            isDarkMode ? 'border-gray-600' : 'border-gray-200'
          }`}>
            <div>
              <h3 className={`text-xl sm:text-2xl font-bold ${isDarkMode ? 'text-gray-100' : 'text-gray-800'}`}>
                Order Details: <span className={isDarkMode ? 'text-blue-400' : 'text-blue-600'}>
                  {selectedOrder.doNumber}
                </span>
              </h3>
              <p className={`text-xs sm:text-sm mt-1 ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                Created on {formatDate(selectedOrder.createdAt)} by {selectedOrder.createdBy?.name || "Unknown"}
              </p>
            </div>
            <button
              onClick={() => setShowViewModal(false)}
              className={`p-2 rounded-full transition duration-150 ${
                isDarkMode 
                  ? 'text-gray-400 hover:text-gray-200 hover:bg-gray-700' 
                  : 'text-gray-500 hover:text-gray-700 hover:bg-gray-100'
              }`}
            >
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>

          {viewingPastData ? (
            // Viewing a previous version of the order
            <div className="relative">
              <div className="absolute -top-12 right-0">
                <button
                  onClick={() => setViewingPastData(null)}
                  className={`px-4 py-2 text-white rounded flex items-center transition-colors ${
                    isDarkMode ? 'bg-blue-700 hover:bg-blue-600' : 'bg-blue-600 hover:bg-blue-700'
                  }`}
                >
                  <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 19l-7-7m0 0l7-7m-7 7h18" />
                  </svg>
                  Back to Current Version
                </button>
              </div>

              <div className={`border-l-4 p-4 mb-6 ${
                isDarkMode 
                  ? 'bg-yellow-900/30 border-yellow-600' 
                  : 'bg-yellow-50 border-yellow-400'
              }`}>
                <div className="flex items-center">
                  <svg className={`w-5 h-5 mr-3 ${isDarkMode ? 'text-yellow-400' : 'text-yellow-600'}`} fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                  <p className={`text-sm ${isDarkMode ? 'text-yellow-200' : 'text-yellow-700'}`}>
                    You are viewing a previous version of this order from <span className="font-medium">{formatDate(viewingPastData.date)}</span> updated by <span className="font-medium">{viewingPastData.employee?.name || "Unknown"}</span>
                  </p>
                </div>
              </div>

              {/* Display past version data */}
              <div className="space-y-6">
                <div className={`rounded-lg shadow-md overflow-hidden ${
                  isDarkMode ? 'bg-gray-750' : 'bg-white'
                }`}>
                  <div className={`px-4 py-3 border-b ${
                    isDarkMode ? 'bg-gray-700 border-gray-600' : 'bg-gray-50 border-gray-200'
                  }`}>
                    <h3 className={`text-lg font-semibold ${isDarkMode ? 'text-gray-100' : 'text-gray-800'}`}>
                      Previous Order Details
                    </h3>
                  </div>
                  <div className="p-4">
                    <pre className={`whitespace-pre-wrap p-4 rounded text-sm overflow-x-auto ${
                      isDarkMode ? 'bg-gray-800 text-gray-200' : 'bg-gray-50 text-gray-900'
                    }`}>
                      {JSON.stringify(viewingPastData.pastData, null, 2)}
                    </pre>
                  </div>
                </div>
              </div>
            </div>
          ) : (
            // Default view: Current order details
            <>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
                {/* Order Info - Enhanced styling */}
                <div className={`p-5 rounded-lg shadow-md ${
                  isDarkMode 
                    ? 'bg-gradient-to-r from-blue-900/40 to-blue-800/40' 
                    : 'bg-gradient-to-r from-blue-50 to-blue-100'
                }`}>
                  <h4 className={`font-semibold mb-4 flex items-center ${
                    isDarkMode ? 'text-blue-300' : 'text-blue-800'
                  }`}>
                    <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" />
                    </svg>
                    Order Information
                  </h4>
                  <dl className="grid grid-cols-2 gap-3">
                    <dt className={`text-sm font-medium ${isDarkMode ? 'text-gray-300' : 'text-gray-600'}`}>
                      DO Number:
                    </dt>
                    <dd className={`text-sm font-medium ${isDarkMode ? 'text-gray-100' : 'text-gray-900'}`}>
                      {selectedOrder.doNumber}
                    </dd>
                    
                    <dt className={`text-sm font-medium ${isDarkMode ? 'text-gray-300' : 'text-gray-600'}`}>
                      Status:
                    </dt>
                    <dd className="text-sm">
                      <span className={`px-2 py-1 inline-flex text-xs leading-5 font-semibold rounded-full 
                        ${selectedOrder.status === 'Pending' 
                          ? isDarkMode ? 'bg-yellow-900/40 text-yellow-300' : 'bg-yellow-100 text-yellow-800'
                          : selectedOrder.status === 'Completed' 
                            ? isDarkMode ? 'bg-green-900/40 text-green-300' : 'bg-green-100 text-green-800'
                            : isDarkMode ? 'bg-red-900/40 text-red-300' : 'bg-red-100 text-red-800'
                        }`}>
                        {selectedOrder.status}
                      </span>
                    </dd>
                    
                    <dt className={`text-sm font-medium ${isDarkMode ? 'text-gray-300' : 'text-gray-600'}`}>
                      Created On:
                    </dt>
                    <dd className={`text-sm font-medium ${isDarkMode ? 'text-gray-100' : 'text-gray-900'}`}>
                      {new Date(selectedOrder.createdAt).toLocaleString()}
                    </dd>
                    
                    <dt className={`text-sm font-medium ${isDarkMode ? 'text-gray-300' : 'text-gray-600'}`}>
                      Mode of Sales:
                    </dt>
                    <dd className={`text-sm font-medium ${isDarkMode ? 'text-gray-100' : 'text-gray-900'}`}>
                      {selectedOrder.modeOfSales}
                    </dd>
                    
                    <dt className={`text-sm font-medium ${isDarkMode ? 'text-gray-300' : 'text-gray-600'}`}>
                      Manual DO Number:
                    </dt>
                    <dd className={`text-sm font-medium ${isDarkMode ? 'text-gray-100' : 'text-gray-900'}`}>
                      {selectedOrder.Manual_dO_Number || '-'}
                    </dd>
                    
                    <dt className={`text-sm font-medium ${isDarkMode ? 'text-gray-300' : 'text-gray-600'}`}>
                      PO Number:
                    </dt>
                    <dd className={`text-sm font-medium ${isDarkMode ? 'text-gray-100' : 'text-gray-900'}`}>
                      {selectedOrder.poNumber || '-'}
                    </dd>
                  </dl>
                </div>
                
                {/* Recipient Info - Enhanced styling */}
                <div className={`p-5 rounded-lg shadow-md ${
                  isDarkMode 
                    ? 'bg-gradient-to-r from-purple-900/40 to-purple-800/40' 
                    : 'bg-gradient-to-r from-purple-50 to-purple-100'
                }`}>
                  <h4 className={`font-semibold mb-4 flex items-center ${
                    isDarkMode ? 'text-purple-300' : 'text-purple-800'
                  }`}>
                    <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
                    </svg>
                    Recipient Information
                  </h4>
                  <dl className="grid grid-cols-2 gap-3">
                    <dt className={`text-sm font-medium ${isDarkMode ? 'text-gray-300' : 'text-gray-600'}`}>
                      Type:
                    </dt>
                    <dd className={`text-sm font-medium ${isDarkMode ? 'text-gray-100' : 'text-gray-900'}`}>
                      {selectedOrder.recipientType}
                    </dd>
                    
                    <dt className={`text-sm font-medium ${isDarkMode ? 'text-gray-300' : 'text-gray-600'}`}>
                      Name:
                    </dt>
                    <dd className={`text-sm font-medium ${isDarkMode ? 'text-gray-100' : 'text-gray-900'}`}>
                      {selectedOrder.recipientType === 'Customer' 
                        ? selectedOrder.recipient?.name
                        : selectedOrder.recipient?.businessName}
                    </dd>
                    
                    <dt className={`text-sm font-medium ${isDarkMode ? 'text-gray-300' : 'text-gray-600'}`}>
                      Address:
                    </dt>
                    <dd className={`text-sm font-medium ${isDarkMode ? 'text-gray-100' : 'text-gray-900'}`}>
                      {selectedOrder.recipient?.address || '-'}
                    </dd>
                    
                    <dt className={`text-sm font-medium ${isDarkMode ? 'text-gray-300' : 'text-gray-600'}`}>
                      Phone:
                    </dt>
                    <dd className={`text-sm font-medium ${isDarkMode ? 'text-gray-100' : 'text-gray-900'}`}>
                      {selectedOrder.recipient?.phone || selectedOrder.recipient?.phoneNumber || '-'}
                    </dd>
                  </dl>
                </div>
              </div>

              {/* MA Information - Only show for Customers with MA assigned */}
              {selectedOrder.recipientType === 'Customer' && selectedOrder.recipient?.ma?.maId && (
                <div className="mb-4">
                  <h4 className={`font-semibold mb-4 flex items-center ${
                    isDarkMode ? 'text-teal-300' : 'text-teal-800'
                  }`}>
                    <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                    </svg>
                    Marketing Agent (MA) Information
                  </h4>
                  <div className={`p-4 rounded-lg border-l-4 ${
                    isDarkMode 
                      ? 'bg-teal-900/30 border-teal-600' 
                      : 'bg-teal-50 border-teal-500'
                  }`}>
                    <dl className="grid grid-cols-2 gap-3">
                      <dt className={`text-sm font-medium ${isDarkMode ? 'text-gray-300' : 'text-gray-600'}`}>
                        MA Type:
                      </dt>
                      <dd className={`text-sm font-medium ${isDarkMode ? 'text-gray-100' : 'text-gray-900'}`}>
                        {selectedOrder.recipient.ma.maType}
                      </dd>
                      
                      <dt className={`text-sm font-medium ${isDarkMode ? 'text-gray-300' : 'text-gray-600'}`}>
                        MA Name:
                      </dt>
                      <dd className={`text-sm font-medium ${isDarkMode ? 'text-gray-100' : 'text-gray-900'}`}>
                        {selectedOrder.recipient.ma.maType === 'Dealer' 
                          ? selectedOrder.recipient.ma.maId.businessName 
                          : selectedOrder.recipient.ma.maId.name || 'N/A'}
                      </dd>
                      
                      <dt className={`text-sm font-medium ${isDarkMode ? 'text-gray-300' : 'text-gray-600'}`}>
                        MA Contact:
                      </dt>
                      <dd className={`text-sm font-medium ${isDarkMode ? 'text-gray-100' : 'text-gray-900'}`}>
                        {selectedOrder.recipient.ma.maType === 'Dealer' 
                          ? selectedOrder.recipient.ma.maId.phone 
                          : selectedOrder.recipient.ma.maId.phoneNumber || 'N/A'}
                      </dd>

                      {selectedOrder.recipient.ma.maType === 'Dealer' && selectedOrder.recipient.ma.maId.station && (
                        <>
                          <dt className={`text-sm font-medium ${isDarkMode ? 'text-gray-300' : 'text-gray-600'}`}>
                            MA Station:
                          </dt>
                          <dd className={`text-sm font-medium ${isDarkMode ? 'text-gray-100' : 'text-gray-900'}`}>
                            {selectedOrder.recipient.ma.maId.station.name || 'N/A'}
                          </dd>
                        </>
                      )}
                    </dl>
                  </div>
                </div>
              )}
              
              {/* Items Table - Enhanced styling with comma formatting */}
              <div className="mt-6 mb-6">
                <h4 className={`font-semibold mb-4 flex items-center ${
                  isDarkMode ? 'text-gray-100' : 'text-gray-800'
                }`}>
                  <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 11V7a4 4 0 00-8 0v4M5 9h14l1 12H4L5 9z" />
                  </svg>
                  Items
                </h4>
                <div className="overflow-x-auto rounded-lg shadow">
                  <table className={`min-w-full ${isDarkMode ? 'bg-gray-800' : 'bg-white'}`}>
                    <thead className={isDarkMode ? 'bg-gray-750' : 'bg-gray-100'}>
                      <tr>
                        <th className={`px-4 py-3 text-left text-xs font-medium uppercase tracking-wider ${
                          isDarkMode ? 'text-gray-300' : 'text-gray-600'
                        }`}>Item</th>
                        <th className={`px-4 py-3 text-left text-xs font-medium uppercase tracking-wider ${
                          isDarkMode ? 'text-gray-300' : 'text-gray-600'
                        }`}>Print Type</th>
                        <th className={`px-4 py-3 text-left text-xs font-medium uppercase tracking-wider ${
                          isDarkMode ? 'text-gray-300' : 'text-gray-600'
                        }`}>Description</th>
                        <th className={`px-4 py-3 text-left text-xs font-medium uppercase tracking-wider ${
                          isDarkMode ? 'text-gray-300' : 'text-gray-600'
                        }`}>HSN</th>
                        <th className={`px-4 py-3 text-left text-xs font-medium uppercase tracking-wider ${
                          isDarkMode ? 'text-gray-300' : 'text-gray-600'
                        }`}>Qty</th>
                        <th className={`px-4 py-3 text-left text-xs font-medium uppercase tracking-wider ${
                          isDarkMode ? 'text-gray-300' : 'text-gray-600'
                        }`}>Unit</th>
                        <th className={`px-4 py-3 text-left text-xs font-medium uppercase tracking-wider ${
                          isDarkMode ? 'text-gray-300' : 'text-gray-600'
                        }`}>Rate</th>
                        <th className={`px-4 py-3 text-left text-xs font-medium uppercase tracking-wider ${
                          isDarkMode ? 'text-gray-300' : 'text-gray-600'
                        }`}>Discount</th>
                        <th className={`px-4 py-3 text-left text-xs font-medium uppercase tracking-wider ${
                          isDarkMode ? 'text-gray-300' : 'text-gray-600'
                        }`}>TT</th>
                        <th className={`px-4 py-3 text-left text-xs font-medium uppercase tracking-wider ${
                          isDarkMode ? 'text-gray-300' : 'text-gray-600'
                        }`}>Amount</th>
                      </tr>
                    </thead>
                    <tbody className={`divide-y ${isDarkMode ? 'divide-gray-700' : 'divide-gray-200'}`}>
                      {(selectedOrder.items || []).map((item, index) => (
                        <tr key={index} className={
                          index % 2 === 0 
                            ? isDarkMode ? 'bg-gray-800' : 'bg-white' 
                            : isDarkMode ? 'bg-gray-750' : 'bg-gray-50'
                        }>
                          <td className={`px-4 py-3 text-sm ${isDarkMode ? 'text-gray-200' : 'text-gray-900'}`}>
                            {item.itemId?.Name || "Unknown Item"}
                          </td>
                          <td className="px-4 py-3 text-sm">
                            <span className={`px-2 py-1 inline-flex text-xs leading-5 font-semibold rounded-full ${
                              item.printType === 'item' 
                                ? isDarkMode ? 'bg-blue-900/40 text-blue-300' : 'bg-blue-100 text-blue-800'
                                : item.printType === 'both' 
                                ? isDarkMode ? 'bg-green-900/40 text-green-300' : 'bg-green-100 text-green-800'
                                : item.printType === 'description' 
                                ? isDarkMode ? 'bg-purple-900/40 text-purple-300' : 'bg-purple-100 text-purple-800'
                                : isDarkMode ? 'bg-gray-700 text-gray-300' : 'bg-gray-100 text-gray-800'
                            }`}>
                              {item.printType || 'item'}
                            </span>
                          </td>
                          <td className={`px-4 py-3 text-sm ${isDarkMode ? 'text-gray-300' : 'text-gray-900'}`}>
                            {item.printType === 'description' || item.printType === 'both' 
                              ? (item.description || '-') 
                              : '-'}
                          </td>
                          <td className={`px-4 py-3 whitespace-nowrap text-sm ${isDarkMode ? 'text-gray-300' : 'text-gray-900'}`}>
                            {item.itemId?._HSNCode || "-"}
                          </td>
                          <td className={`px-4 py-3 whitespace-nowrap text-sm ${isDarkMode ? 'text-gray-300' : 'text-gray-900'}`}>
                            {item.quantity}
                          </td>
                          <td className={`px-4 py-3 whitespace-nowrap text-sm ${isDarkMode ? 'text-gray-300' : 'text-gray-900'}`}>
                            {item.units}
                          </td>
                          <td className={`px-4 py-3 whitespace-nowrap text-sm ${isDarkMode ? 'text-gray-300' : 'text-gray-900'}`}>
                            ₹{formatNumberWithCommas(item.rate)}
                          </td>
                          <td className={`px-4 py-3 whitespace-nowrap text-sm ${isDarkMode ? 'text-gray-300' : 'text-gray-900'}`}>
                            {item.discount}%
                          </td>
                          <td className="px-4 py-3 whitespace-nowrap text-sm">
                            {item.tt?.value > 0 ? (
                              <div className="space-y-1">
                                <span className={`font-medium ${isDarkMode ? 'text-purple-400' : 'text-purple-600'}`}>
                                  {item.tt.type === 'percentage' ? `${item.tt.value}%` : `₹${item.tt.value}`}
                                </span>
                                {item.tt.calculatedAmount > 0 && (
                                  <div className={`text-xs ${isDarkMode ? 'text-purple-400' : 'text-purple-500'}`}>
                                    = ₹{item.tt.calculatedAmount.toFixed(2)}
                                  </div>
                                )}
                              </div>
                            ) : (
                              <span className={isDarkMode ? 'text-gray-500' : 'text-gray-400'}>-</span>
                            )}
                          </td>
                          <td className={`px-4 py-3 whitespace-nowrap text-sm font-medium ${
                            isDarkMode ? 'text-gray-100' : 'text-gray-900'
                          }`}>
                            ₹{formatNumberWithCommas(item.amountAfterDiscount)}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                    <tfoot className={isDarkMode ? 'bg-gray-750' : 'bg-gray-50'}>
                      <tr>
                        <td colSpan="9" className={`px-4 py-3 text-right font-medium ${
                          isDarkMode ? 'text-gray-300' : 'text-gray-700'
                        }`}>Total:</td>
                        <td className={`px-4 py-3 whitespace-nowrap font-bold ${
                          isDarkMode ? 'text-blue-400' : 'text-blue-700'
                        }`}>
                          ₹{formatNumberWithCommas(selectedOrder.totalAmountWithGST)}
                        </td>
                      </tr>
                    </tfoot>
                  </table>
                </div>
              </div>
              
              {/* Additional Charges, Other Details and Amounts - Enhanced grid styling with comma formatting */}
              <div className="mt-6 grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
                <div className={`p-4 rounded-lg shadow-md ${
                  isDarkMode 
                    ? 'bg-gradient-to-r from-green-900/40 to-green-800/40' 
                    : 'bg-gradient-to-r from-green-50 to-green-100'
                }`}>
                  <h4 className={`font-semibold mb-3 flex items-center ${
                    isDarkMode ? 'text-green-300' : 'text-green-800'
                  }`}>
                    <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                    Additional Charges
                  </h4>
                  <dl className="grid grid-cols-2 gap-2 text-sm">
                    <dt className={isDarkMode ? 'text-gray-300' : 'text-gray-600'}>P&F Charges:</dt>
                    <dd className={`font-medium ${isDarkMode ? 'text-gray-100' : 'text-gray-900'}`}>
                      ₹{formatNumberWithCommas(selectedOrder.packingAndForwarding || 0)}
                    </dd>
                    
                    <dt className={isDarkMode ? 'text-gray-300' : 'text-gray-600'}>Labour:</dt>
                    <dd className={`font-medium ${isDarkMode ? 'text-gray-100' : 'text-gray-900'}`}>
                      ₹{formatNumberWithCommas(selectedOrder.labour || 0)}
                    </dd>
                    
                    <dt className={isDarkMode ? 'text-gray-300' : 'text-gray-600'}>Cartage:</dt>
                    <dd className={`font-medium ${isDarkMode ? 'text-gray-100' : 'text-gray-900'}`}>
                      ₹{formatNumberWithCommas(selectedOrder.cartage || 0)}
                    </dd>
                    
                    <dt className={isDarkMode ? 'text-gray-300' : 'text-gray-600'}>Round Off:</dt>
                    <dd className={`font-medium ${isDarkMode ? 'text-gray-100' : 'text-gray-900'}`}>
                      ₹{formatNumberWithCommas(selectedOrder.roundoff || 0)}
                    </dd>
                  </dl>
                </div>
                
                <div className={`p-4 rounded-lg shadow-md ${
                  isDarkMode 
                    ? 'bg-gradient-to-r from-amber-900/40 to-amber-800/40' 
                    : 'bg-gradient-to-r from-amber-50 to-amber-100'
                }`}>
                  <h4 className={`font-semibold mb-3 flex items-center ${
                    isDarkMode ? 'text-amber-300' : 'text-amber-800'
                  }`}>
                    <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                    Other Details
                  </h4>
                  <dl className="grid grid-cols-2 gap-2 text-sm">
                    <dt className={isDarkMode ? 'text-gray-300' : 'text-gray-600'}>Vehicle Number:</dt>
                    <dd className={`font-medium ${isDarkMode ? 'text-gray-100' : 'text-gray-900'}`}>
                      {selectedOrder.vehicleNumber || '-'}
                    </dd>
                    
                    <dt className={isDarkMode ? 'text-gray-300' : 'text-gray-600'}>Transporter:</dt>
                    <dd className={`font-medium ${isDarkMode ? 'text-gray-100' : 'text-gray-900'}`}>
                      {selectedOrder.transporterName?.name || '-'}
                    </dd>
                    
                    <dt className={isDarkMode ? 'text-gray-300' : 'text-gray-600'}>Godown:</dt>
                    <dd className={`font-medium ${isDarkMode ? 'text-gray-100' : 'text-gray-900'}`}>
                      {selectedOrder.godown?.Name || 'Unassigned'}
                    </dd>
                    
                    <dt className={isDarkMode ? 'text-gray-300' : 'text-gray-600'}>Reference:</dt>
                    <dd className={`font-medium ${isDarkMode ? 'text-gray-100' : 'text-gray-900'}`}>
                      {selectedOrder.reference?.name || '-'}
                    </dd>
                    
                    <dt className={isDarkMode ? 'text-gray-300' : 'text-gray-600'}>Payment Mode:</dt>
                    <dd className={`font-medium ${isDarkMode ? 'text-gray-100' : 'text-gray-900'}`}>
                      {selectedOrder.modeOfPayment?.join(', ') || '-'}
                    </dd>
                    
                    {/* Claim Payment From Section - Enhanced Display */}
                    {(selectedOrder.claimPaymentFrom?.name || selectedOrder.claimPaymentFrom?.number) && (
                      <>
                        <dt className={`flex items-center ${isDarkMode ? 'text-gray-300' : 'text-gray-600'}`}>
                          <svg className="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                          </svg>
                          Claim Payment From:
                        </dt>
                        <dd className={`font-medium ${isDarkMode ? 'text-gray-100' : 'text-gray-900'}`}>
                          <div className={`p-2 rounded border-l-4 ${
                            isDarkMode 
                              ? 'bg-purple-900/30 border-purple-600' 
                              : 'bg-purple-50 border-purple-300'
                          }`}>
                            {selectedOrder.claimPaymentFrom.name && (
                              <div className={`text-sm font-semibold flex items-center ${
                                isDarkMode ? 'text-purple-300' : 'text-purple-800'
                              }`}>
                                <svg className="w-3 h-3 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                                </svg>
                                {selectedOrder.claimPaymentFrom.name}
                              </div>
                            )}
                            {selectedOrder.claimPaymentFrom.number && (
                              <div className={`text-xs mt-1 flex items-center ${
                                isDarkMode ? 'text-purple-400' : 'text-purple-600'
                              }`}>
                                <svg className="w-3 h-3 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z" />
                                </svg>
                                {selectedOrder.claimPaymentFrom.number}
                              </div>
                            )}
                          </div>
                        </dd>
                      </>
                    )}
                  </dl>
                </div>
                
                {/* TT Summary Section */}
                {selectedOrder.reference && selectedOrder.totalTTAmount > 0 && (
                  <div className={`p-4 rounded-lg shadow-md ${
                    isDarkMode 
                      ? 'bg-gradient-to-r from-purple-900/40 to-purple-800/40' 
                      : 'bg-gradient-to-r from-purple-50 to-purple-100'
                  }`}>
                    <h4 className={`font-semibold mb-3 flex items-center ${
                      isDarkMode ? 'text-purple-300' : 'text-purple-800'
                    }`}>
                      <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                      </svg>
                      TT Summary
                    </h4>
                    <div className="space-y-2">
                      <div className="flex justify-between items-center text-sm">
                        <span className={`font-medium ${isDarkMode ? 'text-purple-300' : 'text-purple-700'}`}>
                          Total TT Amount:
                        </span>
                        <span className={`font-bold ${isDarkMode ? 'text-purple-400' : 'text-purple-800'}`}>
                          ₹{formatNumberWithCommas(selectedOrder.totalTTAmount)}
                        </span>
                      </div>
                      
                      {/* Item-wise TT breakdown */}
                      <div className={`space-y-1 text-xs p-2 rounded border ${
                        isDarkMode 
                          ? 'bg-purple-900/20 border-purple-700' 
                          : 'bg-purple-25 border-purple-200'
                      }`}>
                        <div className={`font-medium mb-1 ${isDarkMode ? 'text-purple-300' : 'text-purple-700'}`}>
                          Item-wise breakdown:
                        </div>
                        {selectedOrder.items?.filter(item => item.tt?.calculatedAmount > 0).map((item, index) => (
                          <div key={index} className="flex justify-between items-center">
                            <span className={isDarkMode ? 'text-purple-400' : 'text-purple-600'}>
                              {item.itemId?.Name || `Item #${index + 1}`}
                              <span className={`ml-1 ${isDarkMode ? 'text-purple-500' : 'text-purple-500'}`}>
                                ({item.tt.type === 'percentage' ? `${item.tt.value}%` : `₹${item.tt.value}`})
                              </span>
                            </span>
                            <span className={`font-medium ${isDarkMode ? 'text-purple-300' : 'text-purple-700'}`}>
                              ₹{formatNumberWithCommas(item.tt.calculatedAmount)}
                            </span>
                          </div>
                        ))}
                        
                        {selectedOrder.items?.filter(item => item.tt?.calculatedAmount > 0).length === 0 && (
                          <div className={`italic ${isDarkMode ? 'text-purple-500' : 'text-purple-500'}`}>
                            No TT calculations found
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                )}

                <div className={`p-4 rounded-lg shadow-md ${
                  isDarkMode 
                    ? 'bg-gradient-to-r from-blue-900/40 to-blue-800/40' 
                    : 'bg-gradient-to-r from-blue-50 to-blue-100'
                }`}>
                  <h4 className={`font-semibold mb-3 flex items-center ${
                    isDarkMode ? 'text-blue-300' : 'text-blue-800'
                  }`}>
                    <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 8h6m-5 0a3 3 0 110 6H9l3 3m-3-6h6m6 1a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                    Amounts
                  </h4>
                  <dl className="grid grid-cols-2 gap-2 text-sm">
                    <dt className={isDarkMode ? 'text-gray-300' : 'text-gray-600'}>Amount without GST:</dt>
                    <dd className={`font-medium ${isDarkMode ? 'text-gray-100' : 'text-gray-900'}`}>
                      ₹{formatNumberWithCommas(selectedOrder.totalAmountWithoutGST || 0)}
                    </dd>
                    
                    <dt className={isDarkMode ? 'text-gray-300' : 'text-gray-600'}>GST Amount:</dt>
                    <dd className={`font-medium ${isDarkMode ? 'text-gray-100' : 'text-gray-900'}`}>
                      ₹{formatNumberWithCommas((selectedOrder.packingAndForwardingGST || 0) + 
                         (selectedOrder.labourGST || 0) + 
                         (selectedOrder.cartageGST || 0))}
                    </dd>
                    
                    <dt className={`font-semibold border-t pt-2 mt-2 ${
                      isDarkMode ? 'text-gray-300 border-blue-700' : 'text-gray-600 border-blue-200'
                    }`}>Final Amount:</dt>
                    <dd className={`font-bold border-t pt-2 mt-2 ${
                      isDarkMode ? 'text-blue-400 border-blue-700' : 'text-blue-700 border-blue-200'
                    }`}>
                      ₹{formatNumberWithCommas(selectedOrder.totalAmountWithGST)}
                    </dd>
                  </dl>
                </div>
              </div>
              
              {/* Update History - Enhanced styling */}
              <div className={`mt-6 rounded-lg shadow overflow-hidden ${
                isDarkMode ? 'bg-gray-800' : 'bg-white'
              }`}>
                <div className={`px-4 py-3 border-b ${
                  isDarkMode 
                    ? 'bg-gray-750 border-gray-700' 
                    : 'bg-gray-50 border-gray-200'
                }`}>
                  <div className="flex items-center justify-between">
                    <h4 className={`text-lg font-semibold ${
                      isDarkMode ? 'text-gray-100' : 'text-gray-800'
                    }`}>Update History</h4>
                    {selectedOrder.updatedBy && selectedOrder.updatedBy.length > 0 && (
                      <span className={`text-sm px-2 py-1 rounded ${
                        isDarkMode 
                          ? 'bg-blue-900/40 text-blue-300' 
                          : 'bg-blue-100 text-gray-600'
                      }`}>
                        {selectedOrder.updatedBy.length} {selectedOrder.updatedBy.length === 1 ? 'Entry' : 'Entries'}
                      </span>
                    )}
                  </div>
                </div>
                <div className="p-4">
                  {selectedOrder.updatedBy && selectedOrder.updatedBy.length > 0 ? (
                    <div className="space-y-4">
                      {selectedOrder.updatedBy.map((update, index) => (
                        <div key={index} className={`border-l-4 pl-4 pb-3 border-b last:border-b-0 ${
                          isDarkMode 
                            ? 'border-blue-700 border-b-gray-700' 
                            : 'border-blue-200 border-b-gray-100'
                        }`}>
                          <div className="flex items-center justify-between mb-2">
                            <div className="flex items-center space-x-2">
                              <div className="flex items-center">
                                <svg className={`w-4 h-4 mr-2 ${
                                  isDarkMode ? 'text-gray-400' : 'text-gray-500'
                                }`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                                </svg>
                                <span className={`text-sm font-medium ${
                                  isDarkMode ? 'text-gray-200' : 'text-gray-700'
                                }`}>
                                  {update.employeeName || update.employee?.name || update.employee?.email || 'Unknown User'}
                                </span>
                              </div>
                              <span className={`text-xs ${isDarkMode ? 'text-gray-500' : 'text-gray-500'}`}>•</span>
                              <span className={`text-xs ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                                {new Date(update.date).toLocaleString()}
                              </span>
                            </div>
                            <div className="flex items-center space-x-2">
                              {update.pastData && (
                                <button
                                  onClick={() => setViewingPastData({
                                    date: update.date,
                                    employee: { name: update.employeeName || update.employee?.name || 'Unknown' },
                                    pastData: update.pastData
                                  })}
                                  className={`text-xs px-2 py-1 rounded transition duration-150 ${
                                    isDarkMode 
                                      ? 'bg-blue-900/40 hover:bg-blue-900/60 text-blue-300' 
                                      : 'bg-blue-100 hover:bg-blue-200 text-blue-700'
                                  }`}
                                >
                                  View Data
                                </button>
                              )}
                            </div>
                          </div>
                          {update.pastData && (
                            <div className={`text-xs ${isDarkMode ? 'text-gray-400' : 'text-gray-600'}`}>
                              Previous order data available - click "View Data" to see details
                            </div>
                          )}
                        </div>
                      ))}
                    </div>
                  ) : (
                    <div className={`text-center py-8 ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                      <svg className={`w-12 h-12 mx-auto mb-3 ${
                        isDarkMode ? 'text-gray-600' : 'text-gray-300'
                      }`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                      </svg>
                      <p className="text-sm">No update history available</p>
                      <p className={`text-xs mt-1 ${isDarkMode ? 'text-gray-500' : 'text-gray-400'}`}>
                        This delivery order hasn't been modified since creation
                      </p>
                    </div>
                  )}
                </div>
              </div>
            </>
          )}
            
          {/* Close Button */}
          <div className="mt-6 flex justify-end">
            <button
              onClick={() => {
                setViewingPastData(null);
                setShowViewModal(false);
              }}
              className={`px-4 py-2 rounded-md transition duration-150 flex items-center ${
                isDarkMode 
                  ? 'bg-gray-700 hover:bg-gray-600 text-gray-100' 
                  : 'bg-gray-800 hover:bg-gray-700 text-white'
              }`}
            >
              <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
              Close
            </button>
          </div>
        </div>
      </div>
    );
  };

  // Item Details Modal Component
  const ItemDetailsModal = () => {
    if (!selectedItemForDetails) return null;

    const formatValue = (value) => {
      if (value === null || value === undefined || value === '') return 'N/A';
      return value.toString();
    };

    return (
      <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-60 overflow-y-auto">
        <div className={`rounded-lg p-4 sm:p-6 w-full max-w-4xl max-h-[70vh] sm:max-h-[75vh] overflow-y-auto shadow-2xl border m-4 ${
          isDarkMode ? 'bg-gray-800 border-gray-600' : 'bg-white border-gray-200'
        }`}>
          <div className={`flex justify-between items-center mb-4 sm:mb-6 border-b pb-3 sm:pb-4 ${
            isDarkMode ? 'border-gray-700' : 'border-gray-200'
          }`}>
            <div>
              <h3 className={`text-xl sm:text-2xl font-bold ${isDarkMode ? 'text-gray-100' : 'text-gray-800'}`}>
                Item Details: <span className={isDarkMode ? 'text-blue-400' : 'text-blue-600'}>{selectedItemForDetails.Name}</span>
              </h3>
              <p className={`text-xs sm:text-sm mt-1 ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                Complete information about this tally item
              </p>
            </div>
            <button
              onClick={() => {
                setShowItemDetailsModal(false);
                setSelectedItemForDetails(null);
              }}
              className={`text-2xl font-bold ${
                isDarkMode ? 'text-gray-400 hover:text-gray-200' : 'text-gray-500 hover:text-gray-700'
              }`}
            >
              ×
            </button>
          </div>

          {/* Item Information Grid */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {/* Basic Information */}
            <div className={`p-4 rounded-lg shadow-md ${
              isDarkMode 
                ? 'bg-blue-900/30 border border-blue-700' 
                : 'bg-gradient-to-r from-blue-50 to-blue-100'
            }`}>
              <h4 className={`font-semibold mb-3 flex items-center ${isDarkMode ? 'text-blue-300' : 'text-blue-800'}`}>
                <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
                Basic Information
              </h4>
              <dl className="grid grid-cols-1 gap-2 text-sm">
                <div>
                  <dt className={`font-medium ${isDarkMode ? 'text-gray-400' : 'text-gray-600'}`}>Name:</dt>
                  <dd className={`font-semibold ${isDarkMode ? 'text-gray-100' : 'text-gray-800'}`}>{formatValue(selectedItemForDetails.Name)}</dd>
                </div>
                <div>
                  <dt className={`font-medium ${isDarkMode ? 'text-gray-400' : 'text-gray-600'}`}>ID:</dt>
                  <dd className={`font-medium ${isDarkMode ? 'text-gray-200' : 'text-gray-800'}`}>{formatValue(selectedItemForDetails.id)}</dd>
                </div>
                <div>
                  <dt className={`font-medium ${isDarkMode ? 'text-gray-400' : 'text-gray-600'}`}>GUID:</dt>
                  <dd className={`font-medium text-xs break-all ${isDarkMode ? 'text-gray-300' : 'text-gray-800'}`}>{formatValue(selectedItemForDetails.GUID)}</dd>
                </div>
                <div>
                  <dt className={`font-medium ${isDarkMode ? 'text-gray-400' : 'text-gray-600'}`}>Category:</dt>
                  <dd className={`font-medium ${isDarkMode ? 'text-gray-200' : 'text-gray-800'}`}>{formatValue(selectedItemForDetails.category)}</dd>
                </div>
                <div>
                  <dt className={`font-medium ${isDarkMode ? 'text-gray-400' : 'text-gray-600'}`}>Parent Group:</dt>
                  <dd className={`font-medium ${isDarkMode ? 'text-gray-200' : 'text-gray-800'}`}>{formatValue(selectedItemForDetails.parent)}</dd>
                </div>
                <div>
                  <dt className={`font-medium ${isDarkMode ? 'text-gray-400' : 'text-gray-600'}`}>Section:</dt>
                  <dd className={`font-medium ${isDarkMode ? 'text-gray-200' : 'text-gray-800'}`}>{formatValue(selectedItemForDetails.section)}</dd>
                </div>
              </dl>
            </div>

            {/* Stock & Units Information */}
            <div className={`p-4 rounded-lg shadow-md ${
              isDarkMode 
                ? 'bg-green-900/30 border border-green-700' 
                : 'bg-gradient-to-r from-green-50 to-green-100'
            }`}>
              <h4 className={`font-semibold mb-3 flex items-center ${isDarkMode ? 'text-green-300' : 'text-green-800'}`}>
                <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M20 7l-8-4-8 4m16 0l-8 4m8-4v10l-8 4m0-10L4 7m8 4v10M4 7v10l8 4" />
                </svg>
                Stock & Units
              </h4>
              <dl className="grid grid-cols-1 gap-2 text-sm">
                <div>
                  <dt className={`font-medium ${isDarkMode ? 'text-gray-400' : 'text-gray-600'}`}>Closing Balance:</dt>
                  <dd className={`font-semibold ${isDarkMode ? 'text-green-300' : 'text-green-700'}`}>{formatValue(selectedItemForDetails.closingBalance)}</dd>
                </div>
                <div className={`p-2 rounded border-l-4 ${
                  isDarkMode 
                    ? 'bg-blue-900/40 border-blue-500' 
                    : 'bg-blue-50 border-blue-400'
                }`}>
                  <dt className={`font-medium ${isDarkMode ? 'text-gray-300' : 'text-gray-600'}`}>Default Unit Selection:</dt>
                  <dd className={`font-bold ${isDarkMode ? 'text-blue-300' : 'text-blue-700'}`}>
                    pcs (Pieces)
                    <span className={`text-xs font-normal ml-2 ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                      (Default unit for all items)
                    </span>
                  </dd>
                  <div className={`text-xs mt-1 ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                    All items default to 'pcs' but can be changed from the dropdown
                  </div>
                </div>
                <div>
                  <dt className={`font-medium ${isDarkMode ? 'text-gray-400' : 'text-gray-600'}`}>Available Units:</dt>
                  <dd className={`font-medium ${isDarkMode ? 'text-blue-300' : 'text-blue-600'}`}>All standard units available (pcs, box, bag, etc.)</dd>
                </div>
              </dl>
            </div>

            {/* Tax Information */}
            <div className={`p-4 rounded-lg shadow-md ${
              isDarkMode 
                ? 'bg-yellow-900/30 border border-yellow-700' 
                : 'bg-gradient-to-r from-yellow-50 to-yellow-100'
            }`}>
              <h4 className={`font-semibold mb-3 flex items-center ${isDarkMode ? 'text-yellow-300' : 'text-yellow-800'}`}>
                <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 7h6m0 10v-3m-3 3h.01M9 17h.01M9 14h.01M12 14h.01M15 11h.01M12 11h.01M9 11h.01M7 21h10a2 2 0 002-2V5a2 2 0 00-2-2H7a2 2 0 00-2 2v14a2 2 0 012 2h2a2 2 0 002-2z" />
                </svg>
                Tax Information
              </h4>
              <dl className="grid grid-cols-1 gap-2 text-sm">
                <div>
                  <dt className={`font-medium ${isDarkMode ? 'text-gray-400' : 'text-gray-600'}`}>HSN Code:</dt>
                  <dd className={`font-medium ${isDarkMode ? 'text-gray-200' : 'text-gray-800'}`}>{formatValue(selectedItemForDetails.hsnCode)}</dd>
                </div>
                <div>
                  <dt className={`font-medium ${isDarkMode ? 'text-gray-400' : 'text-gray-600'}`}>GST Applicable:</dt>
                  <dd className={`font-medium ${isDarkMode ? 'text-gray-200' : 'text-gray-800'}`}>{formatValue(selectedItemForDetails.gstApplicable)}</dd>
                </div>
                <div>
                  <dt className={`font-medium ${isDarkMode ? 'text-gray-400' : 'text-gray-600'}`}>Integrated Tax:</dt>
                  <dd className={`font-medium ${isDarkMode ? 'text-gray-200' : 'text-gray-800'}`}>{formatValue(selectedItemForDetails.integratedTax)}</dd>
                </div>
                <div>
                  <dt className={`font-medium ${isDarkMode ? 'text-gray-400' : 'text-gray-600'}`}>Central Tax:</dt>
                  <dd className={`font-medium ${isDarkMode ? 'text-gray-200' : 'text-gray-800'}`}>{formatValue(selectedItemForDetails.centralTax)}</dd>
                </div>
              </dl>
            </div>

            {/* Additional Information */}
            <div className={`p-4 rounded-lg shadow-md ${
              isDarkMode 
                ? 'bg-purple-900/30 border border-purple-700' 
                : 'bg-gradient-to-r from-purple-50 to-purple-100'
            }`}>
              <h4 className={`font-semibold mb-3 flex items-center ${isDarkMode ? 'text-purple-300' : 'text-purple-800'}`}>
                <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 11H5m14 0a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2v-6a2 2 0 012-2m14 0V9a2 2 0 00-2-2M5 11V9a2 2 0 012-2m0 0V5a2 2 0 012-2h6a2 2 0 012 2v2M7 7h10" />
                </svg>
                Additional Information
              </h4>
              <dl className="grid grid-cols-1 gap-2 text-sm">
                <div>
                  <dt className={`font-medium ${isDarkMode ? 'text-gray-400' : 'text-gray-600'}`}>Part Number:</dt>
                  <dd className={`font-medium ${isDarkMode ? 'text-gray-200' : 'text-gray-800'}`}>{formatValue(selectedItemForDetails.partNo)}</dd>
                </div>
                <div>
                  <dt className={`font-medium ${isDarkMode ? 'text-gray-400' : 'text-gray-600'}`}>Brand:</dt>
                  <dd className={`font-medium ${isDarkMode ? 'text-gray-200' : 'text-gray-800'}`}>{selectedItemForDetails.brand?.name || 'N/A'}</dd>
                </div>
                <div>
                  <dt className={`font-medium ${isDarkMode ? 'text-gray-400' : 'text-gray-600'}`}>Assigned Category:</dt>
                  <dd className={`font-medium ${isDarkMode ? 'text-gray-200' : 'text-gray-800'}`}>{selectedItemForDetails.assigncategory?.name || 'N/A'}</dd>
                </div>
                <div>
                  <dt className={`font-medium ${isDarkMode ? 'text-gray-400' : 'text-gray-600'}`}>Parameter:</dt>
                  <dd className={`font-medium ${isDarkMode ? 'text-gray-200' : 'text-gray-800'}`}>{selectedItemForDetails.parameter?.name || 'N/A'}</dd>
                </div>
                <div>
                  <dt className={`font-medium ${isDarkMode ? 'text-gray-400' : 'text-gray-600'}`}>Size:</dt>
                  <dd className={`font-medium ${isDarkMode ? 'text-gray-200' : 'text-gray-800'}`}>{selectedItemForDetails.size?.name || 'N/A'}</dd>
                </div>
                <div>
                  <dt className={`font-medium ${isDarkMode ? 'text-gray-400' : 'text-gray-600'}`}>Is Assigned:</dt>
                  <dd className="font-medium">
                    <span className={`px-2 py-1 rounded-full text-xs ${
                      selectedItemForDetails.isAssigned 
                        ? isDarkMode ? 'bg-green-900/40 text-green-300' : 'bg-green-200 text-green-800'
                        : isDarkMode ? 'bg-red-900/40 text-red-300' : 'bg-red-200 text-red-800'
                    }`}>
                      {selectedItemForDetails.isAssigned ? 'Yes' : 'No'}
                    </span>
                  </dd>
                </div>
              </dl>
            </div>
          </div>

          {/* Stock History */}
          {selectedItemForDetails.stock && selectedItemForDetails.stock.length > 0 && (
            <div className={`mt-6 p-4 rounded-lg ${isDarkMode ? 'bg-gray-750' : 'bg-gray-50'}`}>
              <h4 className={`font-semibold mb-3 flex items-center ${isDarkMode ? 'text-gray-100' : 'text-gray-800'}`}>
                <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
                </svg>
                Stock History ({selectedItemForDetails.stock.length} entries)
              </h4>
              <div className="max-h-32 overflow-y-auto">
                <pre className={`text-xs whitespace-pre-wrap ${isDarkMode ? 'text-gray-300' : 'text-gray-600'}`}>
                  {JSON.stringify(selectedItemForDetails.stock, null, 2)}
                </pre>
              </div>
            </div>
          )}

          {/* Timestamps */}
          <div className={`mt-6 p-4 rounded-lg ${isDarkMode ? 'bg-gray-750' : 'bg-gray-50'}`}>
            <h4 className={`font-semibold mb-3 ${isDarkMode ? 'text-gray-100' : 'text-gray-800'}`}>Timestamps</h4>
            <div className="grid grid-cols-2 gap-4 text-sm">
              <div>
                <dt className={`font-medium ${isDarkMode ? 'text-gray-400' : 'text-gray-600'}`}>Created At:</dt>
                <dd className={`font-medium ${isDarkMode ? 'text-gray-200' : 'text-gray-800'}`}>{new Date(selectedItemForDetails.createdAt).toLocaleString()}</dd>
              </div>
              <div>
                <dt className={`font-medium ${isDarkMode ? 'text-gray-400' : 'text-gray-600'}`}>Updated At:</dt>
                <dd className={`font-medium ${isDarkMode ? 'text-gray-200' : 'text-gray-800'}`}>{new Date(selectedItemForDetails.updatedAt).toLocaleString()}</dd>
              </div>
            </div>
          </div>

          {/* Actions */}
          <div className="mt-6 flex justify-between items-center">
            <button
              onClick={() => {
                setShowItemDetailsModal(false);
                handleItemSelection(selectedItemForDetails);
              }}
              className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition duration-150 flex items-center"
            >
              <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
              </svg>
              Select This Item
            </button>
            
            <button
              onClick={() => {
                setShowItemDetailsModal(false);
                setSelectedItemForDetails(null);
              }}
              className="px-4 py-2 bg-gray-600 text-white rounded-md hover:bg-gray-700 transition duration-150 flex items-center"
            >
              <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
              Close
            </button>
          </div>
        </div>
      </div>
    );
  };

  return (
    <div className={`min-h-screen ${isDarkMode ? 'bg-gray-900' : 'bg-gray-50'}`}>
      {/* Page Header */}
      <div className={`${isDarkMode ? 'bg-gradient-to-r from-gray-800 via-gray-900 to-gray-800 border-b border-gray-700' : 'bg-gradient-to-r from-blue-600 via-indigo-600 to-purple-600'} shadow-lg`}>
        <div className="w-full mx-auto px-3 sm:px-6 py-4 sm:py-6">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between space-y-3 sm:space-y-0">
            <div className="flex items-center space-x-3 sm:space-x-4">
              <div className={`p-2 sm:p-3 rounded-lg ${isDarkMode ? 'bg-gray-700' : 'bg-white/20 backdrop-blur-sm'}`}>
                <svg className={`w-6 h-6 sm:w-8 sm:h-8 ${isDarkMode ? 'text-blue-400' : 'text-white'}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                </svg>
              </div>
              <div>
                <h1 className={`text-xl sm:text-2xl md:text-3xl font-bold ${isDarkMode ? 'text-gray-100' : 'text-white'}`}>
                  Delivery Order Management
                </h1>
                <p className={`text-xs sm:text-sm mt-0.5 sm:mt-1 ${isDarkMode ? 'text-gray-300' : 'text-blue-100'}`}>
                  Create and manage delivery orders for {form.type === 'TILES' ? 'Tiles' : 'Sanitary'} products
                </p>
              </div>
            </div>
            <div className={`flex items-center space-x-2 text-xs sm:text-sm ${isDarkMode ? 'text-gray-300' : 'text-white'}`}>
              <span className="font-medium">Godown:</span>
              {form.godown ? (
                <span className={`font-semibold px-2 py-1 rounded ${isDarkMode ? 'bg-blue-900 text-blue-200' : 'bg-white/20 text-white'}`}>
                  {godowns.find(g => g._id === form.godown)?.Name || 'Selected Godown'}
                </span>
              ) : (
                <span className={`font-semibold px-2 py-1 rounded ${isDarkMode ? 'bg-red-900 text-red-200' : 'bg-red-500/30 text-white'}`}>
                  Unassigned
                </span>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="w-full mx-auto p-3 sm:p-6">
      
      <form onSubmit={handleSubmit} className={`rounded-lg shadow-lg p-3 sm:p-4 md:p-6 ${isDarkMode ? 'bg-gray-800' : 'bg-white'}`} ref={formRef}>
        {/* Basic Information Section Header */}
        <div className={`flex items-center space-x-3 mb-4 pb-3 border-b ${isDarkMode ? 'border-gray-700' : 'border-gray-200'}`}>
          <div className={`p-2 rounded-lg ${isDarkMode ? 'bg-blue-900/30' : 'bg-blue-50'}`}>
            <svg className={`w-5 h-5 ${isDarkMode ? 'text-blue-400' : 'text-blue-600'}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
          </div>
          <div>
            <h3 className={`text-base sm:text-lg font-semibold ${isDarkMode ? 'text-gray-100' : 'text-gray-900'}`}>
              Basic Information
            </h3>
            <p className={`text-xs ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
              Select delivery order type and recipient details
            </p>
          </div>
        </div>

        {/* Type of Delivery Order, Recipient Type & Recipient - Mobile Responsive */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3 sm:gap-4 mb-4">
          <div>
            <label className={`block text-sm font-medium mb-1 ${isDarkMode ? 'text-gray-200' : 'text-gray-700'}`}>
              Type of Delivery Order <span className="text-red-500">*</span>
            </label>
            <Select
              value={{ value: form.type, label: form.type === 'SANITARY' ? 'Sanitary' : 'Tiles' }}
              onChange={(selectedOption) => handleTypeChange(selectedOption.value)}
              options={[
                { value: 'SANITARY', label: 'Sanitary' },
                { value: 'TILES', label: 'Tiles' }
              ]}
              className="text-sm"
              placeholder="Select Delivery Order Type"
              isSearchable={false}
              isDisabled={isEditing}
              styles={getSelectStyles(isDarkMode)}
              menuPortalTarget={document.body}
              menuPosition="fixed"
            />
          </div>
          
          <div>
            <label className={`block text-sm font-medium mb-1 ${isDarkMode ? 'text-gray-200' : 'text-gray-700'}`}>
              Recipient Type <span className="text-red-500">*</span>
            </label>
            <Select
              value={{ value: form.recipientType, label: form.recipientType }}
              onChange={(selectedOption) => setForm(prev => ({
                ...prev,
                recipientType: selectedOption.value,
                recipient: null // Clear recipient when type changes
              }))}
              options={[
                { value: 'Customer', label: 'Customer' },
                { value: 'Dealer', label: 'Dealer' }
              ]}
              className="text-sm"
              placeholder="Select Recipient Type"
              isSearchable={false}
              styles={getSelectStyles(isDarkMode)}
              menuPortalTarget={document.body}
              menuPosition="fixed"
            />
          </div>
          
          <div className="sm:col-span-2 lg:col-span-1">
            <label className={`block text-sm font-medium mb-1 ${isDarkMode ? 'text-gray-200' : 'text-gray-700'}`}>
              Recipient <span className="text-red-500">*</span>
            </label>
            <button
              type="button"
              onClick={() => setShowRecipientModal(true)}
              className={`w-full p-2.5 sm:p-2 border rounded-lg text-left min-h-[44px] sm:min-h-0 transition-colors focus:ring-2 focus:ring-blue-500 focus:border-blue-500 ${isDarkMode ? 'bg-gray-700 border-gray-600 hover:bg-gray-650 text-gray-100' : 'bg-white border-gray-300 hover:bg-gray-50 text-gray-900'}`}
            >
              <div className="flex items-center justify-between">
                <span className={form.recipient ? (isDarkMode ? "text-gray-100" : "text-gray-900") : (isDarkMode ? "text-gray-400" : "text-gray-500")}>
                  {form.recipient 
                    ? form.recipient.label 
                    : `Select ${form.recipientType}`
                  }
                </span>
                <svg className={`w-5 h-5 ${isDarkMode ? 'text-gray-400' : 'text-gray-400'}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                </svg>
              </div>
            </button>
          </div>
        </div>

        {/* Include Options - Mobile Responsive */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3 sm:gap-4 mb-4">
          <div>
            <label className={`block text-sm font-medium mb-1 ${isDarkMode ? 'text-gray-200' : 'text-gray-700'}`}>
              Print Options
            </label>
            <div className="flex items-center space-x-4 sm:space-x-6">
              <div className="flex items-center">
                <input
                  type="checkbox"
                  id="isReference"
                  checked={form.isreference}
                  onChange={(e) => setForm(prev => ({ ...prev, isreference: e.target.checked }))}
                  className={`h-4 w-4 text-blue-600 focus:ring-blue-500 rounded border ${isDarkMode ? 'bg-gray-700 border-gray-600' : 'border-gray-300'}`}
                />
                <label htmlFor="isReference" className={`ml-2 text-sm ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                  Include Reference
                </label>
              </div>
              
              <div className="flex items-center">
                <input
                  type="checkbox"
                  id="isNarrationInline"
                  checked={form.isnarration}
                  onChange={(e) => setForm(prev => ({ ...prev, isnarration: e.target.checked }))}
                  className={`h-4 w-4 text-blue-600 focus:ring-blue-500 rounded border ${isDarkMode ? 'bg-gray-700 border-gray-600' : 'border-gray-300'}`}
                />
                <label htmlFor="isNarrationInline" className={`ml-2 text-sm ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                  Include Narration
                </label>
              </div>
            </div>
          </div>
        </div>

        {/* Transport & Logistics Section Header */}
        <div className={`flex items-center space-x-3 mb-4 pb-3 border-b ${isDarkMode ? 'border-gray-700' : 'border-gray-200'}`}>
          <div className={`p-2 rounded-lg ${isDarkMode ? 'bg-green-900/30' : 'bg-green-50'}`}>
            <svg className={`w-5 h-5 ${isDarkMode ? 'text-green-400' : 'text-green-600'}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16V6a1 1 0 00-1-1H4a1 1 0 00-1 1v10a1 1 0 001 1h1m8-1a1 1 0 01-1 1H9m4-1V8a1 1 0 011-1h2.586a1 1 0 01.707.293l3.414 3.414a1 1 0 01.293.707V16a1 1 0 01-1 1h-1m-6-1a1 1 0 001 1h1M5 17a2 2 0 104 0m-4 0a2 2 0 114 0m6 0a2 2 0 104 0m-4 0a2 2 0 114 0" />
            </svg>
          </div>
          <div>
            <h3 className={`text-base sm:text-lg font-semibold ${isDarkMode ? 'text-gray-100' : 'text-gray-900'}`}>
              Transport & Logistics
            </h3>
            <p className={`text-xs ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
              Transporter, godown, reference and vehicle information
            </p>
          </div>
        </div>

        {/* Transporter, Godown, Reference, and Vehicle Number - Mobile Responsive */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4 mb-4">
          <div>
            <label className={`block text-sm font-medium mb-1 ${isDarkMode ? 'text-gray-200' : 'text-gray-700'}`}>
              Transporter
            </label>
            <Select
              value={form.transporterName ? { value: form.transporterName, label: transporters.find(t => t._id === form.transporterName)?.name || '' } : null}
              onChange={(selected) => setForm(prev => ({ ...prev, transporterName: selected ? selected.value : '' }))}
              options={transporters.map(t => ({ value: t._id, label: t.name }))}
              isClearable
              placeholder="Select Transporter"
              className="w-full text-sm"
              styles={getSelectStyles(isDarkMode)}
              menuPortalTarget={document.body}
              menuPosition="fixed"
            />
          </div>
          <div>
            <label className={`block text-sm font-medium mb-1 ${isDarkMode ? 'text-gray-200' : 'text-gray-700'}`}>
              Godown
            </label>
            <Select
              value={form.godown ? { value: form.godown, label: godowns.find(g => g._id === form.godown)?.Name || '' } : null}
              onChange={(selected) => {
                console.log('Godown selection changed:', selected);
                setForm(prev => ({ ...prev, godown: selected ? selected.value : null }));
              }}
              options={godowns.map(g => ({ value: g._id, label: g.Name }))}
              isClearable
              placeholder="Select Godown (Optional)"
              className="w-full text-sm"
              styles={getSelectStyles(isDarkMode)}
              menuPortalTarget={document.body}
              menuPosition="fixed"
            />
          </div>
          <div>
            <label className={`block text-sm font-medium mb-1 ${isDarkMode ? 'text-gray-200' : 'text-gray-700'}`}>
              Reference
            </label>
            <Select
              value={form.reference ? { value: form.reference, label: references.find(i => i._id === form.reference)?.name || '' } : null}
              onChange={(selected) => setForm(prev => ({ ...prev, reference: selected ? selected.value : '' }))}
              options={references.map(i => ({ value: i._id, label: i.name }))}
              isClearable
              placeholder="Select Reference"
              className="w-full text-sm"
              styles={getSelectStyles(isDarkMode)}
              menuPortalTarget={document.body}
              menuPosition="fixed"
            />
          </div>
          <div>
            <label className={`block text-sm font-medium mb-1 ${isDarkMode ? 'text-gray-200' : 'text-gray-700'}`}>
              Vehicle Number
            </label>
            <input
              type="text"
              value={form.vehicleNumber}
              onChange={(e) => handleFormFieldChange('vehicleNumber', e.target.value)}
              className={`w-full px-3 py-2.5 sm:py-2 border rounded-md text-sm min-h-[44px] sm:min-h-0 focus:outline-none focus:ring-2 focus:ring-blue-500 ${isDarkMode ? 'bg-gray-700 border-gray-600 text-gray-100 placeholder-gray-400' : 'bg-white border-gray-300 text-gray-900'}`}
              placeholder="Enter Vehicle Number"
            />
          </div>
        </div>

        {/* Items Section Header */}
        <div className={`flex items-center justify-between mb-4 pb-3 border-b ${isDarkMode ? 'border-gray-700' : 'border-gray-200'}`}>
          <div className="flex items-center space-x-3">
            <div className={`p-2 rounded-lg ${isDarkMode ? 'bg-purple-900/30' : 'bg-purple-50'}`}>
              <svg className={`w-5 h-5 ${isDarkMode ? 'text-purple-400' : 'text-purple-600'}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M20 7l-8-4-8 4m16 0l-8 4m8-4v10l-8 4m0-10L4 7m8 4v10M4 7v10l8 4" />
              </svg>
            </div>
            <div>
              <h3 className={`text-base sm:text-lg font-semibold ${isDarkMode ? 'text-gray-100' : 'text-gray-900'}`}>
                Order Items
              </h3>
              <p className={`text-xs ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                Add and configure items for this delivery order
              </p>
            </div>
          </div>
          <span className={`text-xs px-2 py-1 rounded-full ${isDarkMode ? 'bg-gray-700 text-gray-300' : 'bg-gray-100 text-gray-600'}`}>
            {form.items.length} {form.items.length === 1 ? 'Item' : 'Items'}
          </span>
        </div>

        {/* Items Section */}
        <div className="mb-4">
          <label className={`block text-sm font-medium mb-2 ${isDarkMode ? 'text-gray-200' : 'text-gray-700'}`}>
            Items
          </label>
          <div className="space-y-3 sm:space-y-4">
          {form.items.map((item, index) => (
              <div key={index} className={`rounded-lg shadow-sm border p-3 sm:p-4 ${isDarkMode ? 'bg-gray-750 border-gray-600' : 'bg-white border-gray-200'}`}>
                {/* First Line: Item Selection and Actions - Mobile Responsive */}
                <div className="flex flex-col lg:flex-row lg:items-start lg:justify-between mb-3 sm:mb-4 space-y-3 lg:space-y-0 gap-3">
                  {/* Item Info and Selection */}
                  <div className="flex items-center space-x-2 sm:space-x-4 flex-1 min-w-0">
                    <span className={`text-sm font-medium shrink-0 ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`}>#{index + 1}</span>
                    <div className="flex-1 min-w-0">
                      {item.itemId ? (
                        <div className={`p-2 border rounded text-sm ${isDarkMode ? 'bg-gray-700 border-gray-600' : 'bg-white border-gray-300'}`}>
                          <strong className={`truncate block ${isDarkMode ? 'text-gray-200' : 'text-gray-900'}`}>{item.Name || 'Selected Item'}</strong>
                        </div>
                      ) : (
                        <div className={`p-2 border rounded text-sm ${isDarkMode ? 'bg-gray-700 border-gray-600 text-gray-400' : 'bg-white border-gray-300 text-gray-500'}`}>
                          No item selected
                        </div>
                      )}
                    </div>
                  </div>
                  
                  {/* Action Buttons */}
                  <div className="flex space-x-2 justify-end lg:ml-4">
                    <button 
                      type="button"
                      onClick={() => moveItemUp(index)}
                      disabled={index === 0}
                      className={`p-2 rounded disabled:opacity-50 transition-colors ${isDarkMode ? 'text-blue-400 hover:bg-gray-700' : 'text-blue-600 hover:bg-blue-100'}`}
                      title="Move up"
                    >
                      <FaArrowUp className="text-sm" />
                    </button>
                    <button 
                      type="button"
                      onClick={() => moveItemDown(index)}
                      disabled={index === form.items.length - 1}
                      className={`p-2 rounded disabled:opacity-50 transition-colors ${isDarkMode ? 'text-blue-400 hover:bg-gray-700' : 'text-blue-600 hover:bg-blue-100'}`}
                      title="Move down"
                    >
                      <FaArrowDown className="text-sm" />
                    </button>
                    <button 
                      type="button"
                      onClick={() => copyItem(index)}
                      className={`p-2 rounded transition-colors ${isDarkMode ? 'text-violet-400 hover:bg-gray-700' : 'text-violet-600 hover:bg-violet-100'}`}
                      title="Copy item"
                    >
                      <FaCopy className="text-sm" />
                    </button>
                    <button 
                      type="button"
                      onClick={() => removeItem(index)}
                      className={`p-2 rounded transition-colors ${isDarkMode ? 'text-red-400 hover:bg-gray-700' : 'text-red-600 hover:bg-red-100'}`}
                      title="Remove item"
                    >
                      <FaTrash className="text-sm" />
                    </button>
                  </div>
                </div>
                
                {/* Second Line: All Input Fields - Mobile Responsive */}
                <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-7 gap-3 sm:gap-4">
                  {/* Print Type */}
                  <div className="col-span-2 sm:col-span-1">
                    <label className={`block text-xs font-medium mb-1 ${isDarkMode ? 'text-gray-300' : 'text-gray-500'}`}>
                      Print Type
                    </label>
                    <Select
                      value={[
                        { value: 'item', label: 'Item' },
                        { value: 'both', label: 'Both' },
                        { value: 'description', label: 'Description' }
                      ].find(option => option.value === (item.printType || 'item'))}
                      onChange={(selected) => handleItemChange(index, 'printType', selected.value)}
                      options={[
                        { value: 'item', label: 'Item' },
                        { value: 'both', label: 'Both' },
                        { value: 'description', label: 'Description' }
                      ]}
                      placeholder="Select print type"
                      className="text-sm basic-select"
                      isSearchable={false}
                      styles={getSelectStyles(isDarkMode)}
                      menuPortalTarget={document.body}
                      menuPosition="fixed"
                    />
                  </div>
                  
                  {/* Units */}
                  <div className="col-span-2 sm:col-span-1">
                    <label className={`block text-xs font-medium mb-1 ${isDarkMode ? 'text-gray-300' : 'text-gray-500'}`}>
                      Units
                    </label>
                    <Select
                      value={getUnitOptionsForItem(item).find(option => option.value === item.units) || getUnitOptionsForItem(item)[0]}
                      onChange={(selected) => handleItemChange(index, 'units', selected.value)}
                      options={getUnitOptionsForItem(item)}
                      isDisabled={!item.itemId}
                      placeholder="Select units"
                      className="text-sm basic-select"
                      ref={el => unitsSelectRefs.current[index] = el}
                      styles={getSelectStyles(isDarkMode)}
                      menuPortalTarget={document.body}
                      menuPosition="fixed"
                    />
                  </div>
                  
                  {/* Quantity */}
                  <div>
                    <label className={`block text-xs font-medium mb-1 ${isDarkMode ? 'text-gray-300' : 'text-gray-500'}`}>Quantity</label>
                    <input
                      ref={el => quantityInputRefs.current[index] = el}
                      type="number"
                      min="0.001"
                      step="0.001"
                      value={item.quantity}
                      onChange={(e) => handleItemChange(index, 'quantity', parseFloat(e.target.value) || 0)}
                      className={`w-full p-2 border rounded text-sm min-h-[44px] sm:min-h-0 ${isDarkMode ? 'bg-gray-700 border-gray-600 text-gray-100' : 'bg-white border-gray-300 text-gray-900'}`}
                    />
                  </div>
                  
                  {/* Rate */}
                  <div>
                    <label className={`block text-xs font-medium mb-1 ${isDarkMode ? 'text-gray-300' : 'text-gray-500'}`}>Rate</label>
                    <input
                      type="number"
                      step="0.01"
                      value={item.rate}
                      onChange={(e) => handleItemChange(index, 'rate', parseFloat(e.target.value) || 0)}
                      className={`w-full p-2 border rounded text-sm min-h-[44px] sm:min-h-0 ${isDarkMode ? 'bg-gray-700 border-gray-600 text-gray-100' : 'bg-white border-gray-300 text-gray-900'}`}
                    />
                  </div>
                  
                  {/* Discount */}
                  <div>
                    <label className={`block text-xs font-medium mb-1 ${isDarkMode ? 'text-gray-300' : 'text-gray-500'}`}>Discount (%)</label>
                    <input
                      type="number"
                      step="0.01"
                      value={item.discount}
                      onChange={(e) => handleItemChange(index, 'discount', parseFloat(e.target.value) || 0)}
                      className={`w-full p-2 border rounded text-sm min-h-[44px] sm:min-h-0 ${isDarkMode ? 'bg-gray-700 border-gray-600 text-gray-100' : 'bg-white border-gray-300 text-gray-900'}`}
                    />
                  </div>
                  
                  {/* Amount Before Discount */}
                  <div className="col-span-2 sm:col-span-1">
                    <label className={`block text-xs font-medium mb-1 ${isDarkMode ? 'text-gray-300' : 'text-gray-500'}`}>Amount Before Discount</label>
                    <div className="space-y-1">
                      <div className={`text-sm ${isDarkMode ? 'text-gray-300' : 'text-gray-900'}`}>Per Unit: ₹{(item.rate || 0)?.toFixed(2)}</div>
                      <div className={`text-sm font-medium ${isDarkMode ? 'text-gray-200' : 'text-gray-900'}`}>Total: ₹{item.amountBeforeDiscount?.toFixed(2)}</div>
                    </div>
                  </div>
                  
                  {/* Amount After Discount */}
                  <div className="col-span-2 sm:col-span-1">
                    <label className={`block text-xs font-medium mb-1 ${isDarkMode ? 'text-gray-300' : 'text-gray-500'}`}>Amount After Discount</label>
                    <div className="space-y-1">
                      <div className={`text-sm ${isDarkMode ? 'text-gray-300' : 'text-gray-900'}`}>Per Unit: ₹{((item.rate || 0) * (1 - (item.discount || 0) / 100))?.toFixed(2)}</div>
                      <div className={`text-sm font-medium ${isDarkMode ? 'text-gray-200' : 'text-gray-900'}`}>Total: ₹{item.amountAfterDiscount?.toFixed(2)}</div>
                    </div>
                  </div>
                  
                  {/* Select Item Button */}
                  <div className="flex items-end col-span-2 sm:col-span-3 lg:col-span-1">
                    <button
                      type="button"
                      onClick={(e) => {
                        e.preventDefault();
                        setCurrentItemIndex(index);
                        setShowItemsModal(true);
                      }}
                      className={`w-full px-3 py-2.5 sm:py-2 rounded text-sm font-medium min-h-[44px] sm:min-h-0 transition-colors ${isDarkMode ? 'bg-blue-600 hover:bg-blue-700' : 'bg-blue-500 hover:bg-blue-600'} text-white`}
                    >
                      Select Item
                    </button>
                  </div>
                </div>

                {/* Third Line: TT Configuration - Full Width - Only show when reference is selected */}
                {form.reference && (
                <div className="mt-3 sm:mt-4">
                  {/* TT Configuration - Mobile Responsive */}
                  <div className={`bg-gradient-to-r rounded-lg p-3 sm:p-4 ${isDarkMode ? 'from-indigo-900/40 to-blue-900/40 border border-indigo-700' : 'from-indigo-50 to-blue-50 border border-indigo-200'}`}>
                    <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-3 lg:gap-4">
                      {/* Left: TT Configuration Inputs */}
                      <div className="flex flex-wrap items-center gap-2 sm:gap-3">
                        <div className="flex items-center gap-2">
                          <svg className={`w-4 h-4 ${isDarkMode ? 'text-indigo-400' : 'text-indigo-600'}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                          </svg>
                          <label className={`text-sm font-medium ${isDarkMode ? 'text-indigo-300' : 'text-indigo-800'}`}>TT:</label>
                        </div>
                        
                        {/* Type Selector - Inline */}
                        <select
                          value={item.tt?.type || 'percentage'}
                          onChange={(e) => {
                            handleItemChange(index, 'tt', { 
                              ...item.tt, 
                              type: e.target.value,
                              value: item.tt?.value || 0,
                              calculatedAmount: item.tt?.calculatedAmount || 0
                            });
                          }}
                          className={`px-2 py-1 border rounded text-xs focus:ring-1 focus:ring-indigo-500 focus:border-indigo-500 ${isDarkMode ? 'bg-gray-700 border-gray-600 text-gray-100' : 'bg-white border-gray-200 text-gray-900'}`}
                        >
                          <option value="percentage">%</option>
                          <option value="fixed">₹</option>
                        </select>
                        
                        {/* Value Input - Inline */}
                        <div className="relative">
                          <input
                            type="number"
                            step="0.01"
                            min="0"
                            max={item.tt?.type === 'percentage' ? 100 : undefined}
                            value={item.tt?.value || ''}
                            onChange={(e) => {
                              const value = e.target.value === '' ? 0 : parseFloat(e.target.value) || 0;
                              handleItemChange(index, 'tt', { 
                                ...item.tt, 
                                type: item.tt?.type || 'percentage',
                                value: value,
                                calculatedAmount: item.tt?.calculatedAmount || 0
                              });
                            }}
                            onBlur={(e) => {
                              // Ensure proper formatting on blur
                              if (e.target.value === '') {
                                handleItemChange(index, 'tt', { 
                                  ...item.tt, 
                                  type: item.tt?.type || 'percentage',
                                  value: 0,
                                  calculatedAmount: 0
                                });
                              }
                            }}
                            className={`w-16 px-2 py-1 border rounded text-xs text-center focus:ring-1 focus:ring-indigo-500 focus:border-indigo-500 ${
                              (item.tt?.type === 'percentage' && (item.tt?.value || 0) > 100) ||
                              (item.tt?.type === 'fixed' && (item.tt?.value || 0) > ((item.amountAfterDiscount || 0) / (100 + (item.integratedTax || 18)) * 100))
                              ? (isDarkMode ? 'border-red-500 bg-red-900/30 text-red-200' : 'border-red-300 bg-red-50 text-gray-900') 
                              : (isDarkMode ? 'border-gray-600 bg-gray-700 text-gray-100' : 'border-gray-200 bg-white text-gray-900')
                            }`}
                            placeholder="0"
                          />
                        </div>
                      </div>
                      
                      {/* Center: Calculation Flow (when applicable) */}
                      {(item.quantity > 0 && item.rate > 0 && item.tt?.value > 0) && (
                        <div className="flex items-center gap-2 text-xs">
                          <span className={isDarkMode ? 'text-gray-400' : 'text-gray-500'}>→</span>
                          <div className={`flex items-center gap-1 px-2 py-1 rounded ${isDarkMode ? 'bg-blue-900/40 text-blue-300' : 'bg-blue-100 text-blue-700'}`}>
                            <span>₹{(item.quantity * item.rate).toFixed(2)}</span>
                          </div>
                          {item.discount > 0 && (
                            <>
                              <span className={isDarkMode ? 'text-gray-500' : 'text-gray-400'}>→</span>
                              <div className={`px-2 py-1 rounded ${isDarkMode ? 'bg-yellow-900/40 text-yellow-300' : 'bg-yellow-100 text-yellow-700'}`}>-{item.discount}%</div>
                            </>
                          )}
                          <span className={isDarkMode ? 'text-gray-500' : 'text-gray-400'}>→</span>
                          <div className={`px-2 py-1 rounded text-xs ${isDarkMode ? 'bg-purple-900/40 text-purple-300' : 'bg-purple-100 text-purple-700'}`}>
                            Base: ₹{(() => {
                              const amountAfterDiscount = (item.quantity * item.rate) * (1 - (item.discount || 0) / 100);
                              let itemTaxRate = parseFloat(item.integratedTax) || 0;
                              
                              // If no integratedTax, try to extract from item name (e.g., "MISC 12%")
                              if (!itemTaxRate && item.Name) {
                                const taxMatch = item.Name.match(/(\d+)%/);
                                if (taxMatch) {
                                  itemTaxRate = parseFloat(taxMatch[1]);
                                }
                              }
                              
                              // Default fallback
                              if (!itemTaxRate) itemTaxRate = 18;
                              
                              const baseAmount = amountAfterDiscount / (100 + itemTaxRate) * 100;
                              return baseAmount.toFixed(0);
                            })()}
                          </div>
                        </div>
                      )}
                      
                      {/* Right: TT Result */}
                      <div className="flex items-center gap-2">
                        {item.tt?.value > 0 ? (
                          <>
                            <div className={`text-xs ${isDarkMode ? 'text-gray-400' : 'text-gray-600'}`}>Result:</div>
                            <div className={`border px-3 py-1 rounded font-bold ${isDarkMode ? 'bg-gray-700 border-indigo-600 text-indigo-300' : 'bg-white border-indigo-200 text-indigo-900'}`}>
                              ₹{(() => {
                                // If we have a calculated amount, use it
                                if (item.tt.calculatedAmount) {
                                  return item.tt.calculatedAmount.toFixed(2);
                                }
                                // Otherwise, provide immediate fallback calculation
                                if (item.quantity > 0 && item.rate > 0 && item.tt.value > 0) {
                                  const amountAfterDiscount = (item.quantity * item.rate) * (1 - (item.discount || 0) / 100);
                                  
                                  let itemGST = parseFloat(item.integratedTax) || 0;
                                  
                                  // If no integratedTax, try to extract from item name (e.g., "MISC 12%")
                                  if (!itemGST && item.Name) {
                                    const taxMatch = item.Name.match(/(\d+)%/);
                                    if (taxMatch) {
                                      itemGST = parseFloat(taxMatch[1]);
                                    }
                                  }
                                  
                                  // Default fallback
                                  if (!itemGST) itemGST = 18;
                                  
                                  const baseValue = amountAfterDiscount / (100 + itemGST) * 100;
                                  const ttAmount = item.tt.type === 'percentage' 
                                    ? (baseValue * (item.tt.value / 100))
                                    : Math.min(item.tt.value, baseValue);
                                  return Math.min(ttAmount, baseValue).toFixed(2);
                                }
                                return '0.00';
                              })()}
                            </div>
                            {(() => {
                              // Check if we have a valid calculation (either from async or fallback)
                              const hasValidResult = item.tt.calculatedAmount > 0 || 
                                (item.quantity > 0 && item.rate > 0 && item.tt.value > 0);
                              
                              // Only show "Calculating..." if we don't have a valid result
                              return !hasValidResult && item.tt.value > 0 && (
                                <div className={`text-xs flex items-center gap-1 ${isDarkMode ? 'text-blue-400' : 'text-blue-600'}`}>
                                  <svg className="w-3 h-3" fill="currentColor" viewBox="0 0 20 20">
                                    <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm1-12a1 1 0 10-2 0v4a1 1 0 00.293.707l2.828 2.829a1 1 0 101.415-1.415L11 9.586V6z" clipRule="evenodd"/>
                                  </svg>
                                  Calculating...
                                </div>
                              );
                            })()}
                            {!item.tt.calculatedAmount && item.tt.value > 0 && (!item.quantity || !item.rate || item.quantity <= 0 || item.rate <= 0) && (
                              <div className={`text-xs flex items-center gap-1 ${isDarkMode ? 'text-amber-400' : 'text-amber-600'}`}>
                                <svg className="w-3 h-3" fill="currentColor" viewBox="0 0 20 20">
                                  <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd"/>
                                </svg>
                                Enter quantity & rate
                              </div>
                            )}
                          </>
                        ) : (
                          <div className={`text-xs ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>Enter TT value</div>
                        )}
                      </div>
                    </div>
                    
                    {/* Validation Messages - Compact */}
                    {((item.tt?.type === 'percentage' && (item.tt?.value || 0) > 100) ||
                      (item.tt?.type === 'fixed' && (item.amountAfterDiscount > 0) && (item.tt?.value || 0) > ((item.amountAfterDiscount || 0) / (100 + (item.integratedTax || 18)) * 100)) ||
                      (item.tt?.value > 0 && !item.itemId)) && (
                      <div className={`mt-2 pt-2 border-t ${isDarkMode ? 'border-indigo-700' : 'border-indigo-200'}`}>
                        <div className="flex items-center gap-2 text-xs">
                          {item.tt?.type === 'percentage' && (item.tt?.value || 0) > 100 && (
                            <div className={`flex items-center gap-1 px-2 py-1 rounded ${isDarkMode ? 'text-red-400 bg-red-900/30' : 'text-red-600 bg-red-50'}`}>
                              <svg className="w-3 h-3" fill="currentColor" viewBox="0 0 20 20">
                                <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z" clipRule="evenodd"/>
                              </svg>
                              Max 100%
                            </div>
                          )}
                          
                          {item.tt?.type === 'fixed' && (item.amountAfterDiscount > 0) && 
                           (item.tt?.value || 0) > ((item.amountAfterDiscount || 0) / (100 + (item.integratedTax || 18)) * 100) && (
                            <div className={`flex items-center gap-1 px-2 py-1 rounded ${isDarkMode ? 'text-red-400 bg-red-900/30' : 'text-red-600 bg-red-50'}`}>
                              <svg className="w-3 h-3" fill="currentColor" viewBox="0 0 20 20">
                                <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z" clipRule="evenodd"/>
                              </svg>
                              Exceeds base value
                            </div>
                          )}
                          
                          {item.tt?.value > 0 && !item.itemId && (
                            <div className={`flex items-center gap-1 px-2 py-1 rounded ${isDarkMode ? 'text-amber-400 bg-amber-900/30' : 'text-amber-700 bg-amber-50'}`}>
                              <svg className="w-3 h-3" fill="currentColor" viewBox="0 0 20 20">
                                <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd"/>
                              </svg>
                              Select item for accurate calculation
                            </div>
                          )}
                        </div>
                      </div>
                    )}
                  </div>
                </div>
                )}

                {/* Description Field - Only show when item's print type is not 'item' */}
                {item.printType !== 'item' && (
                  <div className="mt-4">
                    <label className={`block text-sm font-medium mb-1 ${
                      isDarkMode ? 'text-gray-200' : 'text-gray-700'
                    }`}>
                      Description
                    </label>
                    <textarea
                      value={item.description || ''}
                      onChange={(e) => handleItemChange(index, 'description', e.target.value)}
                      className={`w-full p-2 border rounded-md focus:outline-none focus:ring-2 ${
                        isDarkMode 
                          ? 'bg-gray-700 border-gray-600 text-gray-100 placeholder-gray-400 focus:ring-blue-500' 
                          : 'bg-white border-gray-300 text-gray-900 placeholder-gray-500 focus:ring-blue-500'
                      }`}
                      rows="2"
                      placeholder="Enter item description..."
                    />
                  </div>
                )}
              </div>
          ))}
          </div>
          <div className="mt-4 flex gap-2">
          <button
              ref={addItemButtonRef}
            type="button"
              onClick={(e) => {e.preventDefault();addItem(e)}}
              className={`px-4 py-2 rounded transition duration-150 ${
                isDarkMode 
                  ? 'bg-green-600 hover:bg-green-700 text-white' 
                  : 'bg-green-500 hover:bg-green-600 text-white'
              }`}
          >
            Add Item
          </button>
          <button
            type="button"
            onClick={pasteItem}
            disabled={!copiedItem}
            className={`px-4 py-2 rounded flex items-center gap-2 transition duration-150 disabled:opacity-40 ${
              isDarkMode 
                ? 'border border-teal-700 text-teal-300 hover:bg-teal-900/30' 
                : 'border border-teal-300 text-teal-700 hover:bg-teal-50'
            }`}
          >
            <FaPaste className="text-sm" /> Paste Item
          </button>
          <button
            type="button"
            onClick={() => setShowProformaImportModal(true)}
            className={`px-4 py-2 rounded flex items-center gap-2 transition duration-150 ${
              isDarkMode 
                ? 'bg-blue-600 hover:bg-blue-700 text-white' 
                : 'bg-blue-500 hover:bg-blue-600 text-white'
            }`}
          >
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M9 19l3 3m0 0l3-3m-3 3V10" />
            </svg>
            Import from Proforma
          </button>
          </div>
        </div>

        {/* Payment & Sales Information Section Header */}
        <div className={`flex items-center space-x-3 mb-4 pb-3 border-b ${isDarkMode ? 'border-gray-700' : 'border-gray-200'}`}>
          <div className={`p-2 rounded-lg ${isDarkMode ? 'bg-yellow-900/30' : 'bg-yellow-50'}`}>
            <svg className={`w-5 h-5 ${isDarkMode ? 'text-yellow-400' : 'text-yellow-600'}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 9V7a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2m2 4h10a2 2 0 002-2v-6a2 2 0 00-2-2H9a2 2 0 00-2 2v6a2 2 0 002 2zm7-5a2 2 0 11-4 0 2 2 0 014 0z" />
            </svg>
          </div>
          <div>
            <h3 className={`text-base sm:text-lg font-semibold ${isDarkMode ? 'text-gray-100' : 'text-gray-900'}`}>
              Payment & Sales Information
            </h3>
            <p className={`text-xs ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
              Configure sales mode, payment methods and order references
            </p>
          </div>
        </div>

        {/* Mode of Sales, Manual DO Number & PO Number - Mobile Responsive */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-3 sm:gap-4 mb-4">
        <div>
            <label className={`block text-sm font-medium mb-1 ${isDarkMode ? 'text-gray-200' : 'text-gray-700'}`}>
              Mode of Sales
            </label>
            <Select
              value={{ value: form.modeOfSales, label: form.modeOfSales }}
              onChange={(selectedOption) => {
                const newModeOfSales = selectedOption.value;
                let updatedPaymentModes = [...form.modeOfPayment];
                
                // If Credit is selected and 'credit' is not already in payment modes, add it
                if (newModeOfSales === 'Credit' && !updatedPaymentModes.some(mode => mode.toLowerCase() === 'credit')) {
                  updatedPaymentModes.push('credit');
                  setPaymentModesInput(updatedPaymentModes.join(', '));
                }
                
                setForm({ 
                  ...form, 
                  modeOfSales: newModeOfSales,
                  modeOfPayment: updatedPaymentModes
                });
              }}
              options={[
                { value: 'Paid Instantly', label: 'Paid Instantly' },
                { value: 'Credit', label: 'Credit' },
                { value: 'Full Advance', label: 'Full Advance' },
                { value: 'Partial Advance', label: 'Partial Advance' },
                { value: 'Breakage Claim', label: 'Breakage Claim' }
              ]}
              className="text-sm"
              placeholder="Select Mode of Sales"
              isSearchable={false}
              styles={getSelectStyles(isDarkMode)}
              menuPortalTarget={document.body}
              menuPosition="fixed"
            />
          </div>
          
          <div>
            <label className={`block text-sm font-medium mb-1 ${isDarkMode ? 'text-gray-200' : 'text-gray-700'}`}>
              Manual DO Number
            </label>
            <input
              type="text"
              value={form.Manual_dO_Number}
              onChange={(e) => handleFormFieldChange('Manual_dO_Number', e.target.value)}
              className={`w-full p-2.5 sm:p-2 border rounded text-sm min-h-[44px] sm:min-h-0 focus:ring focus:ring-blue-200 ${isDarkMode ? 'bg-gray-700 border-gray-600 text-gray-100 placeholder-gray-400' : 'bg-white border-gray-300 text-gray-900'}`}
              placeholder="Enter manual DO number"
            />
        </div>

            <div>
              <label className={`block text-sm font-medium mb-1 ${isDarkMode ? 'text-gray-200' : 'text-gray-700'}`}>
              PO Number
              </label>
              <input
                type="text"
              value={form.poNumber}
              onChange={(e) => setForm({ ...form, poNumber: e.target.value })}
                className={`w-full p-2.5 sm:p-2 border rounded text-sm min-h-[44px] sm:min-h-0 focus:ring focus:ring-blue-200 ${isDarkMode ? 'bg-gray-700 border-gray-600 text-gray-100 placeholder-gray-400' : 'bg-white border-gray-300 text-gray-900'}`}
              placeholder="Enter PO number"
              />
            </div>
            </div>

        {/* Credit Reference and Credit Period - Only show when mode of sales is Credit - Mobile Responsive */}
        {form.modeOfSales === 'Credit' && (
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-4 mb-4">
          <div>
            <label className={`block text-sm font-medium mb-1 ${isDarkMode ? 'text-gray-200' : 'text-gray-700'}`}>
                Credit Reference <span className="text-red-500">*</span>
            </label>
            <input
              type="text"
                value={form.creditReference}
                onChange={(e) => setForm({ ...form, creditReference: e.target.value })}
              className={`w-full p-2.5 sm:p-2 border rounded text-sm min-h-[44px] sm:min-h-0 focus:ring focus:ring-blue-200 ${isDarkMode ? 'bg-gray-700 border-gray-600 text-gray-100 placeholder-gray-400' : 'bg-white border-gray-300 text-gray-900'}`}
                placeholder="Enter credit reference"
            />
          </div>
          
          <div>
            <label className={`block text-sm font-medium mb-1 ${isDarkMode ? 'text-gray-200' : 'text-gray-700'}`}>
                Credit Period (Days) <span className="text-red-500">*</span>
            </label>
            <input
                type="number"
                min="0"
                value={form.creditPeriod}
                onChange={(e) => setForm({ ...form, creditPeriod: parseInt(e.target.value) || 0 })}
              className={`w-full p-2.5 sm:p-2 border rounded text-sm min-h-[44px] sm:min-h-0 focus:ring focus:ring-blue-200 ${isDarkMode ? 'bg-gray-700 border-gray-600 text-gray-100 placeholder-gray-400' : 'bg-white border-gray-300 text-gray-900'}`}
                placeholder="Enter credit period in days"
            />
          </div>
        </div>
        )}

        {/* Claim Payment From - Always visible with improved design - Mobile Responsive */}
        <div className={`bg-gradient-to-r p-3 sm:p-4 rounded-lg border mb-4 ${isDarkMode ? 'from-purple-900/30 to-blue-900/30 border-purple-700' : 'from-purple-50 to-blue-50 border-purple-200'}`}>
          <h3 className={`text-sm font-semibold mb-3 flex items-center ${isDarkMode ? 'text-purple-300' : 'text-purple-800'}`}>
            <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
            </svg>
            Claim Payment From (Optional)
          </h3>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-4">
            <div>
              <label className={`block text-sm font-medium mb-1 ${isDarkMode ? 'text-purple-200' : 'text-gray-700'}`}>
                Contact Person Name
              </label>
              <input
                type="text"
                value={form.claimPaymentFrom?.name || ''}
                onChange={(e) => {
                  const value = e.target.value;
                  if (value.length <= 100) {
                    setForm({ 
                      ...form, 
                      claimPaymentFrom: {
                        ...form.claimPaymentFrom,
                        name: value 
                      }
                    });
                  }
                }}
                className={`w-full p-2.5 sm:p-2 border rounded text-sm min-h-[44px] sm:min-h-0 focus:ring-2 focus:ring-purple-500 focus:border-purple-500 transition-all duration-200 ${isDarkMode ? 'bg-gray-700 border-gray-600 text-gray-100 placeholder-gray-400' : 'bg-white border-gray-300 text-gray-900'}`}
                placeholder="Enter contact person name"
                maxLength="100"
              />
              <div className={`text-xs mt-1 ${isDarkMode ? 'text-purple-300' : 'text-gray-500'}`}>
                {form.claimPaymentFrom?.name?.length || 0}/100 characters
              </div>
            </div>
            
            <div>
              <label className={`block text-sm font-medium mb-1 ${isDarkMode ? 'text-purple-200' : 'text-gray-700'}`}>
                Contact Number
              </label>
              <input
                type="text"
                value={form.claimPaymentFrom?.number || ''}
                onChange={(e) => {
                  const value = e.target.value;
                  // Allow only numeric digits
                  if (/^\d*$/.test(value) && value.length <= 10) {
                    setForm({ 
                      ...form, 
                      claimPaymentFrom: {
                        ...form.claimPaymentFrom,
                        number: value 
                      }
                    });
                  }
                }}
                className={`w-full p-2.5 sm:p-2 border rounded text-sm min-h-[44px] sm:min-h-0 focus:ring-2 focus:ring-purple-500 focus:border-purple-500 transition-all duration-200 ${isDarkMode ? 'bg-gray-700 border-gray-600 text-gray-100 placeholder-gray-400' : 'bg-white border-gray-300 text-gray-900'}`}
                placeholder="10 digit phone number"
                maxLength="10"
              />
              <div className={`text-xs mt-1 ${isDarkMode ? 'text-purple-300' : 'text-gray-500'}`}>
                {form.claimPaymentFrom?.number?.length || 0}/10 digits
                {form.claimPaymentFrom?.number && (() => {
                  const isValidLength = form.claimPaymentFrom.number.length === 10;
                  const isNumeric = /^\d+$/.test(form.claimPaymentFrom.number);
                  
                  if (form.claimPaymentFrom.number.length > 0) {
                    if (!isNumeric) {
                      return (
                        <span className="text-red-500 block">
                          Only numeric digits allowed
                        </span>
                      );
                    }
                    if (!isValidLength) {
                      return (
                        <span className="text-orange-500 block">
                          Must be exactly 10 digits
                        </span>
                      );
                    }
                    return (
                      <span className="text-green-500 block">
                        ✓ Valid phone number
                      </span>
                    );
                  }
                  return null;
                })()}
              </div>
            </div>
          </div>
          <div className={`mt-2 text-xs p-2 rounded border-l-4 ${isDarkMode ? 'text-gray-300 bg-gray-700 border-purple-600' : 'text-gray-600 bg-white border-purple-300'}`}>
            <strong>💡 Optional:</strong> Add contact person name and 10-digit phone number for payment claims or follow-ups. Leave blank if not applicable.
          </div>
        </div>

        {/* Mode of Payment & Salesman - Mobile Responsive */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3 sm:gap-4 mb-4">
          <div>
            <label className={`block text-sm font-medium mb-1 ${isDarkMode ? 'text-gray-200' : 'text-gray-700'}`}>
              Mode of Payment
            </label>
            <Select
              isMulti
              value={
                Array.isArray(form.modeOfPayment)
                  ? form.modeOfPayment.map(mode => {
                      const existingOption = paymentModeOptions.find(option => option.value === mode);
                      return existingOption || { value: mode, label: mode };
                    })
                  : []
              }
              onChange={handlePaymentModesChange}
              options={paymentModeOptions}
              placeholder="Select payment modes"
              className="basic-multi-select text-sm"
              styles={getSelectStyles(isDarkMode)}
              menuPortalTarget={document.body}
              menuPosition="fixed"
            />
          </div>
          
          <div>
            <label className={`block text-sm font-medium mb-1 ${isDarkMode ? 'text-gray-200' : 'text-gray-700'}`}>
              Or enter comma-separated payment modes
            </label>
            <input
              type="text"
              className={`w-full p-2.5 sm:p-2 border rounded text-sm min-h-[44px] sm:min-h-0 ${isDarkMode ? 'bg-gray-700 border-gray-600 text-gray-100 placeholder-gray-400' : 'bg-white border-gray-300 text-gray-900'}`}
              placeholder="Or enter comma-separated payment modes"
              value={paymentModesInput}
              onChange={handlePaymentModeInput}
            />
          </div>
          
          <div>
            <label className={`block text-sm font-medium mb-1 ${isDarkMode ? 'text-gray-200' : 'text-gray-700'}`}>
              Salesperson
            </label>
            <Select
              isMulti
              value={form.salesman.map(id => {
                const salesman = employees.find(emp => emp._id === id);
                return salesman ? { value: salesman._id, label: salesman.name } : null;
              }).filter(Boolean)}
              onChange={handleSalesmanChange}
              options={employees.map(emp => ({
                value: emp._id,
                label: emp.name
              }))}
              className="basic-multi-select text-sm"
              classNamePrefix="select"
              placeholder="Select salesperson"
              styles={getSelectStyles(isDarkMode)}
              menuPortalTarget={document.body}
              menuPosition="fixed"
            />
          </div>
        </div>

        {/* Site Address & Narration - Mobile Responsive */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3 sm:gap-4 mb-4">
          <div>
            <label className={`block text-sm font-medium mb-1 ${isDarkMode ? 'text-gray-200' : 'text-gray-700'}`}>
              Site Address
            </label>
            <textarea
              value={form.siteAddress}
              onChange={(e) => setForm({ ...form, siteAddress: e.target.value })}
              className={`w-full p-2.5 sm:p-2 border rounded text-sm min-h-[100px] focus:ring focus:ring-blue-200 ${isDarkMode ? 'bg-gray-700 border-gray-600 text-gray-100 placeholder-gray-400' : 'bg-white border-gray-300 text-gray-900'}`}
              rows="3"
              placeholder="Enter site address"
            />
          </div>
          
          <div>
            <label className={`block text-sm font-medium mb-1 ${isDarkMode ? 'text-gray-200' : 'text-gray-700'}`}>
              Narration
            </label>
            <textarea
              value={form.Narration}
              onChange={(e) => setForm({ ...form, Narration: e.target.value })}
              className={`w-full p-2.5 sm:p-2 border rounded text-sm min-h-[100px] focus:ring focus:ring-blue-200 ${isDarkMode ? 'bg-gray-700 border-gray-600 text-gray-100 placeholder-gray-400' : 'bg-white border-gray-300 text-gray-900'}`}
              rows="3"
              placeholder="Enter narration"
            />
          </div>
        </div>

        {/* Charges Section - Mobile Responsive */}
        <div className="grid grid-cols-2 sm:grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4 mb-4">
          <div>
            <label className={`block text-sm font-semibold mb-1 ${isDarkMode ? 'text-gray-200' : 'text-gray-700'}`}>
              P&F Charges
            </label>
            <input
              type="number"
              step="0.01"
              name="packingAndForwarding"
              value={form.packingAndForwarding}
              onChange={(e) => handleChargeChange('packingAndForwarding', e.target.value)}
              onKeyDown={handleKeyDown}
              className={`w-full p-2.5 sm:p-2 text-sm border rounded min-h-[44px] sm:min-h-0 focus:outline-none focus:ring focus:ring-blue-100 ${isDarkMode ? 'bg-gray-700 border-gray-600 text-gray-100' : 'bg-white border-gray-300 text-gray-900'}`}
            />
          </div>

          <div>
            <label className={`block text-sm font-semibold mb-1 ${isDarkMode ? 'text-gray-200' : 'text-gray-700'}`}>
              Labour
            </label>
            <input
              type="number"
              step="0.01"
              name="labour"
              value={form.labour}
              onChange={(e) => handleChargeChange('labour', e.target.value)}
              onKeyDown={handleKeyDown}
              className={`w-full p-2.5 sm:p-2 text-sm border rounded min-h-[44px] sm:min-h-0 focus:outline-none focus:ring focus:ring-blue-100 ${isDarkMode ? 'bg-gray-700 border-gray-600 text-gray-100' : 'bg-white border-gray-300 text-gray-900'}`}
            />
          </div>

          <div>
            <label className={`block text-sm font-semibold mb-1 ${isDarkMode ? 'text-gray-200' : 'text-gray-700'}`}>
              Cartage
            </label>
            <input
              type="number"
              step="0.01"
              name="cartage"
              value={form.cartage}
              onChange={(e) => handleChargeChange('cartage', e.target.value)}
              onKeyDown={handleKeyDown}
              className={`w-full p-2.5 sm:p-2 text-sm border rounded min-h-[44px] sm:min-h-0 focus:outline-none focus:ring focus:ring-blue-100 ${isDarkMode ? 'bg-gray-700 border-gray-600 text-gray-100' : 'bg-white border-gray-300 text-gray-900'}`}
            />
          </div>

          <div>
            <label className={`block text-sm font-semibold mb-1 ${isDarkMode ? 'text-gray-200' : 'text-gray-700'}`}>
              Round Off
            </label>
            <input
              type="number"
              step="0.01"
              name="roundoff"
              value={form.roundoff}
              onChange={(e) => handleChargeChange('roundoff', e.target.value)}
              onKeyDown={handleKeyDown}
              className={`w-full p-2.5 sm:p-2 text-sm border rounded min-h-[44px] sm:min-h-0 focus:outline-none focus:ring focus:ring-blue-100 ${isDarkMode ? 'bg-gray-700 border-gray-600 text-gray-100' : 'bg-white border-gray-300 text-gray-900'}`}
            />
          </div>
        </div>

        {/* Amount Summary - Mobile Responsive & Dark Mode */}
        <div className={`mb-6 rounded-lg shadow-lg overflow-hidden ${isDarkMode ? 'bg-gray-800' : 'bg-white'}`}>
          <div className={`px-4 sm:px-6 py-4 border-b ${isDarkMode ? 'bg-gray-750 border-gray-700' : 'bg-gray-50 border-gray-200'}`}>
            <h3 className={`text-lg font-semibold ${isDarkMode ? 'text-gray-100' : 'text-gray-800'}`}>Amount Summary</h3>
          </div>
          
          <div className="p-4 sm:p-6">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6 sm:gap-8">
              <div className="space-y-4">
                <div className={`p-4 rounded-lg ${isDarkMode ? 'bg-gray-750' : 'bg-gray-50'}`}>
                  <h4 className={`font-medium mb-3 ${isDarkMode ? 'text-gray-200' : 'text-gray-700'}`}>Items Summary</h4>
                  {form.items.map((item, index) => (
                    <div key={index} className="flex justify-between text-sm mb-2">
                      <span className={`truncate mr-2 ${isDarkMode ? 'text-gray-400' : 'text-gray-600'}`}>{item.Name || `Item #${index + 1}`}</span>
                      <span className={`font-medium shrink-0 ${isDarkMode ? 'text-gray-200' : 'text-gray-900'}`}>₹{item.amountAfterDiscount?.toFixed(2)}</span>
                    </div>
                  ))}
                  <div className={`border-t mt-2 pt-2 flex justify-between font-medium ${isDarkMode ? 'border-gray-600' : 'border-gray-200'}`}>
                    <span className={isDarkMode ? 'text-gray-200' : 'text-gray-900'}>Items Total:</span>
                    <span className={isDarkMode ? 'text-gray-100' : 'text-gray-900'}>₹{form.items.reduce((sum, item) => sum + (parseFloat(item.amountAfterDiscount) || 0), 0)?.toFixed(2)}</span>
                  </div>
                </div>

                <div className={`p-4 rounded-lg ${isDarkMode ? 'bg-gray-750' : 'bg-gray-50'}`}>
                  <h4 className={`font-medium mb-3 ${isDarkMode ? 'text-gray-200' : 'text-gray-700'}`}>Additional Charges</h4>
                  <div className="space-y-2">
                    {/* P&F Charges with GST */}
                    {form.packingAndForwarding > 0 && (
                      <div className="flex flex-col sm:flex-row sm:justify-between text-sm sm:items-center gap-1 sm:gap-0">
                        <div className="flex flex-wrap items-center">
                          <span className={isDarkMode ? 'text-gray-400' : 'text-gray-600'}>P&F Charges:</span>
                          <span className={`ml-2 ${isDarkMode ? 'text-gray-300' : 'text-gray-900'}`}>₹{form.packingAndForwarding?.toFixed(2)}</span>
                          <span className={`mx-2 ${isDarkMode ? 'text-gray-600' : 'text-gray-400'}`}>+</span>
                          <span className={isDarkMode ? 'text-gray-400' : 'text-gray-600'}>GST ({getChargesTaxRate(form.items).toFixed(1)}%):</span>
                          <span className={`ml-2 ${isDarkMode ? 'text-gray-300' : 'text-gray-900'}`}>₹{(form.packingAndForwardingGST)?.toFixed(2)}</span>
                        </div>
                        <span className={`font-medium ${isDarkMode ? 'text-blue-400' : 'text-blue-600'}`}>
                          = ₹{(form.packingAndForwarding + form.packingAndForwardingGST)?.toFixed(2)}
                        </span>
                      </div>
                    )}

                    {/* Labour Charges with GST */}
                    {form.labour > 0 && (
                      <div className="flex flex-col sm:flex-row sm:justify-between text-sm sm:items-center gap-1 sm:gap-0">
                        <div className="flex flex-wrap items-center">
                          <span className={isDarkMode ? 'text-gray-400' : 'text-gray-600'}>Labour Charges:</span>
                          <span className={`ml-2 ${isDarkMode ? 'text-gray-300' : 'text-gray-900'}`}>₹{form.labour?.toFixed(2)}</span>
                          <span className={`mx-2 ${isDarkMode ? 'text-gray-600' : 'text-gray-400'}`}>+</span>
                          <span className={isDarkMode ? 'text-gray-400' : 'text-gray-600'}>GST ({getChargesTaxRate(form.items).toFixed(1)}%):</span>
                          <span className={`ml-2 ${isDarkMode ? 'text-gray-300' : 'text-gray-900'}`}>₹{(form.labourGST)?.toFixed(2)}</span>
                        </div>
                        <span className={`font-medium ${isDarkMode ? 'text-blue-400' : 'text-blue-600'}`}>
                          = ₹{(form.labour + form.labourGST)?.toFixed(2)}
                        </span>
                      </div>
                    )}

                    {/* Cartage Charges with GST */}
                    {form.cartage > 0 && (
                      <div className="flex flex-col sm:flex-row sm:justify-between text-sm sm:items-center gap-1 sm:gap-0">
                        <div className="flex flex-wrap items-center">
                          <span className={isDarkMode ? 'text-gray-400' : 'text-gray-600'}>Cartage Charges:</span>
                          <span className={`ml-2 ${isDarkMode ? 'text-gray-300' : 'text-gray-900'}`}>₹{form.cartage?.toFixed(2)}</span>
                          <span className={`mx-2 ${isDarkMode ? 'text-gray-600' : 'text-gray-400'}`}>+</span>
                          <span className={isDarkMode ? 'text-gray-400' : 'text-gray-600'}>GST ({getChargesTaxRate(form.items).toFixed(1)}%):</span>
                          <span className={`ml-2 ${isDarkMode ? 'text-gray-300' : 'text-gray-900'}`}>₹{(form.cartageGST)?.toFixed(2)}</span>
                        </div>
                        <span className={`font-medium ${isDarkMode ? 'text-blue-400' : 'text-blue-600'}`}>
                          = ₹{(form.cartage + form.cartageGST)?.toFixed(2)}
                        </span>
                      </div>
                    )}

                    {/* Round Off */}
                    {form.roundoff !== 0 && (
                      <div className={`flex justify-between text-sm border-t pt-2 mt-2 ${
                        isDarkMode ? 'border-gray-600' : 'border-gray-200'
                      }`}>
                        <span className={isDarkMode ? 'text-gray-300' : 'text-gray-600'}>Round Off:</span>
                        <span className={`font-medium ${isDarkMode ? 'text-gray-100' : 'text-gray-900'}`}>
                          ₹{form.roundoff?.toFixed(2)}
                        </span>
                      </div>
                    )}
                  </div>
                </div>
              </div>
              
              <div className="flex flex-col justify-end">
                <div className={`p-4 rounded-lg ${
                  isDarkMode ? 'bg-gray-750' : 'bg-gray-50'
                }`}>
                  <div className="flex justify-between items-center mb-4">
                    <span className={`font-medium ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                      Total Amount (Items + AC):
                    </span>
                    <span className={`text-2xl font-bold ${isDarkMode ? 'text-gray-100' : 'text-gray-900'}`}>
                      ₹{(() => {
                      // Calculate total directly from items as a backup
                      let directItemsTotal = 0;
                      if (form.items && form.items.length > 0) {
                        directItemsTotal = form.items.reduce((sum, item) => {
                          if (!item) return sum;
                          const quantity = parseFloat(item.quantity) || 0;
                          const rate = parseFloat(item.rate) || 0;
                          const discount = parseFloat(item.discount) || 0;
                          const amountBeforeDiscount = quantity * rate;
                          const discountAmount = amountBeforeDiscount * (discount / 100);
                          const amountAfterDiscount = amountBeforeDiscount - discountAmount;
                          return sum + amountAfterDiscount;
                        }, 0);
                      }
                      
                      const packingAndForwarding = parseFloat(form.packingAndForwarding) || 0;
                      const labour = parseFloat(form.labour) || 0;
                      const cartage = parseFloat(form.cartage) || 0;
                      
                      const calculatedTotal = directItemsTotal + packingAndForwarding + labour + cartage;
                      
                      console.log('Display Total Amount Debug:', {
                        formTotalAmountWithoutGST: form.totalAmountWithoutGST,
                        directItemsTotal,
                        packingAndForwarding,
                        labour,
                        cartage,
                        calculatedTotal,
                        itemsCount: form.items?.length || 0,
                        itemsDetails: form.items?.map(item => ({
                          name: item.Name,
                          quantity: item.quantity,
                          rate: item.rate,
                          discount: item.discount,
                          calculated: (parseFloat(item.quantity) || 0) * (parseFloat(item.rate) || 0) * (1 - (parseFloat(item.discount) || 0) / 100)
                        }))
                      });
                      
                      // Always use calculated total since form total seems to be wrong
                      return calculatedTotal.toFixed(2);
                    })()}</span>
                  </div>
                  <div className="flex justify-between items-center">
                    <span className={`font-medium ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                      GST on AC:
                    </span>
                    <span className={`text-2xl font-bold ${isDarkMode ? 'text-green-400' : 'text-green-600'}`}>
                      ₹{(() => {
                        const packingAndForwarding = parseFloat(form.packingAndForwarding) || 0;
                        const labour = parseFloat(form.labour) || 0;
                        const cartage = parseFloat(form.cartage) || 0;
                        const totalCharges = packingAndForwarding + labour + cartage;
                        const gstOnCharges = totalCharges * 0.18;
                        
                        // Use calculated GST if form GST values are zero
                        const formGST = ((form.packingAndForwardingGST || 0) + (form.labourGST || 0) + (form.cartageGST || 0));
                        const displayValue = formGST > 0 ? formGST : gstOnCharges;
                        
                        return displayValue.toFixed(2);
                      })()}
                    </span>
                  </div>
                  <div className="flex justify-between items-center">
                    <span className={`font-medium ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                      Round Off:
                    </span>
                    <span className={`text-2xl font-bold ${isDarkMode ? 'text-green-400' : 'text-green-600'}`}>
                      ₹{form.roundoff?.toFixed(2)}
                    </span>
                  </div>
                  
                  {/* TT Amount Summary - Show if any TT amounts exist */}
                  {(() => {
                    const totalTT = form.items?.reduce((sum, item) => sum + (parseFloat(item.tt?.calculatedAmount) || 0), 0) || 0;
                    const hasTTItems = form.items?.some(item => item.tt?.value > 0);
                    
                    return (totalTT > 0 || hasTTItems) ? (
                      <div className={`rounded p-3 my-3 border ${
                        isDarkMode 
                          ? 'bg-purple-900/30 border-purple-700' 
                          : 'bg-purple-50 border-purple-200'
                      }`}>
                        <div className="flex justify-between items-center mb-2">
                          <div>
                            <span className={`font-medium flex items-center gap-2 ${
                              isDarkMode ? 'text-purple-300' : 'text-purple-800'
                            }`}>
                              <svg className={`w-4 h-4 ${
                                isDarkMode ? 'text-purple-400' : 'text-purple-600'
                              }`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                              </svg>
                              TT Amount Summary
                            </span>
                          </div>
                          <div className="text-right">
                            <span className={`text-2xl font-bold ${
                              isDarkMode ? 'text-purple-400' : 'text-purple-600'
                            }`}>
                              ₹{totalTT?.toFixed(2)}
                            </span>
                            <div className={`text-xs mt-1 ${
                              isDarkMode ? 'text-purple-400' : 'text-purple-500'
                            }`}>
                              {form.items?.filter(item => item.tt?.value > 0).length || 0} item(s) with TT
                            </div>
                          </div>
                        </div>
                        
                        {/* Item-wise TT breakdown */}
                        <div className="space-y-1 text-xs max-h-32 overflow-y-auto">
                          {form.items?.filter(item => item.tt?.value > 0).map((item, index) => (
                            <div key={index} className={`flex justify-between items-center py-1 border-b last:border-b-0 ${
                              isDarkMode ? 'border-purple-700' : 'border-purple-100'
                            }`}>
                              <div className="flex-1">
                                <span className={`font-medium ${isDarkMode ? 'text-purple-300' : 'text-purple-700'}`}>
                                  {item.Name || `Item #${index + 1}`}
                                </span>
                                <span className={`ml-2 ${isDarkMode ? 'text-purple-400' : 'text-purple-600'}`}>
                                  ({item.tt.type === 'percentage' ? `${item.tt.value}%` : `₹${item.tt.value}`})
                                </span>
                              </div>
                              <span className={`font-medium ${isDarkMode ? 'text-purple-400' : 'text-purple-600'}`}>
                                ₹{item.tt.calculatedAmount?.toFixed(2) || '0.00'}
                              </span>
                            </div>
                          ))}
                          
                          {/* Show message if no TT items but TT section is visible */}
                          {(!form.items?.some(item => item.tt?.value > 0)) && (
                            <div className={`text-center py-2 ${isDarkMode ? 'text-purple-400' : 'text-purple-500'}`}>
                              No TT configured for any items
                            </div>
                          )}
                        </div>
                        
                        <div className={`text-xs mt-2 italic ${isDarkMode ? 'text-purple-400' : 'text-purple-600'}`}>
                          TT calculated from base amounts (excluding GST)
                          {!form.reference && ' • Select reference to apply TT commission'}
                        </div>
                      </div>
                    ) : null;
                  })()}
                  <div className={`border-t mt-4 pt-4 ${isDarkMode ? 'border-gray-600' : 'border-gray-200'}`}>
                    {/* Show TT Amount in main totals if exists */}
                    {(() => {
                      const totalTT = form.items?.reduce((sum, item) => sum + (parseFloat(item.tt?.calculatedAmount) || 0), 0) || 0;
                      return totalTT > 0 ? (
                        <div className={`flex justify-between items-center mb-3 pb-3 border-b ${
                          isDarkMode ? 'border-purple-700' : 'border-purple-100'
                        }`}>
                          <span className={`font-semibold ${isDarkMode ? 'text-purple-300' : 'text-purple-700'}`}>
                            Total TT Amount:
                          </span>
                          <span className={`text-xl font-bold ${isDarkMode ? 'text-purple-400' : 'text-purple-600'}`}>
                            ₹{totalTT?.toFixed(2)}
                          </span>
                        </div>
                      ) : null;
                    })()}
                    
                    <div className="flex justify-between items-center">
                      <span className={`font-semibold ${isDarkMode ? 'text-gray-100' : 'text-gray-900'}`}>
                        Final Amount (with GST):
                      </span>
                      <span className={`text-2xl font-bold ${isDarkMode ? 'text-blue-400' : 'text-blue-600'}`}>
                        ₹{(() => {
                        // Calculate final amount directly as backup
                        let directItemsTotal = 0;
                        if (form.items && form.items.length > 0) {
                          directItemsTotal = form.items.reduce((sum, item) => {
                            if (!item) return sum;
                            const quantity = parseFloat(item.quantity) || 0;
                            const rate = parseFloat(item.rate) || 0;
                            const discount = parseFloat(item.discount) || 0;
                            const amountBeforeDiscount = quantity * rate;
                            const discountAmount = amountBeforeDiscount * (discount / 100);
                            const amountAfterDiscount = amountBeforeDiscount - discountAmount;
                            return sum + amountAfterDiscount;
                          }, 0);
                        }
                        
                        const packingAndForwarding = parseFloat(form.packingAndForwarding) || 0;
                        const labour = parseFloat(form.labour) || 0;
                        const cartage = parseFloat(form.cartage) || 0;
                        const roundoff = parseFloat(form.roundoff) || 0;
                        
                        const totalWithoutGST = directItemsTotal + packingAndForwarding + labour + cartage;
                        const gstOnCharges = (packingAndForwarding + labour + cartage) * 0.18;
                        
                        // Calculate TT total (for display only, not added to final amount)
                        const totalTT = form.items?.reduce((sum, item) => 
                          sum + (parseFloat(item.tt?.calculatedAmount) || 0), 0) || 0;
                        
                        const finalAmount = totalWithoutGST + gstOnCharges + roundoff;
                        
                        console.log('Final Amount Calculation:', {
                          directItemsTotal,
                          totalWithoutGST,
                          gstOnCharges,
                          totalTT,
                          roundoff,
                          finalAmount
                        });
                        
                        // Always use calculated final amount
                        return finalAmount.toFixed(2);
                      })()}</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Action Buttons */}
        <div className="flex flex-col sm:flex-row gap-3 sm:gap-4 mt-6">
          {/* Submit Button */}
          <button
            type="submit"
            disabled={submitting}
            className={`flex-1 sm:flex-none sm:px-8 py-3 rounded-lg font-semibold transition-all duration-200 flex items-center justify-center gap-2 min-h-[48px] sm:min-h-0 ${
              submitting 
                ? 'bg-gray-400 cursor-not-allowed' 
                : isDarkMode
                ? 'bg-green-600 hover:bg-green-700 text-white shadow-lg hover:shadow-xl transform hover:scale-105'
                : 'bg-gradient-to-r from-green-500 to-green-600 hover:from-green-600 hover:to-green-700 text-white shadow-lg hover:shadow-xl transform hover:scale-105'
            }`}
          >
            <FaCheck className="text-lg" />
            {submitting ? (
              <>
                <span className="animate-spin">⏳</span>
                {isEditing ? 'Updating...' : 'Creating...'}
              </>
            ) : (
              isEditing ? 'Update Delivery Order' : 'Create Delivery Order'
            )}
          </button>
          
          {isEditing && (
            <button
              type="button"
              onClick={resetForm}
              className={`flex-1 sm:flex-none sm:px-8 py-3 rounded-lg font-semibold transition-colors min-h-[48px] sm:min-h-0 ${
                isDarkMode 
                  ? 'bg-gray-600 hover:bg-gray-500 text-white' 
                  : 'bg-gray-500 hover:bg-gray-600 text-white'
              }`}
            >
              Cancel Edit
            </button>
          )}
        </div>

        {/* Auto-save Toast Notification - Fixed Position */}
        {(draftManager.autoSaving || showSaveNotification) && (
          <div className="fixed top-20 right-4 z-50 animate-fade-in">
            <div 
              className={`rounded-lg shadow-2xl px-4 py-3 min-w-[250px] border-l-4 cursor-pointer transition-transform hover:scale-105 ${
                draftManager.autoSaving 
                  ? (isDarkMode ? 'bg-blue-900/30 border-blue-400' : 'bg-blue-50 border-blue-500')
                  : (isDarkMode ? 'bg-green-900/30 border-green-400' : 'bg-green-50 border-green-500')
              }`}
              onClick={() => !draftManager.autoSaving && setShowSaveNotification(false)}
              title={!draftManager.autoSaving ? 'Click to dismiss' : ''}
            >
              <div className="flex items-center gap-3">
                <div className={`text-2xl ${draftManager.autoSaving ? 'animate-spin' : ''}`}>
                  {draftManager.autoSaving ? '🔄' : '✅'}
                </div>
                <div className="flex-1">
                  <p className={`font-semibold text-sm ${
                    draftManager.autoSaving 
                      ? (isDarkMode ? 'text-blue-200' : 'text-blue-800')
                      : (isDarkMode ? 'text-green-200' : 'text-green-800')
                  }`}>
                    {draftManager.autoSaving ? 'Auto-saving...' : 'Draft Saved'}
                  </p>
                  {draftManager.lastSaved && !draftManager.autoSaving && (
                    <p className={`text-xs mt-0.5 ${
                      isDarkMode ? 'text-gray-400' : 'text-gray-600'
                    }`}>
                      {draftManager.lastSaved.toLocaleTimeString('en-IN')}
                    </p>
                  )}
                </div>
                {!draftManager.autoSaving && (
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      setShowSaveNotification(false);
                    }}
                    className={`ml-2 text-lg leading-none ${
                      isDarkMode ? 'text-gray-400 hover:text-gray-200' : 'text-gray-500 hover:text-gray-700'
                    }`}
                    title="Dismiss"
                  >
                    ×
                  </button>
                )}
              </div>
            </div>
          </div>
        )}
      </form>

      {/* Orders List with Enhanced Controls */}
      <div className={`mt-6 sm:mt-8 rounded-lg shadow-lg p-4 sm:p-6 ${
        isDarkMode ? 'bg-gray-800' : 'bg-white'
      }`}>
        <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center mb-6 gap-4">
          <h3 className={`text-xl font-bold ${isDarkMode ? 'text-gray-100' : 'text-gray-900'}`}>
            Recent Orders
          </h3>
          <div className="flex flex-col sm:flex-row items-start sm:items-center space-y-2 sm:space-y-0 sm:space-x-4 w-full sm:w-auto">
            <button
              onClick={() => setShowFilters(prev => !prev)}
              className={`flex items-center px-3 py-2 rounded w-full sm:w-auto justify-center transition-colors ${
                isDarkMode 
                  ? 'bg-blue-900/40 text-blue-300 hover:bg-blue-900/60' 
                  : 'bg-blue-100 text-blue-600 hover:bg-blue-200'
              }`}
            >
              <FaFilter className="mr-2" />
              {showFilters ? 'Hide Filters' : 'Show Filters'}
            </button>
            <div className="w-full sm:w-auto">
              <Select
                value={{ value: limit, label: `${limit} per page` }}
                onChange={(selectedOption) => setLimit(selectedOption.value)}
                options={[
                  { value: 10, label: '10 per page' },
                  { value: 25, label: '25 per page' },
                  { value: 50, label: '50 per page' }
                ]}
                styles={getSelectStyles(isDarkMode)}
                className="border rounded"
                isSearchable={false}
                menuPortalTarget={document.body}
                menuPosition="fixed"
              />
            </div>
          </div>
        </div>

        {/* Quick Search Bar - Always Visible */}
        <div className="mb-4 relative">
          <div className="flex flex-col sm:flex-row gap-4">
            <div className="flex-1 relative">
              <input
                type="text"
                value={quickSearch}
                onChange={(e) => handleMainSearchChange(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    e.preventDefault();
                    applyFilters();
                  }
                  handleSearchKeyDown(e, false);
                }}
                onFocus={() => {
                  if (quickSearch.length > 1) {
                    setShowMainSearchSuggestions(true);
                    setSelectedSuggestionIndex(-1); // Reset selection on focus
                  }
                }}
                onBlur={() => {
                  setTimeout(() => {
                    setShowMainSearchSuggestions(false);
                    setSelectedSuggestionIndex(-1);
                  }, 200);
                }}
                placeholder="Quick search by DO number, customer name, manual DO, PO number..."
                className={`w-full pl-10 pr-12 py-3 border-2 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all duration-200 min-h-[44px] sm:min-h-0 ${
                  isDarkMode 
                    ? 'bg-gray-750 border-gray-600 text-gray-100 placeholder-gray-400' 
                    : 'bg-white border-gray-300 text-gray-900 placeholder-gray-500'
                }`}
                autoComplete="off"
              />
              <FaSearch className={`absolute left-3 top-4 ${isDarkMode ? 'text-gray-400' : 'text-gray-400'}`} />
              {quickSearch && (
                <button
                  onClick={() => clearSearch(false)}
                  type="button"
                  className={`absolute right-3 top-3 p-1 rounded-full transition-colors ${
                    isDarkMode 
                      ? 'text-gray-400 hover:text-gray-200 hover:bg-gray-600' 
                      : 'text-gray-400 hover:text-gray-600 hover:bg-gray-100'
                  }`}
                  title="Clear search"
                >
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              )}
              
              {/* Search Suggestions for Quick Search */}
              {showMainSearchSuggestions && searchSuggestionsList.length > 0 && (
                <div 
                  id="main-search-dropdown"
                  className={`absolute z-20 w-full mt-1 border rounded-lg shadow-xl max-h-60 overflow-y-auto ${
                    isDarkMode 
                      ? 'bg-gray-800 border-gray-600' 
                      : 'bg-white border-gray-200'
                  }`}
                >
                  <div className={`px-4 py-2.5 text-xs font-semibold border-b sticky top-0 ${
                    isDarkMode 
                      ? 'text-blue-300 bg-gray-750 border-gray-600' 
                      : 'text-blue-700 bg-blue-50 border-blue-200'
                  }`}>
                    <svg className={`w-4 h-4 inline mr-1.5 ${isDarkMode ? 'text-blue-400' : 'text-blue-600'}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
                    </svg>
                    Quick matches from existing orders
                    <span className={`float-right ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                      Use ↑↓ arrows to navigate
                    </span>
                  </div>
                  {searchSuggestionsList.map((suggestion, index) => (
                    <button
                      key={index}
                      data-suggestion-index={index}
                      onClick={() => {
                        setQuickSearch(suggestion.text);
                        handleFilterChange('search', suggestion.text);
                        setShowMainSearchSuggestions(false);
                        setSelectedSuggestionIndex(-1);
                        setTimeout(() => applyFilters(), 100);
                      }}
                      onMouseEnter={() => setSelectedSuggestionIndex(index)}
                      className={`w-full text-left px-4 py-2.5 transition-colors duration-150 border-b last:border-b-0 ${
                        selectedSuggestionIndex === index 
                          ? isDarkMode 
                            ? 'bg-blue-900/50 border-blue-700' 
                            : 'bg-blue-100 border-blue-200'
                          : isDarkMode 
                            ? 'hover:bg-gray-700 border-gray-700' 
                            : 'hover:bg-gray-50 border-gray-100'
                      }`}
                    >
                      <div className={`text-sm font-semibold mb-0.5 ${isDarkMode ? 'text-gray-100' : 'text-gray-900'}`}>
                        {suggestion.text}
                      </div>
                      <div className={`text-xs flex items-center ${isDarkMode ? 'text-gray-400' : 'text-gray-600'}`}>
                        <span className={`inline-block px-1.5 py-0.5 rounded text-xs font-medium mr-2 ${
                          isDarkMode 
                            ? 'bg-blue-900/40 text-blue-300' 
                            : 'bg-blue-100 text-blue-700'
                        }`}>
                          {suggestion.type}
                        </span>
                        <span className="truncate">{suggestion.fullText}</span>
                      </div>
                    </button>
                  ))}
                </div>
              )}
            </div>
            
            {/* Quick Action Buttons */}
            <div className="flex flex-wrap gap-2">
              <button
                onClick={() => {
                  handleFilterChange('status', 'Pending');
                  applyFilters();
                }}
                className={`px-3 py-2 text-sm rounded transition-colors min-h-[44px] sm:min-h-0 ${
                  isDarkMode 
                    ? 'bg-yellow-900/40 text-yellow-300 hover:bg-yellow-900/60' 
                    : 'bg-yellow-100 text-yellow-700 hover:bg-yellow-200'
                }`}
              >
                Pending
              </button>
              <button
                onClick={() => {
                  handleFilterChange('status', 'Completed');
                  applyFilters();
                }}
                className={`px-3 py-2 text-sm rounded transition-colors min-h-[44px] sm:min-h-0 ${
                  isDarkMode 
                    ? 'bg-green-900/40 text-green-300 hover:bg-green-900/60' 
                    : 'bg-green-100 text-green-700 hover:bg-green-200'
                }`}
              >
                Completed
              </button>
              <button
                onClick={() => {
                  resetFilters();
                }}
                className={`px-3 py-2 text-sm rounded transition-colors min-h-[44px] sm:min-h-0 ${
                  isDarkMode 
                    ? 'bg-gray-700 text-gray-200 hover:bg-gray-600' 
                    : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                }`}
              >
                Clear All
              </button>
              
              {/* Keyboard Shortcuts Tooltip */}
              <div className="relative group">
                <button
                  className={`px-3 py-2 text-sm rounded transition-colors flex items-center min-h-[44px] sm:min-h-0 ${
                    isDarkMode 
                      ? 'bg-blue-900/40 text-blue-300 hover:bg-blue-900/60' 
                      : 'bg-blue-100 text-blue-700 hover:bg-blue-200'
                  }`}
                  title="Keyboard shortcuts"
                >
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 9l4-4 4 4m0 6l-4 4-4-4" />
                  </svg>
                  Shortcuts
                </button>
                <div className={`absolute bottom-full right-0 mb-2 w-64 p-3 text-xs rounded-lg shadow-lg opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all duration-200 z-30 ${
                  isDarkMode ? 'bg-gray-900 text-white' : 'bg-gray-900 text-white'
                }`}>
                  <div className="font-medium mb-2">⌨️ Keyboard Shortcuts</div>
                  <div className="space-y-1">
                    <div className="flex justify-between">
                      <span>Quick Search:</span>
                      <span className="font-mono bg-gray-700 px-1 rounded">/ or Ctrl+K</span>
                    </div>
                    <div className="flex justify-between">
                      <span>Toggle Filters:</span>
                      <span className="font-mono bg-gray-700 px-1 rounded">Ctrl+F</span>
                    </div>
                    <div className="flex justify-between">
                      <span>Apply Filters:</span>
                      <span className="font-mono bg-gray-700 px-1 rounded">Ctrl+Enter</span>
                    </div>
                    <div className="flex justify-between">
                      <span>Clear/Close:</span>
                      <span className="font-mono bg-gray-700 px-1 rounded">Esc</span>
                    </div>
                    <div className="flex justify-between">
                      <span>Navigate Suggestions:</span>
                      <span className="font-mono bg-gray-700 px-1 rounded">↑↓</span>
                    </div>
                  </div>
                  {/* Arrow pointing down */}
                  <div className="absolute top-full right-4 w-0 h-0 border-l-4 border-r-4 border-t-4 border-l-transparent border-r-transparent border-t-gray-900"></div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Search Performance Indicator */}
        {(loading || itemsModalLoading) && (
          <div className="mb-4 flex items-center justify-center">
            <div className={`flex items-center space-x-2 border rounded-lg px-4 py-2 ${
              isDarkMode 
                ? 'bg-blue-900/30 border-blue-700' 
                : 'bg-blue-50 border-blue-200'
            }`}>
              <div className={`animate-spin rounded-full h-4 w-4 border-b-2 ${
                isDarkMode ? 'border-blue-400' : 'border-blue-500'
              }`}></div>
              <span className={`text-sm ${isDarkMode ? 'text-blue-300' : 'text-blue-700'}`}>
                {itemsModalLoading ? 'Searching items...' : 'Loading orders...'}
              </span>
            </div>
          </div>
        )}

        {/* Enhanced Filter Panel */}
        {showFilters && (
          <div className={`mb-6 p-4 rounded-lg border ${
            isDarkMode ? 'bg-gray-750 border-gray-600' : 'bg-gray-50 border-gray-200'
          }`}>
            {/* Filter Presets */}
            {filterPresets.length > 0 && (
              <div className="mb-4">
                <label className={`block text-sm font-medium mb-2 ${
                  isDarkMode ? 'text-gray-200' : 'text-gray-700'
                }`}>
                  Saved Filter Presets
                </label>
                <div className="flex flex-wrap gap-2">
                  {filterPresets.map((preset, index) => (
                    <div key={index} className={`flex items-center border rounded-lg ${
                      isDarkMode ? 'bg-gray-700 border-gray-600' : 'bg-white border-gray-300'
                    }`}>
                      <button
                        onClick={() => loadFilterPreset(preset)}
                        className={`px-3 py-1 text-sm rounded-l-lg transition-colors ${
                          isDarkMode ? 'hover:bg-gray-600 text-gray-200' : 'hover:bg-gray-100 text-gray-900'
                        }`}
                      >
                        {preset.name}
                      </button>
                      <button
                        onClick={() => deleteFilterPreset(index)}
                        className={`px-2 py-1 rounded-r-lg border-l transition-colors ${
                          isDarkMode 
                            ? 'text-red-400 hover:bg-red-900/40 border-gray-600' 
                            : 'text-red-600 hover:bg-red-100 border-gray-300'
                        }`}
                      >
                        ×
                      </button>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Recent Filter History */}
            {filterHistory.length > 0 && (
              <div className="mb-4">
                <div className="flex justify-between items-center mb-2">
                  <label className={`text-sm font-medium ${
                    isDarkMode ? 'text-gray-200' : 'text-gray-700'
                  }`}>
                    Recent Filter History
                  </label>
                  <button
                    onClick={() => {
                      setFilterHistory([]);
                      localStorage.removeItem('deliveryOrderFilterHistory');
                    }}
                    className={`text-xs transition-colors ${
                      isDarkMode ? 'text-gray-400 hover:text-gray-200' : 'text-gray-500 hover:text-gray-700'
                    }`}
                  >
                    Clear History
                  </button>
                </div>
                <div className="flex flex-wrap gap-2 max-h-20 overflow-y-auto">
                  {filterHistory.slice(0, 5).map((historyItem, index) => (
                    <button
                      key={historyItem.id}
                      onClick={() => {
                        const { timestamp, id, ...historicalFilters } = historyItem;
                        setFilters(historicalFilters);
                        applyFilters();
                      }}
                      className={`px-3 py-1 text-xs border rounded-lg flex items-center transition-colors ${
                        isDarkMode 
                          ? 'bg-gray-700 text-gray-200 border-gray-600 hover:bg-gray-600' 
                          : 'bg-gray-100 text-gray-700 border-gray-300 hover:bg-gray-200'
                      }`}
                      title={`Applied on ${new Date(historyItem.timestamp).toLocaleString()}`}
                    >
                      <FaCalendarAlt className="mr-1" />
                      {formatDate(historyItem.timestamp)}
                      {historyItem.search && (
                        <span className={`ml-1 ${isDarkMode ? 'text-blue-400' : 'text-blue-600'}`}>
                          "{historyItem.search.slice(0, 10)}..."
                        </span>
                      )}
                    </button>
                  ))}
                </div>
              </div>
            )}

            {/* Basic Filters */}
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 mb-4">
              <div>
                <label className={`block text-sm font-medium mb-1 ${
                  isDarkMode ? 'text-gray-200' : 'text-gray-700'
                }`}>
                  Search Type
                </label>
                <Select
                  value={{ value: filters.searchType, label: filters.searchType === 'all' ? 'All Fields' : filters.searchType }}
                  onChange={(selectedOption) => handleFilterChange('searchType', selectedOption.value)}
                  options={[
                    { value: 'all', label: 'All Fields' },
                    { value: 'doNumber', label: 'DO Number' },
                    { value: 'manual', label: 'Manual DO Number' },
                    { value: 'poNumber', label: 'PO Number' },
                    { value: 'reference', label: 'Reference' },
                    { value: 'recipient', label: 'Recipient Name/ID' }
                  ]}
                  styles={getSelectStyles(isDarkMode)}
                  className="text-sm"
                  isSearchable={false}
                  menuPortalTarget={document.body}
                  menuPosition="fixed"
                />
              </div>

              <div className="relative">
                <label className={`block text-sm font-medium mb-1 ${
                  isDarkMode ? 'text-gray-200' : 'text-gray-700'
                }`}>
                  Quick Search
                </label>
                <div className="relative">
                  <input
                    type="text"
                    value={quickSearch}
                    onChange={(e) => handleMainSearchChange(e.target.value)}
                    onKeyDown={(e) => handleSearchKeyDown(e, false)}
                    onFocus={() => {
                      if (quickSearch.length > 1) {
                        setShowMainSearchSuggestions(true);
                        setSelectedSuggestionIndex(-1); // Reset selection on focus
                      }
                    }}
                    onBlur={() => {
                      setTimeout(() => {
                        setShowMainSearchSuggestions(false);
                        setSelectedSuggestionIndex(-1);
                      }, 200);
                    }}
                    placeholder={
                      filters.searchType === 'all' ? 'Search anywhere...' :
                      filters.searchType === 'recipient' ? 'Search by customer/dealer name, code, or ID...' :
                      `Search ${filters.searchType}...`
                    }
                    className={`w-full pl-10 pr-10 py-2 border-2 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all duration-200 min-h-[44px] sm:min-h-0 ${
                      isDarkMode 
                        ? 'bg-gray-700 border-gray-600 text-gray-100 placeholder-gray-400' 
                        : 'bg-white border-gray-300 text-gray-900 placeholder-gray-500'
                    }`}
                    autoComplete="off"
                  />
                  <FaSearch className={`absolute left-3 top-3 ${isDarkMode ? 'text-gray-400' : 'text-gray-400'}`} />
                  {quickSearch && (
                    <button
                      onClick={() => clearSearch(false)}
                      className={`absolute right-3 top-2 p-1 rounded-full transition-colors ${
                        isDarkMode 
                          ? 'text-gray-400 hover:text-gray-200 hover:bg-gray-600' 
                          : 'text-gray-400 hover:text-gray-600 hover:bg-gray-100'
                      }`}
                    >
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                      </svg>
                    </button>
                  )}
                  
                  {/* Search Suggestions Dropdown */}
                  {showMainSearchSuggestions && searchSuggestionsList.length > 0 && (
                    <div 
                      id="detailed-search-dropdown"
                      className={`absolute z-10 w-full mt-1 border rounded-lg shadow-lg max-h-48 overflow-y-auto ${
                        isDarkMode 
                          ? 'bg-gray-750 border-gray-600' 
                          : 'bg-white border-gray-200'
                      }`}
                    >
                      <div className={`px-3 py-2 text-xs font-medium border-b ${
                        isDarkMode 
                          ? 'text-gray-400 bg-gray-800 border-gray-600' 
                          : 'text-gray-500 bg-gray-50 border-gray-200'
                      }`}>
                        Suggestions from existing orders
                        <span className={`float-right ${isDarkMode ? 'text-gray-500' : 'text-gray-400'}`}>
                          Use ↑↓ arrows to navigate
                        </span>
                      </div>
                      {searchSuggestionsList.map((suggestion, index) => (
                        <button
                          key={index}
                          data-suggestion-index={index}
                          onClick={() => {
                            setQuickSearch(suggestion.text);
                            handleFilterChange('search', suggestion.text);
                            setShowMainSearchSuggestions(false);
                            setSelectedSuggestionIndex(-1);
                          }}
                          onMouseEnter={() => setSelectedSuggestionIndex(index)}
                          className={`w-full text-left px-3 py-2 transition-colors duration-150 ${
                            selectedSuggestionIndex === index 
                              ? isDarkMode 
                                ? 'bg-blue-900/40 ring-2 ring-blue-500 ring-inset' 
                                : 'bg-blue-100 ring-2 ring-blue-500 ring-inset'
                              : isDarkMode 
                                ? 'hover:bg-gray-700' 
                                : 'hover:bg-blue-50'
                          }`}
                        >
                          <div className={`text-sm font-medium ${isDarkMode ? 'text-gray-100' : 'text-gray-800'}`}>
                            {suggestion.text}
                          </div>
                          <div className={`text-xs ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                            {suggestion.type}: {suggestion.fullText}
                          </div>
                        </button>
                      ))}
                    </div>
                  )}
                </div>
              </div>

              <div>
                <label className={`block text-sm font-medium mb-1 ${
                  isDarkMode ? 'text-gray-200' : 'text-gray-700'
                }`}>
                  Status
                </label>
                <Select
                  value={filters.status ? { value: filters.status, label: filters.status } : null}
                  onChange={(selectedOption) => handleFilterChange('status', selectedOption ? selectedOption.value : '')}
                  options={[
                    { value: 'Pending', label: 'Pending' },
                    { value: 'Completed', label: 'Completed' },
                    { value: 'Cancelled', label: 'Cancelled' }
                  ]}
                  styles={getSelectStyles(isDarkMode)}
                  className="text-sm"
                  placeholder="All Status"
                  isClearable
                  isSearchable={false}
                  menuPortalTarget={document.body}
                  menuPosition="fixed"
                />
              </div>

              <div>
                <label className={`block text-sm font-medium mb-1 ${
                  isDarkMode ? 'text-gray-200' : 'text-gray-700'
                }`}>
                  Recipient Type
                </label>
                <Select
                  value={filters.recipientType ? { value: filters.recipientType, label: filters.recipientType } : null}
                  onChange={(selectedOption) => handleFilterChange('recipientType', selectedOption ? selectedOption.value : '')}
                  options={[
                    { value: 'Customer', label: 'Customer' },
                    { value: 'Dealer', label: 'Dealer' }
                  ]}
                  styles={getSelectStyles(isDarkMode)}
                  className="w-full"
                  placeholder="All Recipients"
                  isClearable
                  isSearchable={false}
                  menuPortalTarget={document.body}
                  menuPosition="fixed"
                />
              </div>

              <div>
                <label className={`block text-sm font-medium mb-1 ${
                  isDarkMode ? 'text-gray-200' : 'text-gray-700'
                }`}>
                  Mode of Sales
                </label>
                <Select
                  value={filters.modeOfSales ? { value: filters.modeOfSales, label: filters.modeOfSales } : null}
                  onChange={(selectedOption) => handleFilterChange('modeOfSales', selectedOption ? selectedOption.value : '')}
                  options={[
                    { value: 'Paid Instantly', label: 'Paid Instantly' },
                    { value: 'Credit', label: 'Credit' },
                    { value: 'Full Advance', label: 'Full Advance' },
                    { value: 'Partial Advance', label: 'Partial Advance' },
                    { value: 'Breakage Claim', label: 'Breakage Claim' }
                  ]}
                  styles={getSelectStyles(isDarkMode)}
                  className="w-full"
                  placeholder="All Modes"
                  isClearable
                  isSearchable={false}
                  menuPortalTarget={document.body}
                  menuPosition="fixed"
                />
              </div>

              <div>
                <label className={`block text-sm font-medium mb-1 ${
                  isDarkMode ? 'text-gray-200' : 'text-gray-700'
                }`}>
                  Delivery Order Type
                </label>
                <Select
                  value={filters.type ? { value: filters.type, label: filters.type } : null}
                  onChange={(selectedOption) => handleFilterChange('type', selectedOption ? selectedOption.value : '')}
                  options={[
                    { value: 'SANITARY', label: 'SANITARY' },
                    { value: 'TILES', label: 'TILES' }
                  ]}
                  styles={getSelectStyles(isDarkMode)}
                  className="w-full"
                  placeholder="All Types"
                  isClearable
                  isSearchable={false}
                  menuPortalTarget={document.body}
                  menuPosition="fixed"
                />
              </div>
            </div>

            {/* Date Range Filters */}
            <div className="mb-4">
              <label className={`block text-sm font-medium mb-2 ${
                isDarkMode ? 'text-gray-200' : 'text-gray-700'
              }`}>
                Date Range
              </label>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
                <Select
                  value={filters.dateRange ? { value: filters.dateRange, label: filters.dateRange } : null}
                  onChange={(selectedOption) => handleDateRangeChange(selectedOption ? selectedOption.value : '')}
                  options={[
                    { value: 'today', label: 'Today' },
                    { value: 'yesterday', label: 'Yesterday' },
                    { value: 'thisWeek', label: 'This Week' },
                    { value: 'lastWeek', label: 'Last Week' },
                    { value: 'thisMonth', label: 'This Month' },
                    { value: 'lastMonth', label: 'Last Month' },
                    { value: 'last7Days', label: 'Last 7 Days' },
                    { value: 'last30Days', label: 'Last 30 Days' },
                    { value: 'thisYear', label: 'This Year' }
                  ]}
                  styles={getSelectStyles(isDarkMode)}
                  className="w-full"
                  placeholder="Select Date Range"
                  isClearable
                  isSearchable={false}
                  menuPortalTarget={document.body}
                  menuPosition="fixed"
                />
                
                <DatePicker
                  selected={filters.startDate}
                  onChange={(date) => handleFilterChange('startDate', date)}
                  className={`w-full p-2 border rounded min-h-[44px] sm:min-h-0 ${
                    isDarkMode 
                      ? 'bg-gray-700 border-gray-600 text-gray-100' 
                      : 'bg-white border-gray-300 text-gray-900'
                  }`}
                  placeholderText="Custom Start Date"
                />
                
                <DatePicker
                  selected={filters.endDate}
                  onChange={(date) => handleFilterChange('endDate', date)}
                  className={`w-full p-2 border rounded min-h-[44px] sm:min-h-0 ${
                    isDarkMode 
                      ? 'bg-gray-700 border-gray-600 text-gray-100' 
                      : 'bg-white border-gray-300 text-gray-900'
                  }`}
                  placeholderText="Custom End Date"
                />
                
                <button
                  onClick={() => {
                    setFilters(prev => ({
                      ...prev,
                      dateRange: '',
                      startDate: null,
                      endDate: null
                    }));
                  }}
                  className={`px-3 py-2 border rounded transition-colors min-h-[44px] sm:min-h-0 ${
                    isDarkMode 
                      ? 'text-gray-200 border-gray-600 hover:bg-gray-700' 
                      : 'text-gray-600 border-gray-300 hover:bg-gray-100'
                  }`}
                >
                  Clear Dates
                </button>
              </div>
            </div>

            {/* Amount Range */}
            <div className="mb-4">
              <label className={`block text-sm font-medium mb-2 ${isDarkMode ? 'text-gray-200' : 'text-gray-700'}`}>
                Amount Range
              </label>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
                <input
                  type="number"
                  value={filters.minAmount}
                  onChange={(e) => handleFilterChange('minAmount', e.target.value)}
                  placeholder="Min Amount"
                  className={`w-full p-2 border rounded ${isDarkMode ? 'bg-gray-700 border-gray-600 text-gray-100 placeholder-gray-400' : 'bg-white border-gray-300 text-gray-900'}`}
                />
                <input
                  type="number"
                  value={filters.maxAmount}
                  onChange={(e) => handleFilterChange('maxAmount', e.target.value)}
                  placeholder="Max Amount"
                  className={`w-full p-2 border rounded ${isDarkMode ? 'bg-gray-700 border-gray-600 text-gray-100 placeholder-gray-400' : 'bg-white border-gray-300 text-gray-900'}`}
                />
                <button
                  onClick={() => handleFilterChange('minAmount', '50000')}
                  className={`px-3 py-2 border rounded transition-colors ${isDarkMode ? 'text-blue-300 border-blue-700 hover:bg-blue-900/40' : 'text-blue-600 border-blue-300 hover:bg-blue-50'}`}
                >
                  High Value (&gt;50k)
                </button>
                <button
                  onClick={() => {
                    setFilters(prev => ({
                      ...prev,
                      minAmount: '',
                      maxAmount: ''
                    }));
                  }}
                  className={`px-3 py-2 border rounded transition-colors ${isDarkMode ? 'text-gray-200 border-gray-600 hover:bg-gray-700' : 'text-gray-600 border-gray-300 hover:bg-gray-100'}`}
                >
                  Clear Amount
                </button>
              </div>
            </div>

            {/* Advanced Filters Toggle */}
            <div className="mb-4">
              <button
                onClick={() => setShowAdvancedFilters(!showAdvancedFilters)}
                className={`flex items-center font-medium transition-colors ${isDarkMode ? 'text-blue-400 hover:text-blue-300' : 'text-blue-600 hover:text-blue-800'}`}
              >
                <FaChartBar className="mr-2" />
                {showAdvancedFilters ? 'Hide' : 'Show'} Advanced Filters
              </button>
            </div>

            {/* Advanced Filters */}
            {showAdvancedFilters && (
              <div className={`border-t pt-4 ${isDarkMode ? 'border-gray-600' : 'border-gray-200'}`}>
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 mb-4">
                  <div>
                    <label className={`block text-sm font-medium mb-1 ${isDarkMode ? 'text-gray-200' : 'text-gray-700'}`}>
                      Payment Modes
                    </label>
                    <Select
                      isMulti
                      value={paymentModeOptions.filter(option => filters.paymentModes.includes(option.value))}
                      onChange={(selectedOptions) => handleMultiSelectFilter('paymentModes', selectedOptions)}
                      options={paymentModeOptions}
                      className="w-full"
                      placeholder="Select Payment Modes"
                      isSearchable={false}
                      styles={getSelectStyles(isDarkMode)}
                      menuPortalTarget={document.body}
                      menuPosition="fixed"
                    />
                  </div>

                  <div>
                    <label className={`block text-sm font-medium mb-1 ${isDarkMode ? 'text-gray-200' : 'text-gray-700'}`}>
                      Salesmen
                    </label>
                    <Select
                      isMulti
                      value={employees.filter(emp => filters.salesmen.includes(emp._id)).map(emp => ({ value: emp._id, label: emp.name }))}
                      onChange={(selectedOptions) => handleMultiSelectFilter('salesmen', selectedOptions)}
                      options={employees.map(emp => ({ value: emp._id, label: emp.name }))}
                      className="w-full"
                      placeholder="Select Salesmen"
                      isSearchable
                      styles={getSelectStyles(isDarkMode)}
                      menuPortalTarget={document.body}
                      menuPosition="fixed"
                    />
                  </div>

                  <div>
                    <label className={`block text-sm font-medium mb-1 ${isDarkMode ? 'text-gray-200' : 'text-gray-700'}`}>
                      Transporters
                    </label>
                    <Select
                      isMulti
                      value={transporters.filter(trans => filters.transporters.includes(trans._id)).map(trans => ({ value: trans._id, label: trans.name }))}
                      onChange={(selectedOptions) => handleMultiSelectFilter('transporters', selectedOptions)}
                      options={transporters.map(trans => ({ value: trans._id, label: trans.name }))}
                      className="w-full"
                      placeholder="Select Transporters"
                      isSearchable
                      styles={getSelectStyles(isDarkMode)}
                      menuPortalTarget={document.body}
                      menuPosition="fixed"
                    />
                  </div>

                  <div>
                    <label className={`block text-sm font-medium mb-1 ${isDarkMode ? 'text-gray-200' : 'text-gray-700'}`}>
                      References
                    </label>
                    <Select
                      isMulti
                      value={references.filter(ref => filters.references.includes(ref._id)).map(ref => ({ value: ref._id, label: ref.name }))}
                      onChange={(selectedOptions) => handleMultiSelectFilter('references', selectedOptions)}
                      options={references.map(ref => ({ value: ref._id, label: ref.name }))}
                      className="w-full"
                      placeholder="Select References"
                      isSearchable
                      styles={getSelectStyles(isDarkMode)}
                      menuPortalTarget={document.body}
                      menuPosition="fixed"
                    />
                  </div>

                  <div>
                    <label className={`block text-sm font-medium mb-1 ${isDarkMode ? 'text-gray-200' : 'text-gray-700'}`}>
                      Credit Period (Days)
                    </label>
                    <Select
                      value={filters.creditPeriod ? { value: filters.creditPeriod, label: `${filters.creditPeriod} days` } : null}
                      onChange={(selectedOption) => handleFilterChange('creditPeriod', selectedOption ? selectedOption.value : '')}
                      options={[
                        { value: '0', label: '0 days (Immediate)' },
                        { value: '30', label: '30 days' },
                        { value: '60', label: '60 days' },
                        { value: '90', label: '90 days' }
                      ]}
                      className="w-full"
                      placeholder="All Credit Periods"
                      isClearable
                      isSearchable={false}
                      styles={getSelectStyles(isDarkMode)}
                      menuPortalTarget={document.body}
                      menuPosition="fixed"
                    />
                  </div>

                  <div>
                    <label className={`block text-sm font-medium mb-1 ${isDarkMode ? 'text-gray-200' : 'text-gray-700'}`}>
                      Item Count Range
                    </label>
                    <div className="flex space-x-2">
                      <input
                        type="number"
                        value={filters.minItemCount}
                        onChange={(e) => handleFilterChange('minItemCount', e.target.value)}
                        placeholder="Min Items"
                        className={`w-full p-2 border rounded ${isDarkMode ? 'bg-gray-700 border-gray-600 text-gray-100 placeholder-gray-400' : 'bg-white border-gray-300 text-gray-900'}`}
                      />
                      <input
                        type="number"
                        value={filters.maxItemCount}
                        onChange={(e) => handleFilterChange('maxItemCount', e.target.value)}
                        placeholder="Max Items"
                        className={`w-full p-2 border rounded ${isDarkMode ? 'bg-gray-700 border-gray-600 text-gray-100 placeholder-gray-400' : 'bg-white border-gray-300 text-gray-900'}`}
                      />
                    </div>
                  </div>
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div>
                    <label className={`block text-sm font-medium mb-1 ${isDarkMode ? 'text-gray-200' : 'text-gray-700'}`}>
                      Credit Period Range (Days)
                    </label>
                    <div className="flex space-x-2">
                      <input
                        type="number"
                        value={filters.minCreditPeriod}
                        onChange={(e) => handleFilterChange('minCreditPeriod', e.target.value)}
                        placeholder="Min Credit Days"
                        className={`w-full p-2 border rounded ${isDarkMode ? 'bg-gray-700 border-gray-600 text-gray-100 placeholder-gray-400' : 'bg-white border-gray-300 text-gray-900'}`}
                      />
                      <input
                        type="number"
                        value={filters.maxCreditPeriod}
                        onChange={(e) => handleFilterChange('maxCreditPeriod', e.target.value)}
                        placeholder="Max Credit Days"
                        className={`w-full p-2 border rounded ${isDarkMode ? 'bg-gray-700 border-gray-600 text-gray-100 placeholder-gray-400' : 'bg-white border-gray-300 text-gray-900'}`}
                      />
                    </div>
                  </div>

                  <div>
                    <label className={`block text-sm font-medium mb-1 ${isDarkMode ? 'text-gray-200' : 'text-gray-700'}`}>
                      Created By
                    </label>
                    <Select
                      isMulti
                      value={employees.filter(emp => filters.createdBy.includes(emp._id)).map(emp => ({ value: emp._id, label: emp.name }))}
                      onChange={(selectedOptions) => handleMultiSelectFilter('createdBy', selectedOptions)}
                      options={employees.map(emp => ({ value: emp._id, label: emp.name }))}
                      className="w-full"
                      placeholder="Select Employees"
                      isSearchable
                      styles={getSelectStyles(isDarkMode)}
                      menuPortalTarget={document.body}
                      menuPosition="fixed"
                    />
                  </div>
                </div>
              </div>
            )}

            {/* Action Buttons */}
            <div className="flex flex-col sm:flex-row justify-between mt-6 gap-2">
              <div className="flex flex-col sm:flex-row gap-2">
                <button
                  onClick={saveFilterPreset}
                  className={`px-4 py-2 rounded flex items-center justify-center transition-colors min-h-[44px] sm:min-h-0 ${
                    isDarkMode 
                      ? 'bg-green-700 text-white hover:bg-green-600' 
                      : 'bg-green-600 text-white hover:bg-green-700'
                  }`}
                >
                  <FaSave className="mr-2" />
                  Save as Preset
                </button>
                <button
                  onClick={() => {
                    const filterData = {
                      filters,
                      timestamp: new Date().toISOString(),
                      user: user?.user?.name
                    };
                    navigator.clipboard.writeText(JSON.stringify(filterData, null, 2));
                    Swal.fire('Copied!', 'Filter configuration copied to clipboard', 'success');
                  }}
                  className={`px-4 py-2 rounded flex items-center justify-center transition-colors min-h-[44px] sm:min-h-0 ${
                    isDarkMode 
                      ? 'bg-gray-700 text-white hover:bg-gray-600' 
                      : 'bg-gray-600 text-white hover:bg-gray-700'
                  }`}
                >
                  <FaDownload className="mr-2" />
                  Export Filters
                </button>
                <button
                  onClick={async () => {
                    try {
                      const clipboardText = await navigator.clipboard.readText();
                      const importedData = JSON.parse(clipboardText);
                      if (importedData.filters) {
                        setFilters(importedData.filters);
                        Swal.fire('Imported!', 'Filter configuration imported successfully', 'success');
                        applyFilters();
                      }
                    } catch (error) {
                      Swal.fire('Error!', 'Invalid filter data in clipboard', 'error');
                    }
                  }}
                  className={`px-4 py-2 rounded flex items-center justify-center transition-colors min-h-[44px] sm:min-h-0 ${
                    isDarkMode 
                      ? 'bg-blue-700 text-white hover:bg-blue-600' 
                      : 'bg-blue-600 text-white hover:bg-blue-700'
                  }`}
                >
                  <FaDownload className="mr-2 transform rotate-180" />
                  Import Filters
                </button>
              </div>
              
              <div className="flex flex-col sm:flex-row gap-2">
                <button
                  onClick={resetFilters}
                  className={`px-4 py-2 border rounded transition-colors min-h-[44px] sm:min-h-0 ${
                    isDarkMode 
                      ? 'text-gray-200 border-gray-600 hover:bg-gray-700' 
                      : 'text-gray-600 border-gray-300 hover:bg-gray-100'
                  }`}
                >
                  Reset All Filters
                </button>
                <button
                  onClick={applyFilters}
                  className={`px-4 py-2 rounded transition-colors min-h-[44px] sm:min-h-0 ${
                    isDarkMode 
                      ? 'bg-blue-700 text-white hover:bg-blue-600' 
                      : 'bg-blue-600 text-white hover:bg-blue-700'
                  }`}
                >
                  Apply Filters
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Last 7 Days Statistics */}
        {lastSevenDaysStats && (
          <div className="mb-6">
            <h3 className={`text-lg font-semibold mb-3 flex items-center ${
              isDarkMode ? 'text-gray-100' : 'text-gray-800'
            }`}>
              <svg className={`w-5 h-5 mr-2 ${isDarkMode ? 'text-blue-400' : 'text-blue-600'}`} fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2h2a2 2 0 002-2z" />
              </svg>
              Last 7 Days Overview
            </h3>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
              <div className={`p-4 rounded-lg shadow ${
                isDarkMode 
                  ? 'bg-gradient-to-r from-teal-900/40 to-teal-800/40' 
                  : 'bg-gradient-to-r from-teal-50 to-teal-100'
              }`}>
                <h4 className={`font-medium mb-2 ${isDarkMode ? 'text-teal-300' : 'text-teal-700'}`}>
                  Recent Orders
                </h4>
                <p className={`text-2xl font-bold ${isDarkMode ? 'text-teal-100' : 'text-teal-900'}`}>
                  {lastSevenDaysStats.totalOrders}
                </p>
                <div className={`text-sm mt-2 ${isDarkMode ? 'text-teal-200' : 'text-teal-600'}`}>
                  <div className="flex justify-between">
                    <span>Pending:</span>
                    <span className="font-medium">{lastSevenDaysStats.pendingOrders}</span>
                  </div>
                  <div className="flex justify-between">
                    <span>Completed:</span>
                    <span className="font-medium">{lastSevenDaysStats.completedOrders}</span>
                  </div>
                  <div className="flex justify-between">
                    <span>Cancelled:</span>
                    <span className="font-medium">{lastSevenDaysStats.cancelledOrders}</span>
                  </div>
                </div>
              </div>

              <div className={`p-4 rounded-lg shadow ${
                isDarkMode 
                  ? 'bg-gradient-to-r from-indigo-900/40 to-indigo-800/40' 
                  : 'bg-gradient-to-r from-indigo-50 to-indigo-100'
              }`}>
                <h4 className={`font-medium mb-2 ${isDarkMode ? 'text-indigo-300' : 'text-indigo-700'}`}>
                  Recent Revenue
                </h4>
                <p className={`text-2xl font-bold ${isDarkMode ? 'text-indigo-100' : 'text-indigo-900'}`}>
                  ₹{lastSevenDaysStats.totalAmount}
                </p>
                <div className={`text-sm mt-2 ${isDarkMode ? 'text-indigo-200' : 'text-indigo-600'}`}>
                  <div className="flex justify-between">
                    <span>Daily Average:</span>
                    <span className="font-medium">
                      ₹{formatNumberWithCommas(parseFloat(lastSevenDaysStats.totalAmount.replace(/,/g, '')) / 7)}
                    </span>
                  </div>
                </div>
              </div>

              <div className={`p-4 rounded-lg shadow ${
                isDarkMode 
                  ? 'bg-gradient-to-r from-rose-900/40 to-rose-800/40' 
                  : 'bg-gradient-to-r from-rose-50 to-rose-100'
              }`}>
                <h4 className={`font-medium mb-2 ${isDarkMode ? 'text-rose-300' : 'text-rose-700'}`}>
                  Status Distribution
                </h4>
                <div className={`text-sm ${isDarkMode ? 'text-rose-100' : 'text-rose-900'}`}>
                  <div className="mb-2">
                    <div className="flex justify-between mb-1">
                      <span>Pending:</span>
                      <span className="font-medium">
                        {lastSevenDaysStats.totalOrders ? ((lastSevenDaysStats.pendingOrders / lastSevenDaysStats.totalOrders) * 100).toFixed(1) : 0}%
                      </span>
                    </div>
                    <div className={`w-full rounded-full h-2 ${isDarkMode ? 'bg-rose-950/60' : 'bg-rose-200'}`}>
                      <div
                        className={isDarkMode ? 'bg-yellow-500 h-2 rounded-full' : 'bg-yellow-400 h-2 rounded-full'}
                        style={{ 
                          width: `${lastSevenDaysStats.totalOrders ? 
                            (lastSevenDaysStats.pendingOrders / lastSevenDaysStats.totalOrders) * 100 : 0}%` 
                        }}
                      ></div>
                    </div>
                  </div>
                  <div className="mb-2">
                    <div className="flex justify-between mb-1">
                      <span>Completed:</span>
                      <span className="font-medium">
                        {lastSevenDaysStats.totalOrders ? ((lastSevenDaysStats.completedOrders / lastSevenDaysStats.totalOrders) * 100).toFixed(1) : 0}%
                      </span>
                    </div>
                    <div className={`w-full rounded-full h-2 ${isDarkMode ? 'bg-rose-950/60' : 'bg-rose-200'}`}>
                      <div
                        className={isDarkMode ? 'bg-green-500 h-2 rounded-full' : 'bg-green-400 h-2 rounded-full'}
                        style={{ 
                          width: `${lastSevenDaysStats.totalOrders ? 
                            (lastSevenDaysStats.completedOrders / lastSevenDaysStats.totalOrders) * 100 : 0}%` 
                        }}
                      ></div>
                    </div>
                  </div>
                </div>
              </div>

              <div className={`p-4 rounded-lg shadow ${
                isDarkMode 
                  ? 'bg-gradient-to-r from-amber-900/40 to-amber-800/40' 
                  : 'bg-gradient-to-r from-amber-50 to-amber-100'
              }`}>
                <h4 className={`font-medium mb-2 ${isDarkMode ? 'text-amber-300' : 'text-amber-700'}`}>
                  Recent Amount by Status
                </h4>
                <div className={`text-sm ${isDarkMode ? 'text-amber-100' : 'text-amber-900'}`}>
                  <div className="flex justify-between mb-1">
                    <span>Pending:</span>
                    <span className="font-medium">₹{lastSevenDaysStats.pendingAmount}</span>
                  </div>
                  <div className="flex justify-between mb-1">
                    <span>Completed:</span>
                    <span className="font-medium">₹{lastSevenDaysStats.completedAmount}</span>
                  </div>
                  <div className="flex justify-between mb-1">
                    <span>Cancelled:</span>
                    <span className="font-medium">₹{lastSevenDaysStats.cancelledAmount}</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Search Results Summary */}
        {!loading && (
          <div className="mb-4 flex flex-col sm:flex-row justify-between items-start sm:items-center gap-2">
            <div className="flex items-center space-x-4">
              <div className={`text-sm ${isDarkMode ? 'text-gray-300' : 'text-gray-600'}`}>
                <span className={`font-medium ${isDarkMode ? 'text-gray-100' : 'text-gray-900'}`}>{total}</span> orders found
                {(quickSearch || Object.values(filters).some(v => Array.isArray(v) ? v.length > 0 : v)) && (
                  <span className={`ml-2 ${isDarkMode ? 'text-blue-400' : 'text-blue-600'}`}>
                    (filtered results)
                    <button
                      onClick={() => {
                        clearSearch(false);
                        resetFilters();
                      }}
                      className={`ml-2 text-xs underline ${
                        isDarkMode ? 'text-blue-400 hover:text-blue-300' : 'text-blue-500 hover:text-blue-700'
                      }`}
                    >
                      Clear all filters
                    </button>
                  </span>
                )}
              </div>
              
              {quickSearch && (
                <div className={`text-sm ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                  Searching for: <span className={`font-medium ${isDarkMode ? 'text-gray-200' : 'text-gray-700'}`}>"{quickSearch}"</span>
                </div>
              )}
            </div>
            
            <div className={`text-xs ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
              Page {page} of {Math.ceil(total / limit)}
            </div>
          </div>
        )}

        {/* Orders Table - Desktop and Mobile Responsive */}
        <div className="hidden md:block overflow-x-auto rounded-lg shadow">
          <table className={`min-w-full ${isDarkMode ? 'bg-gray-800' : 'bg-white'}`}>
            <thead className={isDarkMode ? 'bg-gray-750' : 'bg-gray-50'}>
              <tr>
                {[
                  { key: 'orderDetails', label: 'Order Details' },
                  { key: 'status', label: 'Status' },
                  { key: 'totalAmountWithGST', label: 'Final Amount' },
                  { key: 'actions', label: 'Actions' }
                ].map(column => (
                  <th
                    key={column.key}
                    onClick={() => column.key !== 'actions' && handleSort(column.key)}
                    className={`px-6 py-3 text-left text-xs font-medium uppercase tracking-wider ${
                      isDarkMode ? 'text-gray-300' : 'text-gray-500'
                    } ${
                      column.key !== 'actions' 
                        ? isDarkMode 
                          ? 'cursor-pointer hover:bg-gray-700' 
                          : 'cursor-pointer hover:bg-gray-100' 
                        : ''
                    }`}
                  >
                    <div className="flex items-center">
                      {column.label}
                      {column.key !== 'actions' && (
                        <span className="ml-2">
                          {sortBy === column.key ? (
                            sortOrder === 'asc' ? <FaSortUp /> : <FaSortDown />
                          ) : (
                            <FaSort className={isDarkMode ? 'text-gray-600' : 'text-gray-300'} />
                          )}
                        </span>
                      )}
                    </div>
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {!loading && orders.length === 0 ? (
                <tr>
                  <td colSpan={4} className="px-6 py-12 text-center">
                    <div className="flex flex-col items-center justify-center">
                      <div className={`w-16 h-16 rounded-full flex items-center justify-center mb-4 ${
                        isDarkMode ? 'bg-gray-700' : 'bg-gray-100'
                      }`}>
                        <svg className={`w-8 h-8 ${isDarkMode ? 'text-gray-500' : 'text-gray-400'}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                        </svg>
                      </div>
                      <h3 className={`text-lg font-medium mb-2 ${isDarkMode ? 'text-gray-100' : 'text-gray-900'}`}>
                        No orders found
                      </h3>
                      <p className={`mb-4 max-w-md text-center ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                        {quickSearch || Object.values(filters).some(v => Array.isArray(v) ? v.length > 0 : v) ? (
                          <>
                            No orders match your current search criteria. Try adjusting your filters or search terms.
                          </>
                        ) : (
                          <>
                            You haven't created any delivery orders yet. Create your first order using the form above.
                          </>
                        )}
                      </p>
                      <div className="flex flex-col sm:flex-row gap-3">
                        {(quickSearch || Object.values(filters).some(v => Array.isArray(v) ? v.length > 0 : v)) && (
                          <button
                            onClick={() => {
                              clearSearch(false);
                              resetFilters();
                            }}
                            className={`px-4 py-2 text-white rounded-md transition-colors duration-200 flex items-center justify-center ${
                              isDarkMode ? 'bg-blue-700 hover:bg-blue-600' : 'bg-blue-500 hover:bg-blue-600'
                            }`}
                          >
                            <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                            </svg>
                            Clear All Filters
                          </button>
                        )}
                        <button
                          onClick={() => {
                            window.scrollTo({ top: 0, behavior: 'smooth' });
                            // Focus on the first form field
                            setTimeout(() => {
                              const firstInput = document.querySelector('form input, form select');
                              if (firstInput) firstInput.focus();
                            }, 500);
                          }}
                          className={`px-4 py-2 text-white rounded-md transition-colors duration-200 flex items-center justify-center ${
                            isDarkMode ? 'bg-green-700 hover:bg-green-600' : 'bg-green-500 hover:bg-green-600'
                          }`}
                        >
                          <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
                          </svg>
                          Create New Order
                        </button>
                      </div>
                    </div>
                  </td>
                </tr>
              ) : (
                orders.map((order) => (
                  <tr key={order._id} className={`border-b ${
                    isDarkMode 
                      ? 'border-gray-700 hover:bg-gray-750' 
                      : 'border-gray-200 hover:bg-gray-50'
                  }`}>
                    <td className="px-6 py-4">
                      <div className="space-y-1">
                        <div className={`font-medium ${isDarkMode ? 'text-gray-100' : 'text-gray-900'}`}>
                          DO: {order.doNumber}
                        </div>
                        <div className={`text-sm ${isDarkMode ? 'text-gray-300' : 'text-gray-600'}`}>
                          {order.recipientType === 'Customer' ? order.recipient?.name : order.recipient?.businessName}
                        </div>
                        <div className={`text-xs flex items-center justify-between ${
                          isDarkMode ? 'text-gray-400' : 'text-gray-500'
                        }`}>
                          <span>{order.recipientType}</span>
                          <span className="ml-2">
                            <strong>Godown:</strong> {order.godown?.Name || 'Unassigned'}
                          </span>
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className={`px-2 py-1 inline-flex text-xs leading-5 font-semibold rounded-full 
                        ${order.status === 'Pending' 
                          ? isDarkMode ? 'bg-yellow-900/40 text-yellow-300' : 'bg-yellow-100 text-yellow-800'
                          : order.status === 'Completed' 
                            ? isDarkMode ? 'bg-green-900/40 text-green-300' : 'bg-green-100 text-green-800'
                            : isDarkMode ? 'bg-red-900/40 text-red-300' : 'bg-red-100 text-red-800'
                        }`}>
                        {order.status}
                      </span>
                    </td>
                    <td className={`px-6 py-4 whitespace-nowrap font-medium ${
                      isDarkMode ? 'text-blue-400' : 'text-blue-600'
                    }`}>
                      ₹{formatNumberWithCommas(order.totalAmountWithGST)}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <button 
                        onClick={() => handleViewOrder(order._id)}
                        className={`p-2 rounded-full transition-colors ${
                          isDarkMode 
                            ? 'text-blue-400 hover:bg-blue-900/30' 
                            : 'text-blue-600 hover:bg-blue-100'
                        }`}
                        title="View details"
                      >
                        <FaEye className="w-5 h-5" />
                      </button>
                      <button 
                        onClick={(e) => handlePrintMenuOpen(e, order._id)}
                        className={`p-2 rounded-full transition-colors ${
                          isDarkMode 
                            ? 'text-blue-400 hover:bg-blue-900/30' 
                            : 'text-blue-600 hover:bg-blue-100'
                        }`}
                        title="Print"
                      >
                        <FaPrint className="w-5 h-5" />
                      </button>
                      <button 
                        onClick={() => handleWhatsAppSend(order)}
                        className={`p-2 rounded-full transition-colors ${
                          isDarkMode 
                            ? 'text-green-400 hover:bg-green-900/30' 
                            : 'text-green-600 hover:bg-green-100'
                        }`}
                        title="Send via WhatsApp"
                      >
                        <FaWhatsapp className="w-5 h-5" />
                      </button>
                      {order.status === 'Pending' && (
                        <>
                          <button 
                            onClick={() => handleEdit(order)}
                            title="Edit delivery order"
                            className={`p-2 rounded-full transition-colors ${
                              isDarkMode 
                                ? 'text-yellow-400 hover:bg-yellow-900/30' 
                                : 'text-yellow-600 hover:bg-yellow-100'
                            }`}
                          >
                            <FaEdit className="w-5 h-5" />
                          </button>
                          <button 
                            onClick={() => handleUpdateStatus(order._id)}
                            title="Update status"
                            className={`p-2 rounded-full transition-colors ${
                              isDarkMode 
                                ? 'text-green-400 hover:bg-green-900/30' 
                                : 'text-green-600 hover:bg-green-100'
                            }`}
                          >
                            <FaCheck className="w-5 h-5" />
                          </button>
                        </>
                      )}
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>

        {/* Mobile Card Layout */}
        <div className="md:hidden space-y-4">
          {!loading && orders.length === 0 ? (
            <div className={`rounded-lg p-8 text-center ${isDarkMode ? 'bg-gray-800' : 'bg-white'}`}>
              <div className={`w-16 h-16 rounded-full flex items-center justify-center mb-4 mx-auto ${
                isDarkMode ? 'bg-gray-700' : 'bg-gray-100'
              }`}>
                <svg className={`w-8 h-8 ${isDarkMode ? 'text-gray-500' : 'text-gray-400'}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                </svg>
              </div>
              <h3 className={`text-lg font-medium mb-2 ${isDarkMode ? 'text-gray-100' : 'text-gray-900'}`}>
                No orders found
              </h3>
              <p className={`mb-4 text-sm ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                {quickSearch || Object.values(filters).some(v => Array.isArray(v) ? v.length > 0 : v) ? 
                  'No orders match your search' : 
                  'Create your first delivery order'
                }
              </p>
              <button
                onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}
                className={`px-4 py-2 text-white rounded-md ${isDarkMode ? 'bg-green-700' : 'bg-green-500'}`}
              >
                Create New Order
              </button>
            </div>
          ) : (
            orders.map((order) => (
              <div key={order._id} className={`rounded-lg shadow-md p-4 ${isDarkMode ? 'bg-gray-800 border border-gray-700' : 'bg-white border border-gray-200'}`}>
                {/* Header with DO Number and Status */}
                <div className="flex justify-between items-start mb-3">
                  <div className="flex-1">
                    <div className={`font-semibold text-sm ${isDarkMode ? 'text-gray-100' : 'text-gray-900'}`}>
                      {order.doNumber}
                    </div>
                    <div className={`text-xs mt-1 ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                      {order.recipientType}
                    </div>
                  </div>
                  <span className={`px-2 py-1 text-xs font-semibold rounded-full whitespace-nowrap ${
                    order.status === 'Pending' 
                      ? isDarkMode ? 'bg-yellow-900/40 text-yellow-300' : 'bg-yellow-100 text-yellow-800'
                      : order.status === 'Completed' 
                        ? isDarkMode ? 'bg-green-900/40 text-green-300' : 'bg-green-100 text-green-800'
                        : isDarkMode ? 'bg-red-900/40 text-red-300' : 'bg-red-100 text-red-800'
                  }`}>
                    {order.status}
                  </span>
                </div>

                {/* Recipient Details */}
                <div className={`mb-3 pb-3 border-b ${isDarkMode ? 'border-gray-700' : 'border-gray-200'}`}>
                  <div className={`text-sm font-medium ${isDarkMode ? 'text-gray-200' : 'text-gray-700'}`}>
                    {order.recipientType === 'Customer' ? order.recipient?.name : order.recipient?.businessName}
                  </div>
                  <div className={`text-xs mt-1 ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                    <strong>Godown:</strong> {order.godown?.Name || 'Unassigned'}
                  </div>
                </div>

                {/* Amount */}
                <div className="mb-3">
                  <div className={`text-xs ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                    Total Amount
                  </div>
                  <div className={`text-lg font-bold ${isDarkMode ? 'text-blue-400' : 'text-blue-600'}`}>
                    ₹{formatNumberWithCommas(order.totalAmountWithGST)}
                  </div>
                </div>

                {/* Action Buttons */}
                <div className="flex flex-wrap gap-2">
                  <button 
                    onClick={() => handleViewOrder(order._id)}
                    className={`flex-1 px-3 py-2 rounded-md text-xs font-medium flex items-center justify-center gap-1 transition-colors ${
                      isDarkMode 
                        ? 'bg-blue-600 hover:bg-blue-700 text-white' 
                        : 'bg-blue-500 hover:bg-blue-600 text-white'
                    }`}
                  >
                    <FaEye className="w-3 h-3" />
                    View
                  </button>
                  <button 
                    onClick={(e) => handlePrintMenuOpen(e, order._id)}
                    className={`flex-1 px-3 py-2 rounded-md text-xs font-medium flex items-center justify-center gap-1 transition-colors ${
                      isDarkMode 
                        ? 'bg-purple-600 hover:bg-purple-700 text-white' 
                        : 'bg-purple-500 hover:bg-purple-600 text-white'
                    }`}
                  >
                    <FaPrint className="w-3 h-3" />
                    Print
                  </button>
                  <button 
                    onClick={() => handleWhatsAppSend(order)}
                    className={`flex-1 px-3 py-2 rounded-md text-xs font-medium flex items-center justify-center gap-1 transition-colors ${
                      isDarkMode 
                        ? 'bg-green-600 hover:bg-green-700 text-white' 
                        : 'bg-green-500 hover:bg-green-600 text-white'
                    }`}
                  >
                    <FaWhatsapp className="w-3 h-3" />
                    WhatsApp
                  </button>
                  {order.status === 'Pending' && (
                    <>
                      <button 
                        onClick={() => handleEdit(order)}
                        className={`flex-1 px-3 py-2 rounded-md text-xs font-medium flex items-center justify-center gap-1 transition-colors ${
                          isDarkMode 
                            ? 'bg-yellow-600 hover:bg-yellow-700 text-white' 
                            : 'bg-yellow-500 hover:bg-yellow-600 text-white'
                        }`}
                      >
                        <FaEdit className="w-3 h-3" />
                        Edit
                      </button>
                      <button 
                        onClick={() => handleUpdateStatus(order._id)}
                        className={`flex-1 px-3 py-2 rounded-md text-xs font-medium flex items-center justify-center gap-1 transition-colors ${
                          isDarkMode 
                            ? 'bg-green-600 hover:bg-green-700 text-white' 
                            : 'bg-green-500 hover:bg-green-600 text-white'
                        }`}
                      >
                        <FaCheck className="w-3 h-3" />
                        Complete
                      </button>
                    </>
                  )}
                </div>
              </div>
            ))
          )}
        </div>

        {/* Enhanced Pagination - Only show if there are orders */}
        {!loading && orders.length > 0 && (
          <div className="mt-4 flex flex-col sm:flex-row items-center justify-between gap-4">
            <div className="flex items-center space-x-1 sm:space-x-2">
              <button
                onClick={() => setPage(1)}
                disabled={page === 1}
                className={`px-2 sm:px-3 py-1 text-xs sm:text-sm border rounded disabled:opacity-50 disabled:cursor-not-allowed transition-colors ${
                  isDarkMode 
                    ? 'border-gray-600 text-gray-200 hover:bg-gray-700' 
                    : 'border-gray-300 text-gray-700 hover:bg-gray-50'
                }`}
              >
                First
              </button>
              <button
                onClick={() => setPage(prev => Math.max(1, prev - 1))}
                disabled={page === 1}
                className={`px-2 sm:px-3 py-1 text-xs sm:text-sm border rounded disabled:opacity-50 disabled:cursor-not-allowed transition-colors ${
                  isDarkMode 
                    ? 'border-gray-600 text-gray-200 hover:bg-gray-700' 
                    : 'border-gray-300 text-gray-700 hover:bg-gray-50'
                }`}
              >
                Prev
              </button>
              <span className={`px-2 sm:px-4 py-1 text-xs sm:text-sm rounded ${
                isDarkMode ? 'bg-gray-700 text-gray-200' : 'bg-gray-100 text-gray-900'
              }`}>
                {page} / {Math.ceil(total / limit)}
              </span>
              <button
                onClick={() => setPage(prev => prev + 1)}
                disabled={page >= Math.ceil(total / limit)}
                className={`px-2 sm:px-3 py-1 text-xs sm:text-sm border rounded disabled:opacity-50 disabled:cursor-not-allowed transition-colors ${
                  isDarkMode 
                    ? 'border-gray-600 text-gray-200 hover:bg-gray-700' 
                    : 'border-gray-300 text-gray-700 hover:bg-gray-50'
                }`}
              >
                Next
              </button>
              <button
                onClick={() => setPage(Math.ceil(total / limit))}
                disabled={page >= Math.ceil(total / limit)}
                className={`px-2 sm:px-3 py-1 text-xs sm:text-sm border rounded disabled:opacity-50 disabled:cursor-not-allowed transition-colors ${
                  isDarkMode 
                    ? 'border-gray-600 text-gray-200 hover:bg-gray-700' 
                    : 'border-gray-300 text-gray-700 hover:bg-gray-50'
                }`}
              >
                Last
              </button>
          </div>
            <div className={`text-xs sm:text-sm text-center sm:text-right ${
              isDarkMode ? 'text-gray-300' : 'text-gray-600'
            }`}>
              Showing {((page - 1) * limit) + 1} to {Math.min(page * limit, total)} of {total} orders
          </div>
          </div>
        )}
      </div>

      {/* Tally Items Modal */}
      {showItemsModal && <ItemsModal />}
      {showProformaImportModal && <ProformaImportModal />}
      {showViewModal && <OrderDetailsModal />}
      {showItemDetailsModal && <ItemDetailsModal />}
      
      {/* Recipient Selection Modal */}
<MasterSelection
    isOpen={showRecipientModal}
    onClose={() => setShowRecipientModal(false)}
    onSelect={handleRecipientSelect}
    allowedTypes={['Customer', 'Dealer']}
    defaultType={form.recipientType}
        title="Select Recipient"
      />
      
      {/* Print Options Menu */}
      {showPrintMenu && (
        <div 
          ref={printMenuRef}
          className={`fixed z-50 rounded-lg shadow-lg border ${
            isDarkMode 
              ? 'bg-gray-800 border-gray-600' 
              : 'bg-white border-gray-200'
          }`}
          style={{
            top: `${printMenuPosition.top}px`,
            left: `${printMenuPosition.left}px`,
            minWidth: '250px'
          }}
        >
          <button
            onClick={() => handlePrintOption('basic')}
            className={`w-full px-4 py-3 text-left flex items-center space-x-3 transition-colors ${
              isDarkMode 
                ? 'text-gray-200 hover:bg-gray-700' 
                : 'text-gray-700 hover:bg-gray-100'
            }`}
          >
            <FaPrint className="w-4 h-4" />
            <span>Basic Print (No Discount Details)</span>
          </button>
          <button
            onClick={() => handlePrintOption('discount')}
            className={`w-full px-4 py-3 text-left flex items-center space-x-3 transition-colors ${
              isDarkMode 
                ? 'text-gray-200 hover:bg-gray-700' 
                : 'text-gray-700 hover:bg-gray-100'
            }`}
          >
            <FaPrint className="w-4 h-4" />
            <span>Print with Discount Details</span>
          </button>
        </div>
      )}
      </div>
    </div>
  );
};

export default DeliveryOrderNew;  